import { useState, useEffect, useRef, useCallback } from "react";

/* ═══════════════════════════════════════════════════════════
   LA MARITEC FLEET - Platform Management
   Backend API: /api (proxy → localhost:4000)
   ═══════════════════════════════════════════════════════════ */

/* ─── FONT ─── */
const fontStyle = document.createElement("style");
fontStyle.textContent = `@font-face{font-family:'Bira';src:url('/Bira.ttf') format('truetype');font-display:swap}
html,body{margin:0;padding:0;width:100%;overflow-x:hidden}
#root{width:100%;overflow-x:hidden}
@media(max-width:768px){
  .lm-content{padding:12px 10px!important;box-sizing:border-box!important;max-width:100vw!important;overflow-x:hidden!important}
  .lm-stat-cards{grid-template-columns:1fr!important;gap:8px!important}
  .lm-service-grid{grid-template-columns:1fr!important}
  .lm-recent-table{display:none!important}
  .lm-recent-cards{display:flex!important}
  .lm-btn{min-height:44px!important;font-size:14px!important}
  .lm-info-grid{grid-template-columns:1fr 1fr!important}
  .lm-desktop-table{display:none!important}
  .lm-mobile-cards{display:flex!important}
  .lm-mobile-cards>div{overflow:hidden!important;box-sizing:border-box!important}
  .lm-tabs{overflow-x:auto!important;-webkit-overflow-scrolling:touch;scrollbar-width:none;gap:0!important}
  .lm-tabs::-webkit-scrollbar{display:none}
  .lm-tabs button{white-space:nowrap!important;padding:10px 14px!important;font-size:13px!important;flex-shrink:0!important}
  .lm-page-header{flex-direction:column!important;align-items:flex-start!important;gap:10px!important}
  .lm-page-header h2{font-size:20px!important}
  .lm-filters{flex-direction:column!important;gap:8px!important;overflow:hidden!important;width:100%!important}
  .lm-filters input,.lm-filters select{max-width:100%!important;width:100%!important}
  .lm-filter-btns{overflow-x:auto!important;flex-wrap:nowrap!important;-webkit-overflow-scrolling:touch;scrollbar-width:none;width:100%!important;padding-bottom:4px!important;box-sizing:border-box!important}
  .lm-filter-btns::-webkit-scrollbar{display:none}
  .lm-filter-btns .lm-btn{flex-shrink:0!important;min-height:36px!important;padding:6px 12px!important;font-size:12px!important}
  .lm-3col{grid-template-columns:1fr!important}
  .lm-2col{grid-template-columns:1fr!important}
  .lm-modal{padding:20px!important;width:95%!important;max-height:90vh!important}
  .lm-photo-grid{grid-template-columns:repeat(2,1fr)!important}
  .lm-photo-slot{width:100%!important;height:auto!important;aspect-ratio:4/3!important;min-height:80px!important}
  .lm-wizard-steps{gap:4px!important}
  .lm-wizard-steps button{min-width:0!important;padding:8px 10px!important;font-size:12px!important;flex:1!important}
  .lm-checkbox-item{padding:12px!important}
  .lm-checkbox-box{width:28px!important;height:28px!important;font-size:16px!important}
  .lm-chart-wrap svg{max-width:100%!important}
  .lm-cost-grid{grid-template-columns:1fr!important}
  .lm-detail-grid{grid-template-columns:1fr 1fr!important}
  .lm-zone-layout{flex-direction:column!important}
  .lm-zone-sidebar{flex:none!important;width:100%!important;position:static!important}
  .lm-zone-sidebar>div{position:static!important}
  .lm-empty-icon{font-size:40px!important}
  .lm-detail-header{padding-top:0!important}
  .lm-detail-wrap{overflow-x:hidden!important;max-width:100vw!important}
  .lm-detail-wrap *{max-width:100%!important;box-sizing:border-box!important}
  .lm-detail-wrap svg{max-width:100%!important;height:auto!important}
  @media(max-width:768px){.lm-diff-actions>button,.lm-diff-actions>span{flex:1 1 100%!important;text-align:center!important}}
  .lm-wheel-grid{grid-template-columns:1fr 1fr!important;gap:8px!important}
  .lm-wheel-grid>div{min-width:0!important;overflow:hidden!important}
  .lm-check-text{word-wrap:break-word!important;overflow-wrap:break-word!important;white-space:normal!important;min-width:0!important}
  .lm-btn{touch-action:manipulation!important}
  .lm-zone-info-text{font-size:11px!important;text-align:center!important}
}`;
if (!document.head.querySelector("[data-bira]")) {
  fontStyle.setAttribute("data-bira", "1");
  document.head.appendChild(fontStyle);
}

/* ─── CONSTANTS ─── */
const PHOTO_UPLOAD_TIMEOUT_MS = 30000;
const AI_POLL_INTERVAL_MS = 5000;
const BRAKE_PAD_GREEN_THRESHOLD = 50;   // % above = green
const BRAKE_PAD_YELLOW_THRESHOLD = 30;  // % above = yellow, below = red

const TIPURI = ["Prelată","Frigorifică","Platformă","Cisternă","Basculantă","Container","Furgon"];
const STARI = ["Activă","Închiriată","Vânzare în rate","Returnată","În service","Scoasă din uz","De vânzare","În așteptare"];
const MARCI = ["Krone","Schmitz","Wielton","Kögel","Schwarzmüller","Fliegl","Berger","Tirsan","Altă marcă"];
const AXE_POS = ["Axa 1 St.","Axa 1 Dr.","Axa 2 St.","Axa 2 Dr.","Axa 3 St.","Axa 3 Dr.","Rezervă"];

const TRACTOR_TIPURI = ["Cap tractor"];
const TRACTOR_MARCI = ["MAN","Volvo","Mercedes-Benz","Scania","DAF","Iveco","Renault","Alt\u0103 marc\u0103"];
const TRACTOR_AXE_POS_4x2 = ["Axa 1 St.","Axa 1 Dr.","Axa 2 St-ext.","Axa 2 St-int.","Axa 2 Dr-int.","Axa 2 Dr-ext."];
const TRACTOR_AXE_POS_6x2 = ["Axa 1 St.","Axa 1 Dr.","Axa 2 St-ext.","Axa 2 St-int.","Axa 2 Dr-int.","Axa 2 Dr-ext.","Axa 3 St.","Axa 3 Dr."];

const TRACTOR_ZONES = [
  {id:"t_fata",label:"FA\u021a\u0102",desc:"Bar\u0103, faruri, parbriz, oglinzi",checks:["Bar\u0103 frontal\u0103 \u2013 stare bun\u0103, f\u0103r\u0103 fisuri","Faruri \u2013 func\u021bionale, f\u0103r\u0103 sp\u0103rturi","Parbriz \u2013 f\u0103r\u0103 fisuri, cr\u0103p\u0103turi","Oglinzi retrovizoare \u2013 fixe, reglabile","\u0218terg\u0103toare \u2013 func\u021bionale, lamele bune"],photoSlots:[{id:"t_fata_gen",label:"Fa\u021b\u0103 general",required:true},{id:"t_faruri",label:"Faruri",required:true}]},
  {id:"t_cabina_st",label:"CABIN\u0102 ST.",desc:"U\u0219\u0103, deflector, scar\u0103 st\u00e2nga",checks:["U\u0219\u0103 st\u00e2nga \u2013 deschidere/\u00eenchidere corect\u0103","Geam st\u00e2nga \u2013 f\u0103r\u0103 fisuri","Deflector aer st\u00e2nga \u2013 fixat","Scar\u0103 acces st\u00e2nga \u2013 stabil\u0103, antiderapant\u0103","Oglind\u0103 lateral\u0103 st\u00e2nga \u2013 fix\u0103"],photoSlots:[{id:"t_cab_st",label:"Cabin\u0103 st\u00e2nga",required:true}]},
  {id:"t_cabina_dr",label:"CABIN\u0102 DR.",desc:"U\u0219\u0103, deflector, scar\u0103 dreapta",checks:["U\u0219\u0103 dreapta \u2013 deschidere/\u00eenchidere corect\u0103","Geam dreapta \u2013 f\u0103r\u0103 fisuri","Deflector aer dreapta \u2013 fixat","Scar\u0103 acces dreapta \u2013 stabil\u0103, antiderapant\u0103","Oglind\u0103 lateral\u0103 dreapta \u2013 fix\u0103"],photoSlots:[{id:"t_cab_dr",label:"Cabin\u0103 dreapta",required:true}]},
  {id:"t_acoperis",label:"ACOPERI\u0218",desc:"Acoperi\u0219 cabin\u0103, spoiler",checks:["Acoperi\u0219 cabin\u0103 \u2013 f\u0103r\u0103 lovituri, rugin\u0103","Spoiler \u2013 fixat, f\u0103r\u0103 fisuri","Trapa (dac\u0103 exist\u0103) \u2013 func\u021bional\u0103","Antene \u2013 fixe"],photoSlots:[{id:"t_acoperis_foto",label:"Acoperi\u0219/Spoiler",required:true}]},
  {id:"t_interior",label:"INTERIOR",desc:"Scaune, bord, cu\u0219et\u0103, tahograf",checks:["Scaun \u0219ofer \u2013 reglabil, f\u0103r\u0103 rupturi","Scaun pasager \u2013 stare bun\u0103","Bord \u2013 indicatoare func\u021bionale, f\u0103r\u0103 avertiz\u0103ri","Cu\u0219et\u0103 \u2013 curat\u0103, func\u021bional\u0103","Tahograf \u2013 sigilat, func\u021bional, verificat","Climatizare \u2013 func\u021bional\u0103"],photoSlots:[{id:"t_int_bord",label:"Bord",required:true},{id:"t_int_cuseta",label:"Cu\u0219et\u0103",required:true},{id:"t_tahograf",label:"Tahograf",required:true}]},
  {id:"t_motor",label:"MOTOR",desc:"Compartiment motor",checks:["Nivel ulei motor \u2013 OK","Nivel lichid r\u0103cire \u2013 OK","Nivel lichid fr\u00e2ne \u2013 OK","Curele \u2013 f\u0103r\u0103 uzur\u0103","Furtunuri \u2013 f\u0103r\u0103 scurgeri","Filtru aer \u2013 curat"],photoSlots:[{id:"t_motor_gen",label:"Compartiment motor",required:true}]},
  {id:"t_sasiu",label:"\u0218ASIU",desc:"Structur\u0103 \u0219asiu",checks:["\u0218asiu \u2013 f\u0103r\u0103 fisuri, rugin\u0103 avansat\u0103","Traversele \u2013 fixe, f\u0103r\u0103 \u00eendoituri","Suspensia \u2013 f\u0103r\u0103 scurgeri, jocuri"],photoSlots:[{id:"t_sasiu_st",label:"\u0218asiu st\u00e2nga",required:true},{id:"t_sasiu_dr",label:"\u0218asiu dreapta",required:true}]},
  {id:"t_sa",label:"\u0218A CUPLARE",desc:"Sel\u0103 cuplare semiremorcă",checks:["Sel\u0103 cuplare \u2013 stare bun\u0103, lubrifiere","Mecanism blocare \u2013 func\u021bional","Plac\u0103 uzur\u0103 \u2013 stare acceptabil\u0103","Sistem siguran\u021b\u0103 deblocare \u2013 func\u021bional"],photoSlots:[{id:"t_sa_gen",label:"\u0218a cuplare",required:true},{id:"t_sa_mecanism",label:"Mecanism blocare",required:true}]},
  {id:"t_rezervor_st",label:"REZERVOR ST.",desc:"Rezervor motorin\u0103 st\u00e2nga",checks:["Rezervor st\u00e2nga \u2013 f\u0103r\u0103 scurgeri, lovit.","Capac \u2013 etan\u0219, cu cheie","Suport \u2013 fixat"],photoSlots:[{id:"t_rez_st",label:"Rezervor st\u00e2nga",required:true}]},
  {id:"t_rezervor_dr",label:"REZERVOR DR.",desc:"Rezervor motorin\u0103 dreapta + AdBlue",checks:["Rezervor dreapta \u2013 f\u0103r\u0103 scurgeri, lovit.","Capac \u2013 etan\u0219, cu cheie","Suport \u2013 fixat","Rezervor AdBlue \u2013 nivel OK, f\u0103r\u0103 scurgeri"],photoSlots:[{id:"t_rez_dr",label:"Rezervor dreapta",required:true},{id:"t_adblue",label:"AdBlue",required:true}]},
  {id:"t_baterii",label:"BATERII",desc:"Baterii acumulatori",checks:["Baterii \u2013 stare bun\u0103, fixate","Borne \u2013 curate, str\u00e2nse","Cutie protec\u021bie \u2013 fixat\u0103"],photoSlots:[{id:"t_baterii_foto",label:"Baterii",required:true}]},
  {id:"t_frane",label:"FR\u00c2NE",desc:"Sistem fr\u00e2nare",checks:["Fr\u00e2ne \u2013 func\u021bionale, f\u0103r\u0103 scurgeri","Compresor aer \u2013 func\u021bional","Furtunuri aer \u2013 f\u0103r\u0103 fisuri","Fr\u00e2na de m\u00e2n\u0103 \u2013 func\u021bional\u0103"],photoSlots:[{id:"t_frane_det",label:"Sistem fr\u00e2nare",required:true}]},
  {id:"t_electric",label:"ELECTRIC",desc:"Instalație, lumini",checks:["Lumini pozi\u021bie \u2013 func\u021bionale","Semnalizatoare \u2013 func\u021bionale","Stop-uri \u2013 func\u021bionale","Instala\u021bie electric\u0103 \u2013 f\u0103r\u0103 improviza\u021bii","Prize aer/curent tractor \u2013 func\u021bionale"],photoSlots:[{id:"t_lumini",label:"Lumini",required:true}]},
  {id:"t_documente",label:"DOCUMENTE",desc:"Documente la bord",checks:["Talon \u2013 valid, autentic","ITP \u2013 valabil","Asigurare RCA \u2013 valabil\u0103","Tahograf verificare \u2013 valabil\u0103","Copie conform\u0103 \u2013 valabil\u0103"],photoSlots:[{id:"t_talon",label:"Talon",required:true},{id:"t_asig",label:"Asigurare RCA",required:true}]},
  {id:"t_dotari",label:"DOT\u0102RI",desc:"Trus\u0103, sting\u0103tor, cric, chingi",checks:["Trus\u0103 medical\u0103 \u2013 complet\u0103, valabil\u0103","Sting\u0103tor \u2013 valabil, fixat","Triunghi reflectorizant \u2013 prezent","Vest\u0103 reflectorizant\u0103 \u2013 prezent\u0103","Cric + cheie ro\u021bi \u2013 prezente"],photoSlots:[],dynamicPhotos:true},
  {id:"t_spate",label:"SPATE",desc:"Travers\u0103, lumini, prize",checks:["Travers\u0103 spate \u2013 fixat\u0103, bar\u0103 protec\u021bie","Stop-uri \u0219i semnalizatoare spate \u2013 func\u021bionale","Prize aer (ro\u0219u+galben) \u2013 func\u021bionale, etan\u0219e","Priz\u0103 curent semiremorcă \u2013 func\u021bional\u0103","Reflectorizante \u2013 conforme"],photoSlots:[{id:"t_spate_gen",label:"Spate general",required:true},{id:"t_prize",label:"Prize aer/curent",required:true}]},
  {id:"t_axa1_st",label:"ROATA A1 ST.",desc:"Axa 1 st\u00e2nga (direc\u021bie)",isWheel:true,wheelIdx:0,checks:["Anvelop\u0103 f\u0103r\u0103 cr\u0103p\u0103turi, t\u0103ieturi, uzur\u0103 neuniform\u0103","Jant\u0103 f\u0103r\u0103 fisuri, \u00eendoituri","Prezoane str\u00e2nse","Senzor presiune"],photoSlots:[{id:"t_r_a1_st",label:"Roata A1 St.",required:true}]},
  {id:"t_axa1_dr",label:"ROATA A1 DR.",desc:"Axa 1 dreapta (direc\u021bie)",isWheel:true,wheelIdx:1,checks:["Anvelop\u0103 f\u0103r\u0103 cr\u0103p\u0103turi, t\u0103ieturi, uzur\u0103 neuniform\u0103","Jant\u0103 f\u0103r\u0103 fisuri, \u00eendoituri","Prezoane str\u00e2nse","Senzor presiune"],photoSlots:[{id:"t_r_a1_dr",label:"Roata A1 Dr.",required:true}]},
  {id:"t_axa2_st_ext",label:"A2 ST-EXT",desc:"Axa 2 st\u00e2nga ext (trac\u021biune)",isWheel:true,wheelIdx:2,checks:["Anvelop\u0103 f\u0103r\u0103 cr\u0103p\u0103turi, uzur\u0103","Jant\u0103 intact\u0103","Prezoane str\u00e2nse"],photoSlots:[{id:"t_r_a2_st_ext",label:"A2 St-ext",required:true}]},
  {id:"t_axa2_st_int",label:"A2 ST-INT",desc:"Axa 2 st\u00e2nga int (trac\u021biune)",isWheel:true,wheelIdx:3,checks:["Anvelop\u0103 f\u0103r\u0103 cr\u0103p\u0103turi, uzur\u0103","Jant\u0103 intact\u0103","Prezoane str\u00e2nse"],photoSlots:[{id:"t_r_a2_st_int",label:"A2 St-int",required:true}]},
  {id:"t_axa2_dr_int",label:"A2 DR-INT",desc:"Axa 2 dreapta int (trac\u021biune)",isWheel:true,wheelIdx:4,checks:["Anvelop\u0103 f\u0103r\u0103 cr\u0103p\u0103turi, uzur\u0103","Jant\u0103 intact\u0103","Prezoane str\u00e2nse"],photoSlots:[{id:"t_r_a2_dr_int",label:"A2 Dr-int",required:true}]},
  {id:"t_axa2_dr_ext",label:"A2 DR-EXT",desc:"Axa 2 dreapta ext (trac\u021biune)",isWheel:true,wheelIdx:5,checks:["Anvelop\u0103 f\u0103r\u0103 cr\u0103p\u0103turi, uzur\u0103","Jant\u0103 intact\u0103","Prezoane str\u00e2nse"],photoSlots:[{id:"t_r_a2_dr_ext",label:"A2 Dr-ext",required:true}]},
  {id:"t_axa3_st",label:"ROATA A3 ST.",desc:"Axa 3 st\u00e2nga (6x2)",isWheel:true,wheelIdx:6,checks:["Anvelop\u0103 f\u0103r\u0103 cr\u0103p\u0103turi, uzur\u0103","Jant\u0103 intact\u0103","Prezoane str\u00e2nse"],photoSlots:[{id:"t_r_a3_st",label:"Roata A3 St.",required:true}]},
  {id:"t_axa3_dr",label:"ROATA A3 DR.",desc:"Axa 3 dreapta (6x2)",isWheel:true,wheelIdx:7,checks:["Anvelop\u0103 f\u0103r\u0103 cr\u0103p\u0103turi, uzur\u0103","Jant\u0103 intact\u0103","Prezoane str\u00e2nse"],photoSlots:[{id:"t_r_a3_dr",label:"Roata A3 Dr.",required:true}]},
];

const TRACTOR_ZONES_LABELS = {
  t_fata:"FA\u021a\u0102",t_cabina_st:"CABIN\u0102 ST.",t_cabina_dr:"CABIN\u0102 DR.",t_acoperis:"ACOPERI\u0218",
  t_interior:"INTERIOR",t_motor:"MOTOR",t_sasiu:"\u0218ASIU",t_sa:"\u0218A CUPLARE",
  t_rezervor_st:"REZERVOR ST.",t_rezervor_dr:"REZERVOR DR.",t_baterii:"BATERII",
  t_frane:"FR\u00c2NE",t_electric:"ELECTRIC",t_documente:"DOCUMENTE",t_dotari:"DOT\u0102RI",t_spate:"SPATE",
  t_axa1_st:"ROATA A1 ST.",t_axa1_dr:"ROATA A1 DR.",
  t_axa2_st_ext:"A2 ST-EXT",t_axa2_st_int:"A2 ST-INT",
  t_axa2_dr_int:"A2 DR-INT",t_axa2_dr_ext:"A2 DR-EXT",
  t_axa3_st:"ROATA A3 ST.",t_axa3_dr:"ROATA A3 DR.",
};

function getVehicleZones(vehicleType, config) {
  if (vehicleType !== 'tractor') return ZONES;
  if (config === '6x2') return TRACTOR_ZONES;
  return TRACTOR_ZONES.filter(z => z.id !== 't_axa3_st' && z.id !== 't_axa3_dr');
}
function getVehicleZonesLabels(vehicleType) {
  return vehicleType === 'tractor' ? {...ZONES_LABELS, ...TRACTOR_ZONES_LABELS} : ZONES_LABELS;
}

const emptyTractor = () => ({
  numar_auto: "", serie_sasiu: "", tip: "Cap tractor", marca: "MAN", model: "",
  an_fabricatie: 2024, data_achizitie: "", pret_achizitie: 0, stare: "Activ\u0103",
  nr_axe: 2, lungime: 0, latime: 0, inaltime: 0, greutate_proprie: 0,
  sarcina_maxima: 0, culoare: "", itp_expirare: "", rca_expirare: "",
  dotari: "", observatii: "", vehicle_type: "tractor",
  putere_cp: 0, norma_poluare: "Euro 6", configuratie_axe: "4x2",
  sarcina_sa: 0, tahograf_expirare: "", copie_conforma_expirare: "", km_achizitie: 0,
  anvelope: TRACTOR_AXE_POS_4x2.map(p => ({ pozitie: p, profil: "", marca: "", serie: "", placute: "" }))
});

const ZONES = [
  {id:"fata",label:"FAȚĂ",desc:"Cuplare & picioare",checks:["Cui cuplare – fără uzură excesivă, bine fixat","Picioare susținere – verificat, lubrifiat, neîndoite","Panou frontal extern – stare bună","Panou frontal intern – fără deteriorări"],photoSlots:[{id:"fata_general",label:"Față general",required:true},{id:"cuplare",label:"Sistem cuplare",required:true},{id:"picioare",label:"Picioare",required:true}]},
  {id:"spate",label:"SPATE",desc:"Uși, stopuri, protecție",checks:["Uși spate – închidere corectă, fără jocuri","Cheder etanșeitate uși","Mecanism închidere – stare bună","Bara spate protecție – bine fixată","Stopuri și semnalizatoare spate","Reflectorizante conforme","Cablu vamal"],photoSlots:[{id:"spate_general",label:"Spate general",required:true},{id:"usi_spate",label:"Uși deschise",required:true}]},
  {id:"lateral_st",label:"LATERAL ST.",desc:"Prelată stânga",checks:["Prelată laterală stânga – fără rupturi","Sistem culisare prelată – funcțional","Lampi gabarit laterale stânga","Inscripționare LA MARITEC stânga","Barele laterale protecție – fixate"],photoSlots:[{id:"lat_st_general",label:"Lateral stânga",required:true},{id:"lat_st_prelata",label:"Prelată stânga",required:true},{id:"lat_st_inscriptie",label:"Inscripție stânga",required:true}]},
  {id:"lateral_dr",label:"LATERAL DR.",desc:"Prelată dreapta",checks:["Prelată laterală dreapta – fără rupturi","Sistem culisare prelată – funcțional","Lampi gabarit laterale dreapta","Inscripționare LA MARITEC dreapta","Barele laterale protecție – fixate"],photoSlots:[{id:"lat_dr_general",label:"Lateral dreapta",required:true},{id:"lat_dr_prelata",label:"Prelată dreapta",required:true},{id:"lat_dr_inscriptie",label:"Inscripție dreapta",required:true}]},
  {id:"acoperis",label:"ACOPERIȘ",desc:"Prelată sus, tavan",checks:["Prelată sus – fără rupturi sau pete mari","Sistem tensionare (cabluri, șine, role, catarame)","Tavan interior – stare bună"],photoSlots:[{id:"prelata_sus",label:"Prelată sus",required:true},{id:"tavan",label:"Tavan interior",required:true}]},
  {id:"interior",label:"INTERIOR",desc:"Podea, pereți, curățenie",checks:["Podea – fără găuri, crăpături, deteriorare gravă","Pereți interiori – curați, fără deteriorări","Spălare completă interior","Curățare și degresare podea","Fără urme de umezeală, mucegai, chimicale","REMORCA CURATĂ"],photoSlots:[{id:"int_fata",label:"Interior față",required:true},{id:"int_spate",label:"Interior spate",required:true},{id:"int_mijloc",label:"Interior mijloc",required:true},{id:"podea",label:"Podea",required:true}]},
  {id:"sasiu_zona",label:"ȘASIU",desc:"Structură, rugină",checks:["Stare generală șasiu – fără fisuri, rugină avansată, îndoiri","Verificare suduri și nituri stâlpi/uși"],photoSlots:[{id:"sasiu_st_fata",label:"Șasiu st. față",required:true},{id:"sasiu_st_spate",label:"Șasiu st. spate",required:true},{id:"sasiu_dr_fata",label:"Șasiu dr. față",required:true},{id:"sasiu_dr_spate",label:"Șasiu dr. spate",required:true}]},
  {id:"frane_zona",label:"FRÂNE",desc:"ABS/EBS, furtunuri",checks:["ABS/EBS funcțional – fără erori","Furtunuri aer și racorduri – fără fisuri, scurgeri","Frâna de mână – blocare/deblocare corectă"],photoSlots:[{id:"frane_detaliu",label:"Sistem frânare",required:true},{id:"furtunuri",label:"Furtunuri aer",required:true}]},
  {id:"electric_zona",label:"ELECTRIC",desc:"Faruri, cabluri",checks:["Faruri, stopuri, semnalizatoare, poziții – funcționale","Instalație electrică – fără improvizații, fire rupte","Cablu conectare electrică cap tractor – verificat","Plăcuțe indicatoare reflectorizante – vizibile"],photoSlots:[{id:"electric_spate",label:"Lampi spate",required:true}]},
  {id:"documente",label:"DOCUMENTE",desc:"Serie, talon, ITP, RCA",checks:["Serie șasiu – vizibilă și conformă cu actele","Talon – verificare autenticitate","ITP valabil","Asigurare RCA valabilă"],photoSlots:[{id:"serie_sasiu",label:"Serie șasiu",required:true},{id:"talon",label:"Talon",required:true},{id:"asigurare",label:"Asigurare RCA",required:true}]},
  {id:"dotari_zona",label:"DOTĂRI",desc:"Paleți, roată rezervă, scule",checks:["Suport roată de rezervă","Ladă paleți","Ladă scule"],photoSlots:[],dynamicPhotos:true},
  {id:"axa1_st",label:"ROATA A1 ST.",desc:"Axa 1 stânga",isWheel:true,wheelIdx:0,checks:["Anvelopă fără crăpături, tăieturi, uzură neuniformă","Jantă fără fisuri, îndoituri","Prezoane strânse, fără lipsuri","Senzor presiune"],photoSlots:[{id:"r_a1_st",label:"Roata A1 St.",required:true}]},
  {id:"axa1_dr",label:"ROATA A1 DR.",desc:"Axa 1 dreapta",isWheel:true,wheelIdx:1,checks:["Anvelopă fără crăpături, tăieturi, uzură neuniformă","Jantă fără fisuri, îndoituri","Prezoane strânse, fără lipsuri","Senzor presiune"],photoSlots:[{id:"r_a1_dr",label:"Roata A1 Dr.",required:true}]},
  {id:"axa2_st",label:"ROATA A2 ST.",desc:"Axa 2 stânga",isWheel:true,wheelIdx:2,checks:["Anvelopă fără crăpături, tăieturi, uzură neuniformă","Jantă fără fisuri, îndoituri","Prezoane strânse, fără lipsuri","Senzor presiune"],photoSlots:[{id:"r_a2_st",label:"Roata A2 St.",required:true}]},
  {id:"axa2_dr",label:"ROATA A2 DR.",desc:"Axa 2 dreapta",isWheel:true,wheelIdx:3,checks:["Anvelopă fără crăpături, tăieturi, uzură neuniformă","Jantă fără fisuri, îndoituri","Prezoane strânse, fără lipsuri","Senzor presiune"],photoSlots:[{id:"r_a2_dr",label:"Roata A2 Dr.",required:true}]},
  {id:"axa3_st",label:"ROATA A3 ST.",desc:"Axa 3 stânga",isWheel:true,wheelIdx:4,checks:["Anvelopă fără crăpături, tăieturi, uzură neuniformă","Jantă fără fisuri, îndoituri","Prezoane strânse, fără lipsuri","Senzor presiune"],photoSlots:[{id:"r_a3_st",label:"Roata A3 St.",required:true}]},
  {id:"axa3_dr",label:"ROATA A3 DR.",desc:"Axa 3 dreapta",isWheel:true,wheelIdx:5,checks:["Anvelopă fără crăpături, tăieturi, uzură neuniformă","Jantă fără fisuri, îndoituri","Prezoane strânse, fără lipsuri","Senzor presiune"],photoSlots:[{id:"r_a3_dr",label:"Roata A3 Dr.",required:true}]},
];

/* ─── HELPERS ─── */
const daysTo = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : 999;
const eur = (v) => (v || 0).toLocaleString("ro-RO") + " €";
const fmtDT = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric" }) + ", " + dt.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });
};
const fmtLei = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " lei";
};
const emptyTrailer = () => ({
  numar_auto: "", serie_sasiu: "", tip: "Prelată", marca: "Krone", model: "",
  an_fabricatie: 2024, data_achizitie: "", pret_achizitie: 0, stare: "Activă",
  nr_axe: 3, lungime: 13.6, latime: 2.48, inaltime: 2.7, greutate_proprie: 0,
  sarcina_maxima: 33000, culoare: "", itp_expirare: "", rca_expirare: "",
  dotari: "", observatii: "",
  anvelope: AXE_POS.map(p => ({ pozitie: p, profil: "", marca: "", serie: "", placute: "" }))
});

const AUTH_COOKIE_NAME = "lm_token";
const AUTH_COOKIE_MAX_AGE = 8 * 60 * 60;

function setAuthCookie(token) {
  if (!token) return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${AUTH_COOKIE_NAME}=${encodeURIComponent(token)}; path=/; max-age=${AUTH_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

function clearAuthCookie() {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${AUTH_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax${secure}`;
}

try {
  const bootToken = localStorage.getItem("lm_token");
  if (bootToken) setAuthCookie(bootToken);
} catch {
  // ignore boot-time storage issues
}

/* ─── API HELPER ─── */
const api = {
  _token: () => localStorage.getItem("lm_token"),
  _headers: () => ({
    "Content-Type": "application/json",
    ...(localStorage.getItem("lm_token") ? { Authorization: `Bearer ${localStorage.getItem("lm_token")}` } : {})
  }),
  async login(username, password) {
    const r = await fetch("/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, password }) });
    if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Eroare login"); }
    return r.json();
  },
  async get(url) {
    const r = await fetch(url, { headers: this._headers() });
    if (r.status === 401) { localStorage.removeItem("lm_token"); localStorage.removeItem("lm_user"); clearAuthCookie(); window.location.reload(); return; }
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || "Eroare server"); }
    return r.json();
  },
  async post(url, data) {
    const r = await fetch(url, { method: "POST", headers: this._headers(), body: JSON.stringify(data) });
    if (r.status === 401) { localStorage.removeItem("lm_token"); localStorage.removeItem("lm_user"); clearAuthCookie(); window.location.reload(); return; }
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      // Adaugare pur aditiva: pastram mesajul (singurul lucru citit de apelantii
      // existenti) si atasam statusul + corpul, ca un apelant sa poata reactiona
      // la un raspuns structurat — ex. 409 duplicate cu existing_pv_id la PV-uri.
      const err = new Error(e.error || "Eroare server");
      err.status = r.status;
      err.data = e;
      throw err;
    }
    return r.json();
  },
  async put(url, data) {
    const r = await fetch(url, { method: "PUT", headers: this._headers(), body: JSON.stringify(data) });
    if (r.status === 401) { localStorage.removeItem("lm_token"); localStorage.removeItem("lm_user"); clearAuthCookie(); window.location.reload(); return; }
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || "Eroare server"); }
    return r.json();
  },
  async del(url) {
    const r = await fetch(url, { method: "DELETE", headers: this._headers() });
    if (r.status === 401) { localStorage.removeItem("lm_token"); localStorage.removeItem("lm_user"); clearAuthCookie(); window.location.reload(); return; }
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || "Eroare server"); }
    return r.json();
  },
  async uploadPhoto(formData) {    const ctrl = new AbortController();    const timer = setTimeout(() => ctrl.abort(), PHOTO_UPLOAD_TIMEOUT_MS);    try {      const r = await fetch("/api/photos/upload", {        method: "POST",        headers: { Authorization: `Bearer ${localStorage.getItem("lm_token")}` },        body: formData,        signal: ctrl.signal      });      clearTimeout(timer);      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || "Eroare upload"); }      return r.json();    } catch (ex) {      clearTimeout(timer);      if (ex.name === "AbortError") throw new Error("Timeout — conexiune prea lenta. Reincercati.");      throw ex;    }  }









};

/* ─── PROFESSIONAL PV PDF BUILDER ─── */
function buildPvHtmlPro(opts) {
  const { pvId, pvType, date, trailer, clientName, clientEmail, clientCui, clientPhone, clientContact, clientAddress, checksData, wheelDataMap, signatureUrl, photosArr, mentionsData, driverName, driverIdCard, dispecerName, scanduriData } = opts;
  const today = date ? fmtDT(date) : fmtDT(new Date());
  const isInsp = pvType === "inspectie";
  const pvLabel = pvType === "predare" ? "De primire" : pvType === "inspectie" ? "Inspecție" : "De restituire";
  const yr = new Date().getFullYear();
  const pvNr = pvId ? `PV-${String(pvId).padStart(3, "0")}` : "";

  const plBar = (pl) => {
    const v = Number(pl) || 0;
    const col = v > BRAKE_PAD_GREEN_THRESHOLD ? "#16a34a" : v > BRAKE_PAD_YELLOW_THRESHOLD ? "#ea580c" : "#dc2626";
    return `<div style="display:flex;align-items:center;gap:6px"><div style="width:60px;height:7px;background:#e5e7eb;border-radius:4px;overflow:hidden"><div style="width:${v}%;height:100%;background:${col};border-radius:4px"></div></div><span style="font-weight:700;color:${col};font-size:12px">${v}%</span></div>`;
  };

  // ── ZONES CHECKS (1 coloană, full width) ──
  const zonesHtml = ZONES.filter(z => !z.isWheel).map(z => {
    const zc = checksData[z.id];
    if (!zc || Object.keys(zc).length === 0) return "";
    const items = z.checks.map((ch, idx) => {
      const c = zc[idx] || (Array.isArray(zc) ? zc.find(x => x.check_index === idx) : null);
      const st = c?.status;
      if (!st) return "";
      const isOk = st === "ok";
      const isProblem = st === "problem";
      const icon = isOk ? "✓" : isProblem ? "✗" : "—";
      const bg = isOk ? "#ecfdf5" : isProblem ? "#fef2f2" : "#f9fafb";
      const col = isOk ? "#16a34a" : isProblem ? "#dc2626" : "#8896a6";
      const note = isProblem && (c.note || c.check_note) ? `<div style="margin:3px 0 0 28px;padding:3px 8px;background:#fef2f2;border-left:3px solid #dc2626;border-radius:0 4px 4px 0;color:#dc2626;font-size:11px;font-weight:600">${c.note || c.check_note}</div>` : "";
      const problemBorder = isProblem ? "border-left:3px solid #dc2626;" : "";
      return `<div style="display:flex;align-items:flex-start;gap:6px;padding:4px 8px;background:${bg};border-radius:5px;margin-bottom:2px;${problemBorder}"><span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:${col};color:#fff;font-size:11px;font-weight:700;flex-shrink:0;margin-top:1px">${icon}</span><div style="flex:1"><span style="font-size:12px;color:#1a2332">${ch}</span>${note}</div></div>`;
    }).filter(Boolean).join("");
    if (!items) return "";
    const mention = mentionsData?.[z.id] ? `<div style="margin-top:3px;padding:3px 8px;background:#fffbeb;border-radius:4px;font-size:11px;font-style:italic;color:#92400e;border:1px solid #fef3c7">Mențiuni: ${mentionsData[z.id]}</div>` : "";
    return `<div class="zone-section" style="margin-bottom:8px"><div style="font-weight:700;color:#2563eb;font-size:12px;margin-bottom:3px;padding-bottom:2px;border-bottom:2px solid #dbeafe">${z.label} — ${z.desc}</div>${items}${mention}</div>`;
  }).join("");

  // ── WHEEL ZONES (1 coloană) ──
  const wheelZonesHtml = ZONES.filter(z => z.isWheel).map(z => {
    const zc = checksData[z.id];
    const wd = wheelDataMap[z.id] || {};
    if (!zc || Object.keys(zc).length === 0) return "";
    const items = z.checks.map((ch, idx) => {
      const c = zc[idx] || (Array.isArray(zc) ? zc.find(x => x.check_index === idx) : null);
      const st = c?.status;
      if (!st) return "";
      const isOk = st === "ok";
      const isProblem = st === "problem";
      const icon = isOk ? "✓" : isProblem ? "✗" : "—";
      const bg = isOk ? "#ecfdf5" : isProblem ? "#fef2f2" : "#f9fafb";
      const col = isOk ? "#16a34a" : isProblem ? "#dc2626" : "#8896a6";
      const note = isProblem && (c.note || c.check_note) ? `<div style="margin:3px 0 0 28px;padding:3px 8px;background:#fef2f2;border-left:3px solid #dc2626;border-radius:0 4px 4px 0;color:#dc2626;font-size:11px;font-weight:600">${c.note || c.check_note}</div>` : "";
      const problemBorder = isProblem ? "border-left:3px solid #dc2626;" : "";
      return `<div style="display:flex;align-items:flex-start;gap:6px;padding:4px 8px;background:${bg};border-radius:5px;margin-bottom:2px;${problemBorder}"><span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:${col};color:#fff;font-size:11px;font-weight:700;flex-shrink:0;margin-top:1px">${icon}</span><div style="flex:1"><span style="font-size:12px;color:#1a2332">${ch}</span>${note}</div></div>`;
    }).filter(Boolean).join("");
    const tireCard = (wd.marca || wd.profil) ? `<div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;padding:6px 10px;margin-top:3px;display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:4px;font-size:11px"><div><span style="color:#6b7280;font-size:9px;text-transform:uppercase">Marcă</span><div style="font-weight:700;color:#1a2332">${wd.marca || "—"}</div></div><div><span style="color:#6b7280;font-size:9px;text-transform:uppercase">Profil</span><div style="font-weight:700;color:#1a2332">${wd.profil || "—"}</div></div><div><span style="color:#6b7280;font-size:9px;text-transform:uppercase">Serie</span><div style="color:#1a2332;font-family:monospace;font-size:10px">${wd.serie || "—"}</div></div><div><span style="color:#6b7280;font-size:9px;text-transform:uppercase">Plăcuțe</span>${plBar(wd.placute)}</div></div>` : "";
    return `<div class="zone-section" style="margin-bottom:8px"><div style="font-weight:700;color:#2563eb;font-size:12px;margin-bottom:3px;padding-bottom:2px;border-bottom:2px solid #dbeafe">${z.label} — ${z.desc}</div>${items}${tireCard}</div>`;
  }).join("");

  // ── ANVELOPE SUMAR (cu coloană Senzor Presiune) ──
  const thStyle = 'padding:5px 8px;text-align:left;font-size:10px;text-transform:uppercase;color:#1e40af;letter-spacing:0.5px;border-bottom:2px solid #bfdbfe';
  const tdStyle = 'padding:5px 8px;border-bottom:1px solid #e5e7eb;font-size:11px';
  const wheelRows = ZONES.filter(z => z.isWheel).map(z => {
    const wd = wheelDataMap[z.id] || {};
    const zc = checksData[z.id];
    const senzorCheck = zc ? (zc[3] || (Array.isArray(zc) ? zc.find(x => x.check_index === 3) : null)) : null;
    const senzorOk = senzorCheck?.status === "ok";
    const senzorIcon = senzorCheck ? (senzorOk ? `<span style="color:#16a34a;font-weight:700">✓</span>` : `<span style="color:#dc2626;font-weight:700">✗</span>`) : "—";
    return `<tr><td style="${tdStyle};font-weight:600">${z.label}</td><td style="${tdStyle}">${wd.marca || "—"}</td><td style="${tdStyle}">${wd.profil || "—"}</td><td style="${tdStyle};font-family:monospace;font-size:10px">${wd.serie || "—"}</td><td style="${tdStyle}">${plBar(wd.placute)}</td><td style="${tdStyle};text-align:center">${senzorIcon}</td></tr>`;
  }).join("");

  // ── PHOTOS (URL-uri absolute) ──
  let photosHtml = "";
  if (photosArr && photosArr.length > 0) {
    const byZone = {};
    photosArr.forEach(p => { const z = p.zone_id || "other"; if (!byZone[z]) byZone[z] = []; byZone[z].push(p); });
    let pSections = "";
    for (const [zid, ps] of Object.entries(byZone)) {
      const zLabel = ZONES.find(z => z.id === zid)?.label || zid;
      const imgs = ps.filter(p => p.base64).map(p => {
        return `<div style="text-align:center"><img src="${p.base64}" style="width:140px;height:105px;object-fit:cover;border-radius:8px;border:1px solid #dce3ec" /><div style="font-size:9px;color:#6b7280;margin-top:2px">${p.label || p.slot_id || ""}</div></div>`;
      }).join("");
      pSections += `<div style="margin-bottom:6px"><div style="font-size:11px;font-weight:600;color:#4a5568;margin-bottom:3px">${zLabel}</div><div style="display:flex;flex-wrap:wrap;gap:8px">${imgs}</div></div>`;
    }
    photosHtml = `<div class="zone-section" style="margin-top:8px"><div style="font-size:14px;font-weight:700;color:#1a2332;margin-bottom:6px;padding-bottom:3px;border-bottom:3px solid #2563eb">FOTOGRAFII</div>${pSections}</div>`;
  }

  // ── DOTĂRI ──
  let dotariHtml = "";
  const dotZc = checksData["dotari_zona"];
  if (dotZc) {
    const dotLabels = ["Suport roată de rezervă", "Ladă paleți", "Ladă scule"];
    const dotItems = dotLabels.map((lbl, i) => {
      const c = dotZc[i] || (Array.isArray(dotZc) ? dotZc.find(x => x.check_index === i) : null);
      if (!c?.status) return "";
      const isOk = c.status === "ok";
      const noteHtml = !isOk && c.note ? ` — <span style="font-style:italic">${c.note}</span>` : "";
      return `<span style="display:inline-block;padding:3px 10px;border-radius:16px;font-size:11px;font-weight:600;margin:2px;background:${isOk ? "#ecfdf5" : "#fef2f2"};color:${isOk ? "#16a34a" : "#dc2626"};border:1px solid ${isOk ? "#bbf7d0" : "#fecaca"}">${isOk ? "✓" : "✗"} ${lbl}${noteHtml}</span>`;
    }).filter(Boolean).join("");
    // Scanduri
    let scandHtml = "";
    if (scanduriData?.present === true) {
      scandHtml = `<span style="display:inline-block;padding:3px 10px;border-radius:16px;font-size:11px;font-weight:600;margin:2px;background:#ecfdf5;color:#16a34a;border:1px solid #bbf7d0">✓ Scanduri — Lemn: ${scanduriData.lemn || 0} buc., Aluminiu: ${scanduriData.aluminiu || 0} buc.</span>`;
    } else if (scanduriData?.present === false) {
      scandHtml = `<span style="display:inline-block;padding:3px 10px;border-radius:16px;font-size:11px;font-weight:600;margin:2px;background:#fef2f2;color:#dc2626;border:1px solid #fecaca">✗ Scanduri</span>`;
    }
    const allDot = [dotItems, scandHtml].filter(Boolean).join("");
    if (allDot) {
      dotariHtml = `<div class="zone-section" style="margin-top:8px"><div style="font-size:14px;font-weight:700;color:#1a2332;margin-bottom:4px;padding-bottom:3px;border-bottom:3px solid #2563eb">DOTĂRI</div><div>${allDot}</div></div>`;
    }
  }

  // ── INFO CARDS ──
  const infoCard = (label, value) => `<div style="background:#f5f7fb;border-radius:8px;border:1px solid #dce3ec;padding:8px 12px"><div style="font-size:9px;text-transform:uppercase;letter-spacing:0.5px;color:#6b7280;margin-bottom:2px">${label}</div><div style="font-weight:700;color:#1a2332;font-size:13px">${value}</div></div>`;

  // Build transportator card with extra details
  let transportatorCard = "";
  if (!isInsp) {
    let transportatorVal = clientName || "—";
    const lines = [];
    if (clientCui) lines.push(`<b>CUI:</b> ${clientCui}`);
    if (clientAddress) lines.push(`<b>Adresa:</b> ${opts.clientAddress}`);
    if (clientPhone) lines.push(`<b>Tel:</b> ${clientPhone}`);
    if (clientEmail) lines.push(`<b>Email:</b> ${clientEmail}`);
    if (clientContact) lines.push(`<b>Contact:</b> ${clientContact}`);
    if (lines.length) transportatorVal += `<div style="font-size:10px;font-weight:400;color:#4a5568;margin-top:3px;line-height:1.6">${lines.join("<br/>")}</div>`;
    transportatorCard = `<div style="background:#f5f7fb;border-radius:8px;border:1px solid #dce3ec;padding:8px 12px;grid-column:span 2"><div style="font-size:9px;text-transform:uppercase;letter-spacing:0.5px;color:#6b7280;margin-bottom:2px">Transportator</div><div style="font-weight:700;color:#1a2332;font-size:13px">${transportatorVal}</div></div>`;
  } else {
    transportatorCard = infoCard("Tip Inspecție", "Inspecție internă");
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${pvNr || "PV"} ${pvLabel} - ${trailer.numar_auto}</title>
<style>
@font-face{font-family:'Bira';src:url('/Bira.ttf') format('truetype');font-display:swap}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,system-ui,'Segoe UI',sans-serif;color:#1a2332;background:#fff}
.zone-section{page-break-inside:auto}
@media print{
  .no-print{display:none!important}
  img{max-width:180px!important;height:135px!important;object-fit:cover!important}
  .zone-section{page-break-inside:auto}
  body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
}
@page{size:A4;margin:10mm}
</style></head><body>

<!-- HEADER -->
<div style="background:linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%);padding:20px 32px 18px;text-align:center;position:relative">
  <div style="font-family:'Bira';font-size:48px;color:#fff;letter-spacing:2px;line-height:1">La Maritec</div>
  <div style="font-size:11px;color:rgba(255,255,255,0.85);margin-top:3px;font-weight:600;letter-spacing:1px">SC LA MARITEC SRL</div>
  ${pvNr ? `<div style="position:absolute;top:12px;left:16px;background:rgba(255,255,255,0.2);border:1px solid rgba(255,255,255,0.4);border-radius:6px;padding:3px 10px;font-size:13px;font-weight:800;color:#fff;letter-spacing:1px">${pvNr}</div>` : ""}
  <div style="margin-top:10px">
    <div style="display:inline-block;background:rgba(255,255,255,0.2);border:2px solid rgba(255,255,255,0.5);border-radius:24px;padding:8px 28px">
      <div style="font-size:17px;font-weight:800;color:#fff;letter-spacing:2px">NOTĂ DE CONSTATARE SEMIREMORCĂ</div>
      <div style="font-size:12px;color:rgba(255,255,255,0.95);margin-top:2px;font-weight:500">${pvLabel} &bull; ${today}</div>
    </div>
  </div>
</div>

<div style="padding:12px 28px;max-width:820px;margin:0 auto">

<!-- INFO -->
<div class="zone-section" style="margin-bottom:10px">
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px">
    ${infoCard("Număr Înmatriculare", `<span style="font-size:20px">${trailer.numar_auto}</span>`)}
    ${infoCard("Serie Șasiu", `<span style="font-family:monospace;font-size:12px">${trailer.serie_sasiu || "—"}</span>`)}
    ${infoCard("Data PV", today)}
    ${transportatorCard}
    ${infoCard("Tip / Marcă / Model", `${trailer.tip || "—"} &bull; ${trailer.marca || "—"} ${trailer.model || ""}`)}
    ${infoCard("An fabricație", trailer.an_fabricatie || "—")}
  </div>
</div>

<!-- VERIFICĂRI -->
<div style="margin-bottom:10px">
  <div style="font-size:14px;font-weight:700;color:#1a2332;margin-bottom:6px;padding-bottom:3px;border-bottom:3px solid #2563eb">VERIFICĂRI PE ZONE</div>
  ${zonesHtml}
</div>

<!-- WHEEL ZONES -->
${wheelZonesHtml ? `<div style="margin-bottom:10px"><div style="font-size:14px;font-weight:700;color:#1a2332;margin-bottom:6px;padding-bottom:3px;border-bottom:3px solid #2563eb">VERIFICĂRI ROȚI</div>${wheelZonesHtml}</div>` : ""}

<!-- FOTOGRAFII -->
${photosHtml}

<!-- ANVELOPE SUMAR -->
<div class="zone-section" style="margin-bottom:10px;margin-top:8px">
  <div style="font-size:14px;font-weight:700;color:#1a2332;margin-bottom:6px;padding-bottom:3px;border-bottom:3px solid #2563eb">SUMAR ANVELOPE</div>
  <table style="width:100%;border-collapse:collapse">
    <thead><tr style="background:linear-gradient(135deg,#eff6ff,#dbeafe)">
      <th style="${thStyle}">Poziție</th>
      <th style="${thStyle}">Marcă</th>
      <th style="${thStyle}">Profil</th>
      <th style="${thStyle}">Serie</th>
      <th style="${thStyle}">Plăcuțe</th>
      <th style="${thStyle};text-align:center">Senzor</th>
    </tr></thead>
    <tbody>${wheelRows}</tbody>
  </table>
</div>

${dotariHtml}

<!-- SEMNĂTURI -->
<div class="zone-section" style="margin-top:10px;display:grid;grid-template-columns:1fr 1fr;gap:10px">
  <div style="background:#f5f7fb;border-radius:8px;padding:12px 14px;border:1px solid #dce3ec;text-align:center">
    <div style="font-size:12px;font-weight:700;color:#1a2332;margin-bottom:16px">SC LA MARITEC SRL</div>
    <div style="height:60px;display:flex;align-items:center;justify-content:center;color:#c1cad6;font-size:11px">Spațiu ștampilă</div>
    <div style="border-top:1px solid #c1cad6;padding-top:5px;font-size:10px;color:#6b7280">Ștampila și semnătura</div>
  </div>
  <div style="background:#f5f7fb;border-radius:8px;padding:12px 14px;border:1px solid #dce3ec;text-align:center">
    <div style="font-size:12px;font-weight:700;color:#1a2332;margin-bottom:4px">${isInsp ? "DISPECER" : "TRANSPORTATOR"}</div>
    ${!isInsp ? `<div style="font-size:10px;color:#6b7280;margin-bottom:4px">${clientName || ""}</div>` : `<div style="font-size:10px;color:#6b7280;margin-bottom:4px">${dispecerName || ""}</div>`}
    ${signatureUrl ? `<img src="${signatureUrl}" style="max-width:180px;max-height:60px;margin:0 auto;display:block" />` : `<div style="height:60px"></div>`}
    <div style="border-top:1px solid #c1cad6;padding-top:5px;font-size:10px;color:#6b7280">Semnătura</div>
    ${!isInsp && clientContact ? `<div style="font-size:10px;color:#4a5568;margin-top:3px">Numele: <b>${clientContact}</b></div>` : ""}
    ${!isInsp && driverName ? `<div style="font-size:10px;color:#4a5568;margin-top:3px">Șofer: <b>${driverName}</b></div>` : ""}
    ${!isInsp && driverIdCard ? `<div style="font-size:10px;color:#4a5568;margin-top:1px">C.I.: <b>${driverIdCard}</b></div>` : ""}
  </div>
</div>

<!-- FOOTER WARNING -->
<div style="margin-top:10px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;padding:10px 14px;text-align:center">
  <div style="font-size:11px;color:#1e40af;line-height:1.4">
    <b>ATENȚIE:</b> Odată ce semiremorca părăsește unitatea noastră, nu mai suntem răspunzători pentru eventuale defecte apărute ulterior.
  </div>
</div>

<!-- DOWNLOAD PDF BUTTON -->
<div class="no-print" style="text-align:center;margin:16px 0">
  <button onclick="window.print()" style="background:linear-gradient(135deg,#1e3a5f,#2563eb);color:#fff;border:none;padding:12px 40px;border-radius:10px;font-size:16px;font-weight:700;cursor:pointer;font-family:inherit;letter-spacing:0.5px;box-shadow:0 4px 14px rgba(37,99,235,0.3)">
    📥 Descarcă PDF
  </button>
  <div style="margin-top:6px;font-size:11px;color:#8896a6">Alege "Save as PDF" din dialogul de print</div>
</div>

<div style="text-align:center;font-size:9px;color:#c1cad6;margin-top:8px;padding-top:6px;border-top:1px solid #eef2f7">
  Document generat automat — La Maritec Fleet &copy; ${yr}
</div>

</div></body></html>`;
}

// Fetch photos as base64 data URLs (with auth token)
async function fetchPhotosBase64(photosArr) {
  const token = localStorage.getItem("lm_token");
  const results = await Promise.all((photosArr || []).map(async (p) => {
    try {
      const r = await fetch(`/api/photos/file/${p.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!r.ok) return { ...p, base64: null };
      const blob = await r.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve({ ...p, base64: reader.result });
        reader.onerror = () => resolve({ ...p, base64: null });
        reader.readAsDataURL(blob);
      });
    } catch { return { ...p, base64: null }; }
  }));
  return results;
}

function openPvHtmlWindow(pvId) {
  if (!pvId) return false;
  try {
    const token = localStorage.getItem("lm_token");
    if (token) setAuthCookie(token);
  } catch {
    // ignore storage/cookie sync issues
  }
  const w = window.open(`/api/pvs/${pvId}/html?ts=${Date.now()}`, "_blank");
  return Boolean(w);
}

function AuthPhotoThumb({
  photo,
  src,
  width = 140,
  height = 105,
  radius = 8,
  labelSize = 10,
  placeholderText = "Se încarcă...",
  placeholderBg = "#f8fafc",
  loadingFontSize = 11,
}) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const previewSrc = src || (photo?.id ? `/api/photos/thumb/${photo.id}?r=${retryKey}` : null);

  useEffect(() => {
    setFailed(false);
    setLoaded(false);
    setRetryKey(0);
  }, [photo?.id, src]);

  useEffect(() => {
    if (!previewSrc) {
      setLoaded(false);
      setFailed(false);
      return;
    }

    let cancelled = false;
    const probe = new window.Image();
    probe.decoding = "async";

    probe.onload = () => {
      if (cancelled) return;
      setFailed(false);
      setLoaded(true);
    };

    probe.onerror = () => {
      if (cancelled) return;
      const token = localStorage.getItem("lm_token");
      if (retryKey === 0 && token) {
        setAuthCookie(token);
        setRetryKey(1);
        return;
      }
      setLoaded(false);
      setFailed(true);
    };

    probe.src = previewSrc;
    if (probe.complete && probe.naturalWidth > 0) {
      setFailed(false);
      setLoaded(true);
    }

    return () => {
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [previewSrc, retryKey]);

  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{ width, height, borderRadius: radius, overflow: "hidden", border: `1px solid ${C.border}`, cursor: (photo?.id || src) ? "pointer" : "default", position: "relative", background: placeholderBg }}
        onClick={() => {
          if (photo?.id) {
            window.open(`/api/photos/file/${photo.id}?open=${Date.now()}`, "_blank");
            return;
          }
          if (src) window.open(src, "_blank");
        }}
      >
        {previewSrc && (
          <img
            src={previewSrc}
            alt={photo?.label || "Foto"}
            loading="eager"
            decoding="async"
            width={width}
            height={height}
            style={{ width: "100%", height: "100%", objectFit: "cover", opacity: loaded && !failed ? 1 : 0, transition: "opacity 0.15s ease" }}
          />
        )}
        {(!previewSrc || failed || !loaded) && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: placeholderBg, color: C.text2, fontSize: loadingFontSize }}>
            {failed ? "Nu s-a încărcat" : placeholderText}
          </div>
        )}
      </div>
      <div style={{ fontSize: labelSize, color: C.text2, marginTop: 3 }}>{photo?.label || photo?.slot_id || ""}</div>
    </div>
  );
}

// Open PV PDF in new window — from API data (checks as array)
async function downloadPvPdf(pvData, trailer, setLoadingPdf) {
  if (setLoadingPdf) setLoadingPdf(true);
  try {
    if (!openPvHtmlWindow(pvData?.id)) {
      throw new Error("Popup blocat");
    }
  } catch (err) {
    console.error("PDF generation error:", err);
    alert("Eroare la generarea PDF-ului.");
  }
  if (setLoadingPdf) setLoadingPdf(false);
}

/* ═══════════════════════════════════════════════════════════
   COLORS / THEME
   ═══════════════════════════════════════════════════════════ */
const C = {
  bg: "#eef2f7", card: "#ffffff", card2: "#f5f7fb", accent: "#2563eb",
  accent2: "#2563eb", green: "#16a34a", orange: "#ea580c", red: "#dc2626",
  yellow: "#ca8a04", text: "#1a2332", text2: "#4a5568", border: "#dce3ec",
  white: "#ffffff", muted: "#8896a6",
};

/* ═══════════════════════════════════════════════════════════
   STYLES
   ═══════════════════════════════════════════════════════════ */
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const S = {
  app: { fontFamily: FONT, background: C.bg, color: C.text, minHeight: "100vh", display: "flex" },
  // Login
  loginWrap: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: `linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)` },
  loginBox: { background: C.card, borderRadius: 16, padding: "48px 40px", width: 380, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", border: `1px solid ${C.border}`, textAlign: "center" },
  loginTitle: { fontFamily: "'Bira'", fontSize: 32, color: C.accent, marginBottom: 8 },
  loginSub: { color: C.text2, fontSize: 14, marginBottom: 32 },
  input: { width: "100%", padding: "12px 16px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: 15, outline: "none", boxSizing: "border-box", marginBottom: 16, fontFamily: FONT },
  btn: { padding: "12px 24px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 15, fontWeight: 600, fontFamily: FONT, transition: "all 0.2s" },
  btnPrimary: { background: C.accent, color: C.white },
  btnDanger: { background: C.red, color: C.white },
  btnGhost: { background: "transparent", color: C.accent, border: `1px solid ${C.accent}` },
  // Sidebar
  sidebar: { width: 220, minWidth: 220, background: "linear-gradient(180deg, #1e3a5f 0%, #162d4a 100%)", display: "flex", flexDirection: "column", position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 200 },
  sidebarLogo: { padding: "24px 20px 16px", borderBottom: "1px solid rgba(255,255,255,0.1)" },
  sidebarUser: { padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.1)" },
  sidebarNav: { flex: 1, padding: "12px 10px", display: "flex", flexDirection: "column", gap: 2 },
  sidebarBtn: { display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 14, fontFamily: FONT, background: "transparent", color: "rgba(255,255,255,0.7)", transition: "all 0.15s", textAlign: "left", width: "100%", position: "relative" },
  sidebarBtnActive: { background: "rgba(255,255,255,0.15)", color: "#ffffff" },
  sidebarFooter: { padding: "12px 10px", borderTop: "1px solid rgba(255,255,255,0.1)" },
  // Mobile header
  mobileHeader: { display: "none", background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)", padding: "0 16px", height: 56, alignItems: "center", justifyContent: "space-between", position: "fixed", top: 0, left: 0, right: 0, zIndex: 150, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" },
  sidebarOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 240, transition: "opacity 0.3s ease" },
  // Layout (old header kept for overlay pages)
  header: { background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)", padding: "0 32px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
  headerTitle: { fontFamily: "'Bira'", fontSize: 22, color: "#ffffff", letterSpacing: 1, cursor: "pointer" },
  nav: { display: "flex", gap: 8, alignItems: "center" },
  navBtn: { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 14, fontFamily: FONT, background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.8)", transition: "all 0.2s" },
  navBtnActive: { background: "rgba(255,255,255,0.25)", color: "#ffffff" },
  content: { padding: "24px 32px", maxWidth: 1400, margin: "0 auto", flex: 1 },
  // Cards
  statCard: { background: C.card, borderRadius: 12, padding: "20px 24px", border: `1px solid ${C.border}`, flex: 1, minWidth: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
  statVal: { fontSize: 36, fontWeight: 700 },
  statLabel: { fontSize: 13, color: C.text2, marginTop: 4 },
  // Table
  table: { width: "100%", borderCollapse: "separate", borderSpacing: "0 4px" },
  th: { textAlign: "left", padding: "12px 16px", fontSize: 12, color: C.text2, textTransform: "uppercase", letterSpacing: 1 },
  td: { padding: "14px 16px", background: C.card, fontSize: 14, color: C.text },
  // Detail
  detailHeader: { display: "flex", gap: 24, marginBottom: 24, flexWrap: "wrap" },
  infoGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 },
  infoItem: { background: C.card2, borderRadius: 8, padding: "12px 16px", border: `1px solid ${C.border}` },
  infoLabel: { fontSize: 11, color: C.text2, textTransform: "uppercase", letterSpacing: 0.5 },
  infoVal: { fontSize: 15, marginTop: 4, color: C.text, wordWrap: "break-word", overflowWrap: "break-word" },
  // Modal
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: C.card, borderRadius: 16, padding: 32, width: "90%", maxWidth: 700, maxHeight: "85vh", overflow: "auto", border: `1px solid ${C.border}`, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" },
  // Badge
  badge: (color) => ({ display: "inline-block", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: color + "22", color, border: `1px solid ${color}44` }),
  // Tabs
  tabs: { display: "flex", gap: 4, marginBottom: 24, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 },
  tab: { padding: "10px 20px", cursor: "pointer", fontSize: 14, color: C.text2, borderBottom: "2px solid transparent", transition: "all 0.2s", fontFamily: FONT, background: "none", border: "none" },
  tabActive: { color: C.accent, borderBottom: `2px solid ${C.accent}` },
};

const stareColor = (s) => {
  if (s === "Activă") return C.green;
  if (s === "Închiriată") return C.accent;
  if (s === "În service") return C.orange;
  if (s === "Returnată") return "#8b5cf6";
  if (s === "Scoasă din uz") return C.red;
  if (s === "De vânzare") return C.yellow;
  if (s === "În așteptare") return "#f59e0b";
  if (s === "Vânzare în rate") return "#7c3aed";
  return C.text2;
};

/* ═══════════════════════════════════════════════════════════
   SVG TRAILER TOP VIEW
   ═══════════════════════════════════════════════════════════ */
function TrailerSVG({ onZoneClick, activeZone, zoneStatus }) {
  const zoneStyle = (id) => {
    const isActive = activeZone === id;
    const status = zoneStatus?.[id];
    let fill = "rgba(37,99,235,0.06)";
    if (status === "done") fill = "rgba(22,163,74,0.12)";
    if (status === "partial") fill = "rgba(234,88,12,0.12)";
    if (isActive) fill = "rgba(37,99,235,0.18)";
    return { fill, stroke: isActive ? C.accent : C.border, strokeWidth: isActive ? 2 : 1, cursor: "pointer", transition: "all 0.2s" };
  };

  const wheelStyle = (id) => {
    const isActive = activeZone === id;
    const status = zoneStatus?.[id];
    let fill = "#e8edf4";
    if (status === "done") fill = "rgba(22,163,74,0.2)";
    if (status === "partial") fill = "rgba(234,88,12,0.2)";
    if (isActive) fill = "rgba(37,99,235,0.25)";
    return { fill, stroke: isActive ? C.accent : "#b0bec5", strokeWidth: isActive ? 2.5 : 1.5, cursor: "pointer", rx: 3 };
  };

  const labelStyle = { fill: C.text2, fontSize: 7, textAnchor: "middle", pointerEvents: "none", fontFamily: FONT };

  return (
    <svg viewBox="0 0 200 500" style={{ width: "100%", maxWidth: 240, height: "auto" }}>
      {/* Body outline */}
      <rect x="40" y="30" width="120" height="420" rx="6" fill="#f0f4f8" stroke={C.border} strokeWidth="1.5" />

      {/* FAȚĂ */}
      <rect x="42" y="32" width="116" height="60" rx="4" {...zoneStyle("fata")} onClick={() => onZoneClick("fata")} />
      <text x="100" y="65" {...labelStyle}>FAȚĂ</text>

      {/* ACOPERIȘ */}
      <rect x="60" y="95" width="80" height="30" rx="3" {...zoneStyle("acoperis")} onClick={() => onZoneClick("acoperis")} />
      <text x="100" y="114" {...labelStyle}>ACOPERIȘ</text>

      {/* LATERAL ST */}
      <rect x="42" y="95" width="16" height="310" rx="2" {...zoneStyle("lateral_st")} onClick={() => onZoneClick("lateral_st")} />
      <text x="50" y="250" {...labelStyle} style={{...labelStyle, writingMode: "tb", fontSize: 6}}>LAT.ST</text>

      {/* LATERAL DR */}
      <rect x="142" y="95" width="16" height="310" rx="2" {...zoneStyle("lateral_dr")} onClick={() => onZoneClick("lateral_dr")} />
      <text x="150" y="250" {...labelStyle} style={{...labelStyle, writingMode: "tb", fontSize: 6}}>LAT.DR</text>

      {/* INTERIOR */}
      <rect x="60" y="128" width="80" height="100" rx="3" {...zoneStyle("interior")} onClick={() => onZoneClick("interior")} />
      <text x="100" y="182" {...labelStyle}>INTERIOR</text>

      {/* ȘASIU */}
      <rect x="60" y="232" width="80" height="40" rx="3" {...zoneStyle("sasiu_zona")} onClick={() => onZoneClick("sasiu_zona")} />
      <text x="100" y="256" {...labelStyle}>ȘASIU</text>

      {/* FRÂNE */}
      <rect x="60" y="276" width="38" height="35" rx="3" {...zoneStyle("frane_zona")} onClick={() => onZoneClick("frane_zona")} />
      <text x="79" y="297" {...labelStyle} style={{...labelStyle, fontSize: 6}}>FRÂNE</text>

      {/* ELECTRIC */}
      <rect x="102" y="276" width="38" height="35" rx="3" {...zoneStyle("electric_zona")} onClick={() => onZoneClick("electric_zona")} />
      <text x="121" y="297" {...labelStyle} style={{...labelStyle, fontSize: 6}}>ELECTRIC</text>

      {/* DOCUMENTE */}
      <rect x="60" y="315" width="38" height="35" rx="3" {...zoneStyle("documente")} onClick={() => onZoneClick("documente")} />
      <text x="79" y="336" {...labelStyle} style={{...labelStyle, fontSize: 5.5}}>DOCUMENTE</text>

      {/* DOTĂRI */}
      <rect x="102" y="315" width="38" height="35" rx="3" {...zoneStyle("dotari_zona")} onClick={() => onZoneClick("dotari_zona")} />
      <text x="121" y="336" {...labelStyle} style={{...labelStyle, fontSize: 6}}>DOTĂRI</text>

      {/* SPATE */}
      <rect x="42" y="388" width="116" height="60" rx="4" {...zoneStyle("spate")} onClick={() => onZoneClick("spate")} />
      <text x="100" y="422" {...labelStyle}>SPATE</text>

      {/* Wheels - Axa 1 */}
      <rect x="20" y="355" width="18" height="32" {...wheelStyle("axa1_st")} onClick={() => onZoneClick("axa1_st")} />
      <text x="29" y="375" {...labelStyle} style={{...labelStyle, fontSize: 5}}>A1S</text>
      <rect x="162" y="355" width="18" height="32" {...wheelStyle("axa1_dr")} onClick={() => onZoneClick("axa1_dr")} />
      <text x="171" y="375" {...labelStyle} style={{...labelStyle, fontSize: 5}}>A1D</text>

      {/* Wheels - Axa 2 */}
      <rect x="20" y="393" width="18" height="32" {...wheelStyle("axa2_st")} onClick={() => onZoneClick("axa2_st")} />
      <text x="29" y="413" {...labelStyle} style={{...labelStyle, fontSize: 5}}>A2S</text>
      <rect x="162" y="393" width="18" height="32" {...wheelStyle("axa2_dr")} onClick={() => onZoneClick("axa2_dr")} />
      <text x="171" y="413" {...labelStyle} style={{...labelStyle, fontSize: 5}}>A2D</text>

      {/* Wheels - Axa 3 */}
      <rect x="20" y="431" width="18" height="32" {...wheelStyle("axa3_st")} onClick={() => onZoneClick("axa3_st")} />
      <text x="29" y="451" {...labelStyle} style={{...labelStyle, fontSize: 5}}>A3S</text>
      <rect x="162" y="431" width="18" height="32" {...wheelStyle("axa3_dr")} onClick={() => onZoneClick("axa3_dr")} />
      <text x="171" y="451" {...labelStyle} style={{...labelStyle, fontSize: 5}}>A3D</text>

      {/* Coupling pin */}
      <circle cx="100" cy="22" r="6" fill="#f0f4f8" stroke={C.accent} strokeWidth="1.5" />
      <circle cx="100" cy="22" r="2" fill={C.accent} />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════
   SVG TRAILER DETAIL VIEW (with tire data on wheels)
   ═══════════════════════════════════════════════════════════ */
function TrailerDetailSVG({ anvelope, onZoneClick, activeZone }) {
  const wheelIds = [
    { id: "axa1_st", idx: 0, x: 4, y: 355, label: "A1 St" },
    { id: "axa1_dr", idx: 1, x: 162, y: 355, label: "A1 Dr" },
    { id: "axa2_st", idx: 2, x: 4, y: 397, label: "A2 St" },
    { id: "axa2_dr", idx: 3, x: 162, y: 397, label: "A2 Dr" },
    { id: "axa3_st", idx: 4, x: 4, y: 439, label: "A3 St" },
    { id: "axa3_dr", idx: 5, x: 162, y: 439, label: "A3 Dr" },
  ];

  // Clickable body zones (non-wheel)
  const bodyZones = [
    { id: "fata", x: 42, y: 32, w: 116, h: 55, label: "FAȚĂ" },
    { id: "spate", x: 42, y: 400, w: 116, h: 55, label: "SPATE" },
    { id: "lateral_st", x: 42, y: 95, w: 16, h: 190, label: "LAT.ST", lx: 50, ly: 195 },
    { id: "lateral_dr", x: 142, y: 95, w: 16, h: 190, label: "LAT.DR", lx: 150, ly: 195 },
    { id: "acoperis", x: 62, y: 95, w: 76, h: 55, label: "ACOPERIȘ" },
    { id: "interior", x: 62, y: 155, w: 76, h: 55, label: "INTERIOR" },
    { id: "sasiu_zona", x: 62, y: 215, w: 76, h: 35, label: "ȘASIU" },
    { id: "frane_zona", x: 62, y: 255, w: 76, h: 30, label: "FRÂNE" },
    { id: "electric_zona", x: 62, y: 290, w: 38, h: 28, label: "ELEC.", lx: 81, ly: 307 },
    { id: "documente", x: 100, y: 290, w: 38, h: 28, label: "DOC.", lx: 119, ly: 307 },
    { id: "dotari_zona", x: 62, y: 323, w: 76, h: 28, label: "DOTĂRI" },
  ];

  const tireData = (idx) => {
    if (!anvelope || !anvelope[idx]) return null;
    return anvelope[idx];
  };

  const plColor = (pl) => {
    const v = Number(pl) || 0;
    return v > 50 ? C.green : v > 25 ? C.orange : C.red;
  };

  const labelStyle = { fill: C.text2, fontSize: 7, textAnchor: "middle", pointerEvents: "none", fontFamily: FONT };

  const zoneStyle = (id) => {
    const isActive = activeZone === id;
    return {
      fill: isActive ? C.accent + "30" : "rgba(37,99,235,0.05)",
      stroke: isActive ? C.accent : C.border,
      strokeWidth: isActive ? 2 : 1,
      cursor: "pointer",
    };
  };

  return (
    <svg viewBox="0 0 200 520" style={{ width: "100%", maxWidth: 260, height: "auto" }}>
      {/* Body outline */}
      <rect x="40" y="30" width="120" height="420" rx="6" fill="#f0f4f8" stroke={C.border} strokeWidth="1.5" />

      {/* Coupling */}
      <circle cx="100" cy="22" r="6" fill="#f0f4f8" stroke={C.accent} strokeWidth="1.5" />
      <circle cx="100" cy="22" r="2" fill={C.accent} />

      {/* Clickable body zones */}
      {bodyZones.map(z => (
        <g key={z.id} onClick={() => onZoneClick(z.id)} style={{ cursor: "pointer" }}>
          <rect x={z.x} y={z.y} width={z.w} height={z.h} rx="3" {...zoneStyle(z.id)} />
          <text x={z.lx || (z.x + z.w / 2)} y={z.ly || (z.y + z.h / 2 + 3)}
            style={{ ...labelStyle, fontSize: z.w < 40 ? 5.5 : 7, fill: activeZone === z.id ? C.accent : C.text2, fontWeight: activeZone === z.id ? 700 : 400 }}>
            {z.label}
          </text>
        </g>
      ))}

      {/* Wheels with data */}
      {wheelIds.map(w => {
        const tire = tireData(w.idx);
        const isActive = activeZone === w.id;
        const pl = Number(tire?.placute) || 0;
        const color = plColor(pl);
        const isLeft = w.x < 100;
        const profil = Number(tire?.profil) || 0;
        const profilColor = profil > 100 ? "#16a34a" : profil >= 50 ? "#ea580c" : profil > 0 ? "#dc2626" : "#8896a6";

        return (
          <g key={w.id} onClick={() => onZoneClick(w.id)} style={{ cursor: "pointer" }}>
            <rect x={w.x} y={w.y} width="34" height="42" rx="4"
              fill={isActive ? C.accent + "25" : "#e8edf4"}
              stroke={isActive ? C.accent : "#b0bec5"}
              strokeWidth={isActive ? 2.5 : 1.5}
            />
            {/* Line 1: Profil */}
            <text x={w.x + 17} y={w.y + 10} style={{ ...labelStyle, fontSize: 7.5, fontWeight: 700, fill: profilColor, textAnchor: "middle" }}>
              {profil > 0 ? profil + "mm" : ""}
            </text>
            {/* Line 2: Placute % */}
            <text x={w.x + 17} y={w.y + 21} style={{ ...labelStyle, fontSize: 9, fontWeight: 700, fill: isActive ? C.accent : C.text, textAnchor: "middle" }}>
              {pl > 0 ? `${pl}%` : "—"}
            </text>
            {/* Progress bar for placute */}
            <rect x={w.x + 2} y={w.y + 25} width="30" height="4" rx="2" fill="#d1d5db" />
            <rect x={w.x + 2} y={w.y + 25} width={Math.max(0, Math.min(30, pl * 0.3))} height="4" rx="2" fill={color} />
            {/* Line 3: Marca (inside wheel, bottom) */}
            <text x={w.x + 17} y={w.y + 38} style={{ ...labelStyle, fontSize: 5, fill: C.text2, textAnchor: "middle" }}>
              {tire?.marca || ""}
            </text>
            {/* Label outside */}
            <text x={isLeft ? w.x + 36 + 3 : w.x - 3} y={w.y + 18}
              style={{ ...labelStyle, fontSize: 5.5, fill: C.muted || C.text2, textAnchor: isLeft ? "start" : "end", fontWeight: 600 }}>
              {w.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════
   LOGIN PAGE
   ═══════════════════════════════════════════════════════════ */
function LoginPage({ onLogin }) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await api.login(user, pass);
      localStorage.setItem("lm_token", data.token);
      localStorage.setItem("lm_user", JSON.stringify(data.user));
      setAuthCookie(data.token);
      onLogin(data.user);
    } catch (ex) {
      setErr(ex.message);
    }
    setLoading(false);
  };

  return (
    <div style={S.loginWrap}>
      <form onSubmit={handleLogin} style={S.loginBox}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🚛</div>
        <div style={S.loginTitle}>LA MARITEC</div>
        <div style={S.loginSub}>Fleet Management Platform</div>
        {err && <div style={{ color: C.red, marginBottom: 16, fontSize: 14, padding: "8px 12px", background: C.red + "15", borderRadius: 8 }}>{err}</div>}
        <input style={S.input} placeholder="Username" value={user} onChange={e => setUser(e.target.value)} autoFocus />
        <input style={S.input} placeholder="Parolă" type="password" value={pass} onChange={e => setPass(e.target.value)} />
        <button type="submit" disabled={loading} style={{ ...S.btn, ...S.btnPrimary, width: "100%", marginTop: 8, opacity: loading ? 0.6 : 1 }}>
          {loading ? "Se autentifică..." : "Autentificare"}
        </button>
      </form>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DASHBOARD
   ═══════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   DIFFERENCES REPORT
   ═══════════════════════════════════════════════════════════ */
function DifferencesReport({ pvId, onClose, onSendToService, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiPhotoUrls, setAiPhotoUrls] = useState({});
  const [aiPhotoErrors, setAiPhotoErrors] = useState({});
  const aiPollRef = useRef(null);
  const [estimatedCosts, setEstimatedCosts] = useState({});

  const loadDiffs = useCallback(async () => {
    try {
      const result = await api.get(`/api/pvs/${pvId}/differences`);
      setData(result);
      // Init estimated costs from DB
      const ec = {};
      (result.differences || []).forEach(d => { if (d.estimated_cost != null) ec[d.id] = parseFloat(d.estimated_cost); });
      setEstimatedCosts(prev => ({ ...prev, ...ec }));
      // Pre-load comparison photo blob URLs (cache: no-store to bypass stale browser cache)
      const photoIds = new Set();
      (result.differences || []).forEach(d => {
        if (d.photo_predare_id) photoIds.add(d.photo_predare_id);
        if (d.photo_returnare_id) photoIds.add(d.photo_returnare_id);
      });
      if (photoIds.size > 0) {
        const token = localStorage.getItem("lm_token");
        const urls = {};
        const errs = {};
        await Promise.all([...photoIds].map(async (pid) => {
          try {
            const r = await fetch(`/api/photos/file/${pid}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, cache: "no-store" });
            if (r.ok) { const blob = await r.blob(); urls[pid] = URL.createObjectURL(blob); }
            else { errs[pid] = true; console.warn(`Photo ${pid}: HTTP ${r.status}`); }
          } catch (e) { errs[pid] = true; console.warn(`Photo ${pid} fetch error:`, e.message); }
        }));
        setAiPhotoUrls(prev => ({ ...prev, ...urls }));
        setAiPhotoErrors(prev => ({ ...prev, ...errs }));
      }
      return result;
    } catch (ex) { console.error(ex); return null; }
  }, [pvId]);

  const saveEstimatedCost = async (diffId, cost) => {
    try {
      await api.put("/api/pvs/" + pvId + "/differences/" + diffId + "/estimate", { estimated_cost: cost || null });
    } catch (ex) { console.error("Save cost error:", ex); }
  };

  useEffect(() => {
    (async () => {
      const result = await loadDiffs();
      setLoading(false);
      // If AI analysis is pending, poll every 5 seconds
      if (result?.ai_pending) {
        aiPollRef.current = setInterval(async () => {
          const updated = await loadDiffs();
          if (updated && !updated.ai_pending) {
            clearInterval(aiPollRef.current);
          }
        }, AI_POLL_INTERVAL_MS);
      }
    })();
    return () => { if (aiPollRef.current) clearInterval(aiPollRef.current); };
  }, [loadDiffs]);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: C.text2 }}>Se încarcă raportul...</div>;
  if (!data || !data.differences || data.differences.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ color: C.green, marginBottom: 8 }}>Nicio diferență găsită</h2>
        <p style={{ color: C.text2, marginBottom: 24 }}>Remorca a fost returnată în aceeași stare ca la predare.</p>
        <button style={{ ...S.btn, ...S.btnPrimary }} onClick={onClose}>Închide</button>
      </div>
    );
  }

  const sevColor = { critical: C.red, major: C.orange, minor: C.green };
  const sevLabel = { critical: "CRITIC", major: "MAJOR", minor: "MINOR" };
  const diffs = data.differences;
  const critCount = diffs.filter(d => d.severity === "critical").length;
  const majorCount = diffs.filter(d => d.severity === "major").length;
  const minorCount = diffs.filter(d => d.severity === "minor").length;
  const predareDate = data.predare ? fmtDT(data.predare.created_at || data.predare.date) : "—";
  const returnareDate = data.returnare ? fmtDT(data.returnare.created_at || data.returnare.date) : "—";

  // Group wheel data for comparison table
  const wheelZones = ["axa1_st","axa1_dr","axa2_st","axa2_dr","axa3_st","axa3_dr"];
  const predWheelMap = {};
  (data.predareWheels || []).forEach(w => { predWheelMap[w.zone_id] = w; });
  const retWheelMap = {};
  (data.returnareWheels || []).forEach(w => { retWheelMap[w.zone_id] = w; });

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 16px", overflowX: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ color: C.text, margin: 0, fontSize: 22 }}>📊 Raport Diferențe</h2>
          <div style={{ color: C.text2, fontSize: 13, marginTop: 4, wordBreak: "break-word" }}>
            {data.trailer?.numar_auto || ""}
          </div>
          <div style={{ color: C.text2, fontSize: 12, marginTop: 2, wordBreak: "break-word" }}>
            Predare: {predareDate}
          </div>
          <div style={{ color: C.text2, fontSize: 12, wordBreak: "break-word" }}>
            Returnare: {returnareDate}
          </div>
        </div>
        <button style={{ ...S.btn, ...S.btnGhost, flexShrink: 0 }} onClick={onClose}>← Înapoi</button>
      </div>

      {/* AI pending banner */}
      {data.ai_pending && (
        <div style={{ background: "#eff6ff", border: `1px solid #bfdbfe`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 20, height: 20, border: `3px solid ${C.accent}`, borderTop: "3px solid transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
          <div>
            <div style={{ fontWeight: 600, color: C.accent, fontSize: 14 }}>Analiză AI în curs...</div>
            <div style={{ color: C.text2, fontSize: 12 }}>Claude analizează fotografiile pentru diferențe vizuale. Rezultatele vor apărea automat.</div>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Summary badges */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {critCount > 0 && <div style={{ ...S.statCard, flex: "0 0 auto", padding: "10px 20px", borderLeft: `4px solid ${C.red}` }}><span style={{ fontSize: 22, fontWeight: 700, color: C.red }}>{critCount}</span><div style={{ fontSize: 11, color: C.text2 }}>Critice</div></div>}
        {majorCount > 0 && <div style={{ ...S.statCard, flex: "0 0 auto", padding: "10px 20px", borderLeft: `4px solid ${C.orange}` }}><span style={{ fontSize: 22, fontWeight: 700, color: C.orange }}>{majorCount}</span><div style={{ fontSize: 11, color: C.text2 }}>Majore</div></div>}
        {minorCount > 0 && <div style={{ ...S.statCard, flex: "0 0 auto", padding: "10px 20px", borderLeft: `4px solid ${C.green}` }}><span style={{ fontSize: 22, fontWeight: 700, color: C.green }}>{minorCount}</span><div style={{ fontSize: 11, color: C.text2 }}>Minore</div></div>}
        <div style={{ ...S.statCard, flex: "0 0 auto", padding: "10px 20px", borderLeft: `4px solid ${C.accent}` }}><span style={{ fontSize: 22, fontWeight: 700, color: C.accent }}>{diffs.length}</span><div style={{ fontSize: 11, color: C.text2 }}>Total</div></div>
        {diffs.some(d => d.ai_detected) && (
          <div style={{ ...S.statCard, flex: "0 0 auto", padding: "10px 20px", borderLeft: "4px solid #7c3aed" }}><span style={{ fontSize: 22, fontWeight: 700, color: "#7c3aed" }}>{diffs.filter(d => d.ai_detected).length}</span><div style={{ fontSize: 11, color: C.text2 }}>Detectate AI</div></div>
        )}
      </div>

      {/* Differences list */}
      <div style={{ ...S.statCard, marginBottom: 20 }}>
        <h3 style={{ color: C.text, marginBottom: 12, fontSize: 16 }}>Diferențe Constatate</h3>
        {(() => {
          // Constatarile AI ale aceleiasi perechi de poze au acelasi rezumat si
          // aceleasi doua imagini, deci randate individual arata ca acelasi rand
          // repetat de 3-5 ori. Le grupam intr-un singur card, cu constatarile
          // listate dedesubt. Randurile ne-AI (checklist, anvelope) ramin 1:1.
          const grupuri = [];
          const indexAi = new Map();
          diffs.forEach(d => {
            const cheie = (d.ai_detected && d.photo_predare_id && d.photo_returnare_id)
              ? `${d.photo_predare_id}-${d.photo_returnare_id}` : null;
            if (cheie && indexAi.has(cheie)) { grupuri[indexAi.get(cheie)].items.push(d); return; }
            if (cheie) indexAi.set(cheie, grupuri.length);
            grupuri.push({ head: d, items: [d] });
          });
          return grupuri.map((g, i) => {
          const d = g.head;
          // Severitatea cardului = cea mai grava din grup.
          const sevMax = ["critical", "major", "minor"].find(s => g.items.some(it => it.severity === s)) || d.severity;
          return (
          <div key={d.id || i} style={{
            padding: "10px 14px", marginBottom: 6,
            background: d.ai_detected ? "#f5f3ff" : sevMax === "critical" ? "#fef2f2" : sevMax === "major" ? "#fffbeb" : "#f0fdf4",
            borderRadius: 8, borderLeft: `4px solid ${d.ai_detected ? "#7c3aed" : sevColor[sevMax]}`,
          }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", maxWidth: "100%", overflow: "hidden" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                <span style={{
                  ...S.badge(sevColor[sevMax]), fontSize: 10, fontWeight: 700,
                  padding: "3px 8px", minWidth: 55, textAlign: "center"
                }}>
                  {sevLabel[sevMax]}
                </span>
                {d.ai_detected && (
                  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 9, fontWeight: 700, background: "#ede9fe", color: "#7c3aed", border: "1px solid #c4b5fd", textAlign: "center" }}>
                    Detectat AI
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: C.text, fontSize: 13, wordBreak: "break-word", overflowWrap: "break-word" }}>
                  {ZONES_LABELS[d.zone_id] || d.zone_id}
                  {d.check_label && <span style={{ fontWeight: 400, color: C.text2 }}> — {d.check_label}</span>}
                  {g.items.length > 1 && <span style={{ fontWeight: 400, color: C.text2 }}> — {g.items.length} constatări</span>}
                </div>
                {g.items.map(it => (
                  <div key={it.id} style={{ marginTop: g.items.length > 1 ? 6 : 2 }}>
                    <div style={{ fontSize: 12, color: C.text2, wordBreak: "break-word", overflowWrap: "break-word" }}>
                      {g.items.length > 1 && (
                        <span style={{ ...S.badge(sevColor[it.severity]), fontSize: 9, fontWeight: 700, padding: "1px 6px", marginRight: 6 }}>
                          {sevLabel[it.severity]}
                        </span>
                      )}
                      {it.description}
                    </div>
                    {/* Estimated cost — per constatare */}
                    {(user?.role === "admin" || user?.role === "dispecer") ? (
                      <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 11, color: C.text2, whiteSpace: "nowrap" }}>💰 Cost est.:</span>
                        <input type="number" step="0.01" min="0" style={{ ...S.input, width: 100, fontSize: 12, padding: "3px 8px", marginBottom: 0 }}
                          value={estimatedCosts[it.id] ?? ""} placeholder="0.00"
                          onChange={e => setEstimatedCosts(prev => ({ ...prev, [it.id]: e.target.value }))}
                          onBlur={e => saveEstimatedCost(it.id, e.target.value)} />
                        <span style={{ fontSize: 11, color: C.muted }}>lei</span>
                      </div>
                    ) : (it.estimated_cost > 0 && (
                      <div style={{ marginTop: 4, fontSize: 12, color: C.text2 }}>💰 Cost estimat: <b>{fmtLei(it.estimated_cost)}</b></div>
                    ))}
                  </div>
                ))}
                {d.predare_value && d.returnare_value && (
                  <div style={{ display: "flex", gap: 8, marginTop: 6, fontSize: 12, flexWrap: "wrap" }}>
                    <div style={{ wordBreak: "break-word" }}><span style={{ color: C.green, fontWeight: 600 }}>Predare:</span> {d.predare_value}</div>
                    <div style={{ wordBreak: "break-word" }}><span style={{ color: C.red, fontWeight: 600 }}>Returnare:</span> {d.returnare_value}</div>
                  </div>
                )}
                {d.ai_rezumat && (
                  <div style={{ marginTop: 6, padding: "4px 8px", background: "#ede9fe", borderRadius: 4, fontSize: 11, color: "#5b21b6", fontStyle: "italic" }}>
                    {d.ai_rezumat}
                  </div>
                )}
              </div>
            </div>
            {/* Photo comparison (predare vs returnare) */}
            {d.photo_predare_id && d.photo_returnare_id && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.green, marginBottom: 4, textTransform: "uppercase" }}>Predare</div>
                  <div style={{ width: "100%", height: 140, borderRadius: 8, overflow: "hidden", border: `2px solid ${C.green}40`, cursor: "pointer" }}
                    onClick={() => { if (aiPhotoUrls[d.photo_predare_id]) window.open(aiPhotoUrls[d.photo_predare_id], "_blank"); }}>
                    {aiPhotoUrls[d.photo_predare_id] ? (
                      <img src={aiPhotoUrls[d.photo_predare_id]} alt="Predare" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0fdf4", color: aiPhotoErrors[d.photo_predare_id] ? C.muted : C.text2, fontSize: 11 }}>{aiPhotoErrors[d.photo_predare_id] ? "Poză indisponibilă" : "Se încarcă..."}</div>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.red, marginBottom: 4, textTransform: "uppercase" }}>Returnare</div>
                  <div style={{ width: "100%", height: 140, borderRadius: 8, overflow: "hidden", border: `2px solid ${C.red}40`, cursor: "pointer" }}
                    onClick={() => { if (aiPhotoUrls[d.photo_returnare_id]) window.open(aiPhotoUrls[d.photo_returnare_id], "_blank"); }}>
                    {aiPhotoUrls[d.photo_returnare_id] ? (
                      <img src={aiPhotoUrls[d.photo_returnare_id]} alt="Returnare" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#fef2f2", color: aiPhotoErrors[d.photo_returnare_id] ? C.muted : C.text2, fontSize: 11 }}>{aiPhotoErrors[d.photo_returnare_id] ? "Poză indisponibilă" : "Se încarcă..."}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ); }); })()}
      </div>

      {/* Wheel comparison table */}
      {(data.predareWheels.length > 0 || data.returnareWheels.length > 0) && (() => {
        const wa = data.wear_analysis;
        const waMap = {};
        if (wa?.wheels) wa.wheels.forEach(w => { waMap[w.zone_id] = w; });
        return (
          <div style={{ ...S.statCard, marginBottom: 20 }}>
            <h3 style={{ color: C.text, marginBottom: 4, fontSize: 16 }}>Comparativ Anvelope</h3>
            {wa && <div style={{ fontSize: 12, color: C.text2, marginBottom: 8 }}>KM parcurși: <b>{wa.km_parcursi?.toLocaleString() || "—"}</b></div>}
            {!wa && <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, fontStyle: "italic" }}>Notă: Lipsesc datele de KM — uzura e calculată doar ca diferență de profil/plăcuțe.</div>}
            {/* Mobile cards */}
            <div className="lm-wheel-cards" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {wheelZones.map(zid => {
                const pw = predWheelMap[zid] || {};
                const rw = retWheelMap[zid] || {};
                const ww = waMap?.[zid];
                const tw = ww?.tire_wear;
                const bw = ww?.brake_wear;
                const profilPred = parseFloat(pw.profil) || 0;
                const profilRet = parseFloat(rw.profil) || 0;
                const profilDiff = Math.round((profilPred - profilRet) * 10) / 10;
                const placPred = parseInt(pw.placute) || 0;
                const placRet = parseInt(rw.placute) || 0;
                const placDiff = placPred - placRet;
                const brandChanged = pw.marca && rw.marca && pw.marca !== rw.marca;
                const diffColor = (d) => d > 20 ? C.red : d > 5 ? C.orange : C.green;
                const isExcessive = tw?.status === "excessive" || bw?.status === "excessive";
                return (
                  <div key={zid} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid " + (isExcessive ? "#fecaca" : C.border), background: isExcessive ? "#fef2f2" : "#fff" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{ZONES_LABELS[zid] || zid}</span>
                      {brandChanged && <span style={{ fontSize: 10, color: C.red }}>{pw.marca} → {rw.marca}</span>}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                      {/* Profil */}
                      <div>
                        <div style={{ fontSize: 10, color: C.text2, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>Profil</div>
                        <div style={{ display: "flex", gap: 4, alignItems: "baseline", flexWrap: "wrap" }}>
                          <span style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>{profilPred || "—"}</span>
                          <span style={{ color: C.text2, fontSize: 11 }}>→</span>
                          <span style={{ color: C.red, fontSize: 13, fontWeight: 600 }}>{profilRet || "—"}</span>
                        </div>
                        {(profilPred > 0 || profilRet > 0) && <div style={{ fontSize: 12, fontWeight: 700, color: diffColor(profilDiff), marginTop: 2 }}>{profilDiff > 0 ? "−" + profilDiff + "mm" : profilDiff === 0 ? "0mm" : "+" + Math.abs(profilDiff) + "mm"}</div>}
                        {tw && <div style={{ marginTop: 2 }}><span style={{ ...S.badge(tw.status === "excessive" ? C.red : tw.status === "low" ? C.accent : C.green), fontSize: 9, padding: "1px 6px" }}>{tw.status === "excessive" ? "EXCESIVĂ" : tw.status === "low" ? "MICĂ" : "NORMALĂ"}</span></div>}
                      </div>
                      {/* Placute */}
                      <div>
                        <div style={{ fontSize: 10, color: C.text2, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>Plăcuțe</div>
                        <div style={{ display: "flex", gap: 4, alignItems: "baseline", flexWrap: "wrap" }}>
                          <span style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>{placPred}%</span>
                          <span style={{ color: C.text2, fontSize: 11 }}>→</span>
                          <span style={{ color: C.red, fontSize: 13, fontWeight: 600 }}>{placRet}%</span>
                        </div>
                        {<div style={{ fontSize: 12, fontWeight: 700, color: diffColor(placDiff), marginTop: 2 }}>{placDiff > 0 ? "−" + placDiff + "%" : placDiff === 0 ? "0%" : "+" + Math.abs(placDiff) + "%"}</div>}
                        {bw && <div style={{ marginTop: 2 }}><span style={{ ...S.badge(bw.status === "excessive" ? C.red : bw.status === "low" ? C.accent : C.green), fontSize: 9, padding: "1px 6px" }}>{bw.status === "excessive" ? "EXCESIVĂ" : bw.status === "low" ? "MICĂ" : "NORMALĂ"}</span></div>}
                      </div>
                      {/* Marca */}
                      <div>
                        <div style={{ fontSize: 10, color: C.text2, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>Marcă</div>
                        <div style={{ fontSize: 12, color: C.text }}>{rw.marca || pw.marca || "—"}</div>
                        <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{rw.serie || pw.serie || ""}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Estimated cost total */}
      {(() => {
        const totalEst = Object.values(estimatedCosts).reduce((s, v) => s + (parseFloat(v) || 0), 0);
        if (totalEst <= 0 && !(user?.role === "admin" || user?.role === "dispecer")) return null;
        return (
          <div style={{ ...S.statCard, marginBottom: 20, textAlign: "center", padding: "16px 20px", borderLeft: "4px solid #f59e0b" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 20 }}>💰</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Cost estimativ total:</span>
              <span style={{ fontSize: 24, fontWeight: 800, color: "#f59e0b" }}>{fmtLei(totalEst)}</span>
            </div>
            <div style={{ fontSize: 11, color: C.muted }}>(fără TVA) — Acest cost poate fi refacturat transportatorului</div>
          </div>
        );
      })()}

      {/* Actions */}
      <div className="lm-diff-actions" style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 32, flexWrap: "wrap" }}>
        <button style={{ ...S.btn, ...S.btnPrimary }} onClick={() => {
          const w = window.open("", "_blank");
          if (!w) return;
          const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Raport Diferențe - ${data.trailer?.numar_auto || ""}</title>
<style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:-apple-system,system-ui,sans-serif;color:#1a2332;padding:24px;max-width:800px;margin:0 auto}
@media print{.no-print{display:none!important}} @page{size:A4;margin:10mm}
table{width:100%;border-collapse:collapse}th,td{padding:6px 10px;border:1px solid #dce3ec;font-size:12px;text-align:left}
th{background:#f5f7fb;font-weight:600;font-size:11px;text-transform:uppercase}
.sev{display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700;color:#fff}
.critical{background:#dc2626}.major{background:#ea580c}.minor{background:#16a34a}
</style></head><body>
<div style="border-bottom:3px solid #2563eb;padding-bottom:16px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:center">
<div><h1 style="color:#2563eb;font-size:24px">La Maritec</h1><div style="color:#4a5568;font-size:11px">Raport Diferențe</div></div>
<div style="text-align:right"><div style="font-size:16px;font-weight:700">${data.trailer?.numar_auto || ""}</div>
<div style="font-size:12px;color:#4a5568">Predare: ${predareDate} → Returnare: ${returnareDate}</div></div></div>
<p style="margin-bottom:16px;font-size:13px"><b>Total diferențe:</b> ${diffs.length} (${critCount} critice, ${majorCount} majore, ${minorCount} minore)</p>
<table style="margin-bottom:20px"><tr><th>Sev.</th><th>Zonă</th><th>Descriere</th><th>Pred.</th><th>Ret.</th><th>Cost est.</th></tr>${diffs.map(d => { const ec = estimatedCosts[d.id]; return `<tr><td style="width:70px"><span class="sev ${d.severity}">${sevLabel[d.severity]}</span></td><td style="font-weight:600">${ZONES_LABELS[d.zone_id] || d.zone_id}</td><td>${d.description}</td>${d.predare_value ? `<td style="color:#16a34a">${d.predare_value}</td><td style="color:#dc2626">${d.returnare_value}</td>` : `<td colspan="2"></td>`}<td style="text-align:right;font-weight:600">${ec > 0 ? parseFloat(ec).toFixed(2) + ' lei' : '—'}</td></tr>`; }).join("")}</table>
${(() => { const tot = Object.values(estimatedCosts).reduce((s,v) => s + (parseFloat(v)||0), 0); return tot > 0 ? '<div style="text-align:right;font-size:16px;font-weight:700;margin-bottom:20px;padding:8px 12px;background:#fffbeb;border-radius:8px">Cost estimativ total: ' + tot.toFixed(2) + ' lei (fără TVA)</div>' : ''; })()}
${(data.predareWheels.length > 0) ? `<h3 style="margin:20px 0 8px;font-size:14px;color:#2563eb">Comparativ Anvelope</h3>
<table><tr><th>Poziție</th><th>Plăc.Pred.</th><th>Plăc.Ret.</th><th>Diferență</th><th>Marcă Pred.</th><th>Marcă Ret.</th></tr>
${wheelZones.map(zid => { const pw=predWheelMap[zid]||{}; const rw=retWheelMap[zid]||{}; const d2=(pw.placute||0)-(rw.placute||0);
return `<tr><td style="font-weight:600">${ZONES_LABELS[zid]||zid}</td><td>${pw.placute||0}%</td><td>${rw.placute||0}%</td><td style="color:${d2>20?'#dc2626':'#1a2332'};font-weight:700">${d2>0?'−'+d2+'%':'0%'}</td><td>${pw.marca||'—'}</td><td>${rw.marca||'—'}</td></tr>`;}).join("")}</table>` : ""}
<div style="margin-top:32px;font-size:10px;color:#8896a6;text-align:center;border-top:1px solid #dce3ec;padding-top:12px">Document generat automat — La Maritec Fleet © ${new Date().getFullYear()}</div>
<div class="no-print" style="text-align:center;margin:20px 0"><button onclick="window.print()" style="background:linear-gradient(135deg,#1e3a5f,#2563eb);color:#fff;border:none;padding:12px 36px;border-radius:10px;font-size:15px;font-weight:700;cursor:pointer">📥 Descarcă PDF</button></div>
</body></html>`;
          w.document.write(html);
          w.document.close();
        }}>
          📥 Descarcă Raport PDF
        </button>
        {onSendToService && diffs.length > 0 && !data.has_active_service && (
          <button style={{ ...S.btn, background: C.orange, color: C.white }} onClick={() => onSendToService(data)}>
            🔧 Trimite la Service
          </button>
        )}
        {data.has_active_service && (
          <span style={{ ...S.badge(C.green), fontSize: 13, padding: "8px 16px" }}>✅ Trimis la Service</span>
        )}
        <button style={{ ...S.btn, ...S.btnGhost }} onClick={onClose}>Închide</button>
      </div>
    </div>
  );
}


/* ===== TRACTOR SVG (PV Wizard) ===== */
function TractorSVG({ onZoneClick, activeZone, zoneStatus, config }) {
  const is6x2 = config === '6x2';
  const zoneStyle = (id) => {
    const isActive = activeZone === id;
    const status = zoneStatus?.[id];
    let fill = "rgba(37,99,235,0.06)";
    if (status === "done") fill = "rgba(22,163,74,0.12)";
    if (status === "partial") fill = "rgba(234,88,12,0.12)";
    if (isActive) fill = "rgba(37,99,235,0.18)";
    return { fill, stroke: isActive ? C.accent : C.border, strokeWidth: isActive ? 2 : 1, cursor: "pointer", transition: "all 0.2s" };
  };
  const wheelStyle = (id) => {
    const isActive = activeZone === id;
    const status = zoneStatus?.[id];
    let fill = "#e8edf4";
    if (status === "done") fill = "rgba(22,163,74,0.2)";
    if (status === "partial") fill = "rgba(234,88,12,0.2)";
    if (isActive) fill = "rgba(37,99,235,0.25)";
    return { fill, stroke: isActive ? C.accent : "#b0bec5", strokeWidth: isActive ? 2.5 : 1.5, cursor: "pointer", rx: 3 };
  };
  const ls = { fill: C.text2, fontSize: 7, textAnchor: "middle", pointerEvents: "none", fontFamily: FONT };
  const h = is6x2 ? 520 : 480;
  return (
    <svg viewBox={`0 0 200 ${h}`} style={{ width: "100%", maxWidth: 240, height: "auto" }}>
      {/* Cab outline */}
      <rect x="35" y="20" width="130" height="160" rx="12" fill="#f0f4f8" stroke={C.border} strokeWidth="1.5" />
      {/* Chassis */}
      <rect x="45" y="180" width="110" height={is6x2?280:240} rx="4" fill="#f8fafc" stroke={C.border} strokeWidth="1" />

      {/* FATA */}
      <rect x="37" y="22" width="126" height="45" rx="10" {...zoneStyle("t_fata")} onClick={() => onZoneClick("t_fata")} />
      <text x="100" y="48" {...ls}>FATA</text>

      {/* ACOPERIS */}
      <rect x="55" y="70" width="90" height="25" rx="3" {...zoneStyle("t_acoperis")} onClick={() => onZoneClick("t_acoperis")} />
      <text x="100" y="86" {...ls}>ACOPERIS</text>

      {/* CABINA ST */}
      <rect x="37" y="70" width="16" height="108" rx="2" {...zoneStyle("t_cabina_st")} onClick={() => onZoneClick("t_cabina_st")} />
      <text x="45" y="125" {...ls} style={{...ls, writingMode: "tb", fontSize: 5}}>CAB.ST</text>

      {/* CABINA DR */}
      <rect x="147" y="70" width="16" height="108" rx="2" {...zoneStyle("t_cabina_dr")} onClick={() => onZoneClick("t_cabina_dr")} />
      <text x="155" y="125" {...ls} style={{...ls, writingMode: "tb", fontSize: 5}}>CAB.DR</text>

      {/* INTERIOR */}
      <rect x="55" y="98" width="90" height="45" rx="3" {...zoneStyle("t_interior")} onClick={() => onZoneClick("t_interior")} />
      <text x="100" y="124" {...ls}>INTERIOR</text>

      {/* MOTOR */}
      <rect x="55" y="146" width="90" height="30" rx="3" {...zoneStyle("t_motor")} onClick={() => onZoneClick("t_motor")} />
      <text x="100" y="164" {...ls}>MOTOR</text>

      {/* SA CUPLARE */}
      <rect x="60" y="185" width="80" height="35" rx="3" {...zoneStyle("t_sa")} onClick={() => onZoneClick("t_sa")} />
      <text x="100" y="206" {...ls}>SA CUPLARE</text>

      {/* SASIU */}
      <rect x="60" y="225" width="80" height="30" rx="3" {...zoneStyle("t_sasiu")} onClick={() => onZoneClick("t_sasiu")} />
      <text x="100" y="243" {...ls}>SASIU</text>

      {/* REZERVOR ST */}
      <rect x="47" y="260" width="20" height="40" rx="3" {...zoneStyle("t_rezervor_st")} onClick={() => onZoneClick("t_rezervor_st")} />
      <text x="57" y="283" {...ls} style={{...ls, fontSize: 4.5}}>REZ.ST</text>

      {/* REZERVOR DR */}
      <rect x="133" y="260" width="20" height="40" rx="3" {...zoneStyle("t_rezervor_dr")} onClick={() => onZoneClick("t_rezervor_dr")} />
      <text x="143" y="283" {...ls} style={{...ls, fontSize: 4.5}}>REZ.DR</text>

      {/* BATERII */}
      <rect x="70" y="260" width="26" height="25" rx="3" {...zoneStyle("t_baterii")} onClick={() => onZoneClick("t_baterii")} />
      <text x="83" y="276" {...ls} style={{...ls, fontSize: 5}}>BAT.</text>

      {/* FRANE */}
      <rect x="100" y="260" width="30" height="25" rx="3" {...zoneStyle("t_frane")} onClick={() => onZoneClick("t_frane")} />
      <text x="115" y="276" {...ls} style={{...ls, fontSize: 5}}>FRANE</text>

      {/* ELECTRIC */}
      <rect x="70" y="290" width="26" height="25" rx="3" {...zoneStyle("t_electric")} onClick={() => onZoneClick("t_electric")} />
      <text x="83" y="306" {...ls} style={{...ls, fontSize: 5}}>ELEC.</text>

      {/* DOCUMENTE */}
      <rect x="100" y="290" width="30" height="25" rx="3" {...zoneStyle("t_documente")} onClick={() => onZoneClick("t_documente")} />
      <text x="115" y="306" {...ls} style={{...ls, fontSize: 5}}>DOC.</text>

      {/* DOTARI */}
      <rect x="70" y="320" width="60" height="22" rx="3" {...zoneStyle("t_dotari")} onClick={() => onZoneClick("t_dotari")} />
      <text x="100" y="335" {...ls} style={{...ls, fontSize: 5.5}}>DOTARI</text>

      {/* SPATE */}
      <rect x="47" y={is6x2?430:390} width="106" height="40" rx="4" {...zoneStyle("t_spate")} onClick={() => onZoneClick("t_spate")} />
      <text x="100" y={is6x2?454:414} {...ls}>SPATE</text>

      {/* Wheels - Axa 1 (directie, simple) */}
      <rect x="18" y="155" width="15" height="28" {...wheelStyle("t_axa1_st")} onClick={() => onZoneClick("t_axa1_st")} />
      <text x="25" y="173" {...ls} style={{...ls, fontSize: 4.5}}>A1S</text>
      <rect x="167" y="155" width="15" height="28" {...wheelStyle("t_axa1_dr")} onClick={() => onZoneClick("t_axa1_dr")} />
      <text x="174" y="173" {...ls} style={{...ls, fontSize: 4.5}}>A1D</text>

      {/* Wheels - Axa 2 (tractiune, JUMELATE) */}
      <rect x="10" y={is6x2?370:345} width="13" height="28" {...wheelStyle("t_axa2_st_ext")} onClick={() => onZoneClick("t_axa2_st_ext")} />
      <text x="16" y={is6x2?388:363} {...ls} style={{...ls, fontSize: 3.5}}>EXT</text>
      <rect x="25" y={is6x2?370:345} width="13" height="28" {...wheelStyle("t_axa2_st_int")} onClick={() => onZoneClick("t_axa2_st_int")} />
      <text x="31" y={is6x2?388:363} {...ls} style={{...ls, fontSize: 3.5}}>INT</text>

      <rect x="162" y={is6x2?370:345} width="13" height="28" {...wheelStyle("t_axa2_dr_int")} onClick={() => onZoneClick("t_axa2_dr_int")} />
      <text x="168" y={is6x2?388:363} {...ls} style={{...ls, fontSize: 3.5}}>INT</text>
      <rect x="177" y={is6x2?370:345} width="13" height="28" {...wheelStyle("t_axa2_dr_ext")} onClick={() => onZoneClick("t_axa2_dr_ext")} />
      <text x="183" y={is6x2?388:363} {...ls} style={{...ls, fontSize: 3.5}}>EXT</text>

      {/* Axa 3 (6x2 only) */}
      {is6x2 && <>
        <rect x="18" y="405" width="15" height="28" {...wheelStyle("t_axa3_st")} onClick={() => onZoneClick("t_axa3_st")} />
        <text x="25" y="423" {...ls} style={{...ls, fontSize: 4.5}}>A3S</text>
        <rect x="167" y="405" width="15" height="28" {...wheelStyle("t_axa3_dr")} onClick={() => onZoneClick("t_axa3_dr")} />
        <text x="174" y="423" {...ls} style={{...ls, fontSize: 4.5}}>A3D</text>
      </>}
    </svg>
  );
}

/* ===== TRACTOR DETAIL SVG ===== */
function TractorDetailSVG({ anvelope, onZoneClick, activeZone, config }) {
  const is6x2 = config === '6x2';
  const ls = { fill: C.text2, fontSize: 7, textAnchor: "middle", pointerEvents: "none", fontFamily: FONT };
  const zoneStyle = (id) => {
    const isActive = activeZone === id;
    return { fill: isActive ? C.accent + "30" : "rgba(37,99,235,0.05)", stroke: isActive ? C.accent : C.border, strokeWidth: isActive ? 2 : 1, cursor: "pointer" };
  };
  const tireData = (idx) => anvelope && anvelope[idx] ? anvelope[idx] : null;
  const plColor = (pl) => { const v = Number(pl) || 0; return v > 50 ? C.green : v > 25 ? C.orange : C.red; };

  const bodyZones = [
    { id: "t_fata", x: 37, y: 22, w: 126, h: 45, label: "FATA", rx: 10 },
    { id: "t_acoperis", x: 55, y: 70, w: 90, h: 25, label: "ACOPERIS" },
    { id: "t_cabina_st", x: 37, y: 70, w: 16, h: 108, label: "CAB.ST", lx: 45, ly: 125 },
    { id: "t_cabina_dr", x: 147, y: 70, w: 16, h: 108, label: "CAB.DR", lx: 155, ly: 125 },
    { id: "t_interior", x: 55, y: 98, w: 90, h: 45, label: "INTERIOR" },
    { id: "t_motor", x: 55, y: 146, w: 90, h: 30, label: "MOTOR" },
    { id: "t_sa", x: 60, y: 185, w: 80, h: 35, label: "SA CUPLARE" },
    { id: "t_sasiu", x: 60, y: 225, w: 80, h: 30, label: "SASIU" },
    { id: "t_rezervor_st", x: 47, y: 260, w: 20, h: 40, label: "REZ.", lx: 57, ly: 283 },
    { id: "t_rezervor_dr", x: 133, y: 260, w: 20, h: 40, label: "REZ.", lx: 143, ly: 283 },
    { id: "t_baterii", x: 70, y: 260, w: 26, h: 25, label: "BAT.", lx: 83, ly: 276 },
    { id: "t_frane", x: 100, y: 260, w: 30, h: 25, label: "FRANE", lx: 115, ly: 276 },
    { id: "t_electric", x: 70, y: 290, w: 26, h: 25, label: "ELEC.", lx: 83, ly: 306 },
    { id: "t_documente", x: 100, y: 290, w: 30, h: 25, label: "DOC.", lx: 115, ly: 306 },
    { id: "t_dotari", x: 70, y: 320, w: 60, h: 22, label: "DOTARI" },
    { id: "t_spate", x: 47, y: is6x2?430:390, w: 106, h: 40, label: "SPATE" },
  ];

  const wheelDefs = [
    { id: "t_axa1_st", idx: 0, x: 4, y: 155, w: 28, h: 28, label: "A1 St" },
    { id: "t_axa1_dr", idx: 1, x: 167, y: 155, w: 28, h: 28, label: "A1 Dr" },
    { id: "t_axa2_st_ext", idx: 2, x: 2, y: is6x2?370:345, w: 16, h: 28, label: "Ext" },
    { id: "t_axa2_st_int", idx: 3, x: 20, y: is6x2?370:345, w: 16, h: 28, label: "Int" },
    { id: "t_axa2_dr_int", idx: 4, x: 162, y: is6x2?370:345, w: 16, h: 28, label: "Int" },
    { id: "t_axa2_dr_ext", idx: 5, x: 180, y: is6x2?370:345, w: 16, h: 28, label: "Ext" },
  ];
  if (is6x2) {
    wheelDefs.push({ id: "t_axa3_st", idx: 6, x: 4, y: 405, w: 28, h: 28, label: "A3 St" });
    wheelDefs.push({ id: "t_axa3_dr", idx: 7, x: 167, y: 405, w: 28, h: 28, label: "A3 Dr" });
  }

  const h = is6x2 ? 520 : 480;
  return (
    <svg viewBox={`0 0 200 ${h}`} style={{ width: "100%", maxWidth: 260, height: "auto" }}>
      <rect x="35" y="20" width="130" height="160" rx="12" fill="#f0f4f8" stroke={C.border} strokeWidth="1.5" />
      <rect x="45" y="180" width="110" height={is6x2?280:240} rx="4" fill="#f8fafc" stroke={C.border} strokeWidth="1" />
      {bodyZones.map(z => (
        <g key={z.id} onClick={() => onZoneClick(z.id)} style={{ cursor: "pointer" }}>
          <rect x={z.x} y={z.y} width={z.w} height={z.h} rx={z.rx||3} {...zoneStyle(z.id)} />
          <text x={z.lx||(z.x+z.w/2)} y={z.ly||(z.y+z.h/2+3)} style={{...ls, fontSize: z.w < 25 ? 5 : z.w < 40 ? 5.5 : 7, fill: activeZone===z.id ? C.accent : C.text2, fontWeight: activeZone===z.id ? 700 : 400}}>{z.label}</text>
        </g>
      ))}
      {wheelDefs.map(w => {
        const tire = tireData(w.idx);
        const isActive = activeZone === w.id;
        const pl = Number(tire?.placute) || 0;
        const color = plColor(pl);
        const profil = Number(tire?.profil) || 0;
        const profilColor = profil > 100 ? "#16a34a" : profil >= 50 ? "#ea580c" : profil > 0 ? "#dc2626" : "#8896a6";
        return (
          <g key={w.id} onClick={() => onZoneClick(w.id)} style={{ cursor: "pointer" }}>
            <rect x={w.x} y={w.y} width={w.w} height={w.h} rx="4"
              fill={isActive ? C.accent+"25" : "#e8edf4"} stroke={isActive ? C.accent : "#b0bec5"} strokeWidth={isActive ? 2.5 : 1.5} />
            {w.w >= 24 ? <>
              <text x={w.x+w.w/2} y={w.y+10} style={{...ls, fontSize: 6, fontWeight: 700, fill: profilColor}}>{profil > 0 ? profil+"mm" : ""}</text>
              <text x={w.x+w.w/2} y={w.y+19} style={{...ls, fontSize: 7, fontWeight: 700, fill: isActive ? C.accent : C.text}}>{pl > 0 ? pl+"%" : "\u2014"}</text>
              <rect x={w.x+2} y={w.y+22} width={w.w-4} height="3" rx="1.5" fill="#d1d5db" />
              <rect x={w.x+2} y={w.y+22} width={Math.max(0, Math.min(w.w-4, pl*(w.w-4)/100))} height="3" rx="1.5" fill={color} />
            </> : <>
              <text x={w.x+w.w/2} y={w.y+12} style={{...ls, fontSize: 6, fontWeight: 700, fill: isActive ? C.accent : C.text}}>{pl > 0 ? pl+"%" : ""}</text>
              <text x={w.x+w.w/2} y={w.y+22} style={{...ls, fontSize: 4, fill: C.text2}}>{w.label}</text>
            </>}
          </g>
        );
      })}
    </svg>
  );
}

const ZONES_LABELS = {
  fata:"FAȚĂ",spate:"SPATE",lateral_st:"LATERAL ST.",lateral_dr:"LATERAL DR.",acoperis:"ACOPERIȘ",
  interior:"INTERIOR",sasiu_zona:"ȘASIU",frane_zona:"FRÂNE",electric_zona:"ELECTRIC",
  documente:"DOCUMENTE",dotari_zona:"DOTĂRI",
  axa1_st:"ROATA A1 ST.",axa1_dr:"ROATA A1 DR.",axa2_st:"ROATA A2 ST.",axa2_dr:"ROATA A2 DR.",
  axa3_st:"ROATA A3 ST.",axa3_dr:"ROATA A3 DR.",
  // Tractor zones
  t_fata:"FAȚĂ",t_cabina_st:"CABINĂ ST.",t_cabina_dr:"CABINĂ DR.",t_acoperis:"ACOPERIȘ",
  t_interior:"INTERIOR",t_motor:"MOTOR",t_sasiu:"ȘASIU",t_sa:"ȘA CUPLARE",
  t_rezervor_st:"REZERVOR ST.",t_rezervor_dr:"REZERVOR DR.",t_baterii:"BATERII",
  t_frane:"FRÂNE",t_electric:"ELECTRIC",t_documente:"DOCUMENTE",t_dotari:"DOTĂRI",t_spate:"SPATE",
  t_axa1_st:"ROATA A1 ST.",t_axa1_dr:"ROATA A1 DR.",
  t_axa2_st_ext:"A2 ST-EXT",t_axa2_st_int:"A2 ST-INT",
  t_axa2_dr_int:"A2 DR-INT",t_axa2_dr_ext:"A2 DR-EXT",
  t_axa3_st:"ROATA A3 ST.",t_axa3_dr:"ROATA A3 DR.",
};

const SERVICE_STATUS = {
  pending: { label: "Așteptare", color: C.muted, icon: "⏳" },
  in_progress: { label: "În lucru", color: C.accent, icon: "🔧" },
  mechanic_done: { label: "Așteaptă aprobare", color: C.orange, icon: "⏳" },
  rejected: { label: "Respins", color: C.red, icon: "↩️" },
  completed: { label: "Aprobat", color: C.green, icon: "✅" },
};

function Dashboard({ stats, trailers, onSelect, serviceReviewCount, onGoToService, onSendToService, user, onStartPV, onNavToTrailers }) {
  const [returnedTrailers, setReturnedTrailers] = useState([]);
  const [activeServiceOrders, setActiveServiceOrders] = useState([]);
  const [needsInspection, setNeedsInspection] = useState([]);
  const [pendingHandover, setPendingHandover] = useState([]);
  const [pendingReturn, setPendingReturn] = useState([]);
  const [approveModal, setApproveModal] = useState(null);
  const [approveStare, setApproveStare] = useState("Activă");
  const [savingApprove, setSavingApprove] = useState(false);
  const isMechanic = user?.role === "mecanic";
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const [returned, serviceData, inspectionData, handoverData, returnData] = await Promise.all([
          isMechanic ? Promise.resolve([]) : api.get("/api/trailers/returned/recent"),
          api.get("/api/service?status=all"),
          !isMechanic ? api.get("/api/trailers/needs-inspection") : Promise.resolve([]),
          !isMechanic ? api.get("/api/trailers/pending-handover") : Promise.resolve([]),
          !isMechanic ? api.get("/api/trailers/pending-return") : Promise.resolve([]),
        ]);
        if (returned) setReturnedTrailers(returned);
        if (serviceData) setActiveServiceOrders(serviceData.filter(o => !["completed"].includes(o.status) && o.type !== "interventie"));
        if (inspectionData) setNeedsInspection(inspectionData);
        if (handoverData) setPendingHandover(handoverData);
        if (returnData) setPendingReturn(returnData);
      } catch { /* ok */ }
    })();
  }, [isMechanic]);

  const handleApprove = async () => {
    if (!approveModal) return;
    setSavingApprove(true);
    try {
      await api.put(`/api/trailers/${approveModal.trailer_id}`, { stare: approveStare });
      setApproveModal(null);
      setReturnedTrailers(prev => prev.filter(r => r.trailer_id !== approveModal.trailer_id));
    } catch (ex) { alert("Eroare: " + ex.message); }
    setSavingApprove(false);
  };

  const withDiffs = returnedTrailers.filter(r => r.diff_count > 0);
  const withoutDiffs = returnedTrailers.filter(r => r.diff_count === 0);

  /* ─── MECHANIC DASHBOARD ─── */
  if (isMechanic) {
    return (
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20, color: C.text }}>Remorcile de reparat</h2>
        {activeServiceOrders.length === 0 && (
          <div style={{ ...S.statCard, textAlign: "center", padding: 40, color: C.text2 }}>Nicio comandă de service activă.</div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[...activeServiceOrders].sort((a,b) => { const ord = {in_progress:0, rejected:1, pending:2, mechanic_done:3}; return (ord[a.status]??9) - (ord[b.status]??9); }).map(o => {
            const items = typeof o.items === "string" ? JSON.parse(o.items) : (o.items || []);
            const done = items.filter(i => i.status === "done" || i.done).length;
            const st = SERVICE_STATUS[o.status] || SERVICE_STATUS.pending;
            return (
              <div key={o.id} style={{
                ...S.statCard, padding: "14px 16px", cursor: "pointer",
                borderLeft: `4px solid ${st.color}`,
              }} onClick={() => onGoToService && onGoToService(o.id)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{o.numar_auto}</div>
                    {o.priority === "urgent" && <span style={{ ...S.badge(C.red), fontSize: 10 }}>🚨 URGENT</span>}
                  </div>
                  <span style={{ ...S.badge(st.color), fontSize: 11 }}>{st.icon} {st.label}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, height: 10, background: C.border, borderRadius: 5, overflow: "hidden" }}>
                    <div style={{ width: items.length ? `${(done / items.length) * 100}%` : "0%", height: "100%", background: done === items.length && items.length > 0 ? C.green : C.accent, borderRadius: 5, transition: "width 0.3s" }} />
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: done === items.length && items.length > 0 ? C.green : C.text2, whiteSpace: "nowrap" }}>{done}/{items.length} lucrări</span>
                  <span style={{ fontSize: 22, color: C.accent }}>›</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /* ─── DISPATCHER / ADMIN DASHBOARD ─── */
  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20, color: C.text }}>Dashboard</h2>

      {/* Stats cards */}
      {(() => {
        const dispTotal = isAdmin ? (stats?.total || 0) : ((stats?.active || 0) + (stats?.returnate || 0) + (stats?.service || 0));
        const allCards = [
          { label: "Total Remorci", val: dispTotal, color: C.accent2, icon: "🚛", filter: null },
          { label: "Active", val: stats?.active || 0, color: C.green, icon: "✅", filter: "Activă" },
          { label: "Închiriate", val: stats?.inchiriate || 0, color: C.accent, icon: "📋", filter: "Închiriată" },
          ...( isAdmin ? [{ label: "Vânzare în rate", val: stats?.vanzare_rate || 0, color: "#7c3aed", icon: "💰", filter: "Vânzare în rate" }] : []),
          { label: "Returnate", val: stats?.returnate || 0, color: "#8b5cf6", icon: "📦", filter: "Returnată" },
          { label: "În Service", val: stats?.service || 0, color: C.orange, icon: "🔧", filter: "În service" },
          { label: "În așteptare", val: stats?.pending || 0, color: "#f59e0b", icon: "⏳", filter: "În așteptare" },
        ];
        return (
          <div className="lm-stat-cards" style={{ display: "grid", gridTemplateColumns: `repeat(${allCards.length}, 1fr)`, gap: 16, marginBottom: 28 }}>
            {allCards.map((s, i) => (
              <div key={i} style={{ ...S.statCard, cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s" }}
                onClick={() => onNavToTrailers && onNavToTrailers(s.filter)}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ ...S.statVal, color: s.color }}>{s.val}</div>
                    <div style={S.statLabel}>{s.label}</div>
                  </div>
                  <div style={{ fontSize: 26, opacity: 0.7 }}>{s.icon}</div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ═══ REMORCI CARE NECESITĂ INSPECȚIE ═══ */}
      {needsInspection.length > 0 && (
        <div style={{ ...S.statCard, marginBottom: 24, borderLeft: `4px solid #dc2626`, background: "#fef2f2" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <h3 style={{ color: "#dc2626", fontSize: 16, margin: 0 }}>⚠️ Remorci care necesită inspecție</h3>
            <span style={{ background: "#dc2626", color: "#fff", fontSize: 11, fontWeight: 700, width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{needsInspection.length}</span>
          </div>
          {needsInspection.map(t => (
            <div key={t.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 4,
              borderRadius: 8, border: "1px solid #fecaca", background: "#fff", cursor: "pointer", flexWrap: "wrap",
            }} onClick={() => onSelect(t)}>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 15, minWidth: 90 }}>{t.numar_auto}</div>
              <div style={{ flex: 1, fontSize: 13, color: C.text2 }}>{t.tip} — {t.marca}</div>
              <div style={{ fontSize: 11, color: C.text2 }}>{t.created_at ? fmtDT(t.created_at) : ""}</div>
              <button className="lm-btn" style={{ ...S.btn, background: "#dc2626", color: "#fff", padding: "8px 14px", fontSize: 13, whiteSpace: "nowrap" }}
                onClick={e => { e.stopPropagation(); onStartPV && onStartPV(t, "inspectie"); }}>
                🔍 Creează PV Inspecție
              </button>
            </div>
          ))}
        </div>
      )}

      {pendingHandover.length > 0 && !isMechanic && (
        <div style={{ ...S.statCard, marginBottom: 24, borderLeft: "4px solid #2563eb", background: "#eff6ff" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <h3 style={{ color: "#1e3a5f", fontSize: 16, margin: 0 }}>🚛 Vehicule de predat</h3>
            <span style={{ background: "#2563eb", color: "#fff", fontSize: 11, fontWeight: 700, width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{pendingHandover.length}</span>
          </div>
          {pendingHandover.map(t => (
            <div key={t.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 4,
              borderRadius: 8, border: "1px solid #bfdbfe", background: "#fff", cursor: "pointer", flexWrap: "wrap",
            }} onClick={() => onSelect(t)}>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 15, minWidth: 90 }}>{t.numar_auto}</div>
              <div style={{ flex: 1, fontSize: 13, color: C.text2 }}>
                Client: <b>{t.client_name || "Nealocat"}</b>
                {t.cargo_contract_number && <span style={{ marginLeft: 8, background: "#dbeafe", color: "#1e40af", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>Contract {t.cargo_contract_number}</span>}
                {t.vehicle_type === "tractor" && <span style={{ marginLeft: 6, background: "#f3e8ff", color: "#7c3aed", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>Cap tractor</span>}
                {t.estimated_handover_date && <span style={{ marginLeft: 8 }}>📅 {new Date(t.estimated_handover_date).toLocaleDateString("ro-RO")}</span>}
              </div>
              <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, padding: "8px 14px", fontSize: 13, whiteSpace: "nowrap" }}
                onClick={e => { e.stopPropagation(); onStartPV && onStartPV(t, "predare"); }}>
                📋 {t.vehicle_type === "tractor" ? "Crează PV Predare" : "Începe Predarea"}
              </button>
            </div>
          ))}
        </div>
      )}


      {pendingReturn.length > 0 && !isMechanic && (
        <div style={{ ...S.statCard, marginBottom: 24, borderLeft: "4px solid #ea580c", background: "#fff7ed" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <h3 style={{ color: "#9a3412", fontSize: 16, margin: 0 }}>🚛 Remorci de returnat</h3>
            <span style={{ background: "#ea580c", color: "#fff", fontSize: 11, fontWeight: 700, width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{pendingReturn.length}</span>
          </div>
          {pendingReturn.map(t => (
            <div key={t.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 4,
              borderRadius: 8, border: "1px solid #fed7aa", background: "#fff", cursor: "pointer", flexWrap: "wrap",
            }} onClick={() => onSelect(t)}>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 15, minWidth: 90 }}>{t.numar_auto}</div>
              <div style={{ flex: 1, fontSize: 13, color: C.text2 }}>
                Transportator: <b>{t.client_name || "—"}</b>
              </div>
              <button className="lm-btn" style={{ ...S.btn, background: "#ea580c", color: "#fff", padding: "8px 14px", fontSize: 13, whiteSpace: "nowrap" }}
                onClick={e => { e.stopPropagation(); onStartPV && onStartPV(t, "returnare"); }}>
                📋 Începe Returnarea
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ REMORCI RETURNATE ═══ */}
      {returnedTrailers.length > 0 && (
        <div style={{ ...S.statCard, marginBottom: 24 }}>
          <h3 style={{ color: C.text, marginBottom: 12, fontSize: 16 }}>Remorci Returnate ({returnedTrailers.length})</h3>
          {withDiffs.map(r => (
            <div key={r.pv_id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 4,
              borderRadius: 8, border: `1px solid ${C.border}`, cursor: "pointer", flexWrap: "wrap",
              borderLeft: `4px solid ${r.critical_count > 0 ? C.red : r.major_count > 0 ? C.orange : C.green}`,
            }} onClick={() => onSelect({ id: r.trailer_id })}>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 15, minWidth: 90 }}>{r.numar_auto}</div>
              <div style={{ display: "flex", gap: 4, flex: 1, flexWrap: "wrap", minWidth: 120 }}>
                {r.critical_count > 0 && <span style={{ ...S.badge(C.red), fontSize: 10 }}>{r.critical_count} critical</span>}
                {r.major_count > 0 && <span style={{ ...S.badge(C.orange), fontSize: 10 }}>{r.major_count} major</span>}
                {r.minor_count > 0 && <span style={{ ...S.badge(C.green), fontSize: 10 }}>{r.minor_count} minor</span>}
              </div>
              <button className="lm-btn" style={{ ...S.btn, background: C.orange, color: C.white, padding: "8px 14px", fontSize: 13, whiteSpace: "nowrap" }}
                onClick={e => { e.stopPropagation(); onSendToService && onSendToService(r.trailer_id, r.pv_id); }}>
                🔧 Service
              </button>
              {isAdmin && <button className="lm-btn" style={{ ...S.btn, background: C.green, color: C.white, padding: "8px 14px", fontSize: 13, whiteSpace: "nowrap" }}
                onClick={async e => { e.stopPropagation(); if (!window.confirm("Sigur? Diferențele vor fi marcate ca rezolvate.")) return; try { await api.post("/api/pvs/" + r.pv_id + "/dismiss-differences"); setReturnedTrailers(prev => prev.filter(x => x.pv_id !== r.pv_id)); } catch(ex) { alert("Eroare: " + ex.message); } }}>
                ✅ Fără service
              </button>}
            </div>
          ))}
          {withoutDiffs.map(r => (
            <div key={r.pv_id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 4,
              borderRadius: 8, border: `1px solid ${C.border}`, cursor: "pointer", flexWrap: "wrap",
              borderLeft: `4px solid ${C.green}`,
            }} onClick={() => onSelect({ id: r.trailer_id })}>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 15, minWidth: 90 }}>{r.numar_auto}</div>
              <span style={{ fontSize: 13, color: C.green, fontWeight: 600, flex: 1 }}>✅ Fără diferențe</span>
              <button className="lm-btn" style={{ ...S.btn, background: C.green, color: C.white, padding: "8px 14px", fontSize: 13, whiteSpace: "nowrap" }}
                onClick={e => { e.stopPropagation(); setApproveModal({ trailer_id: r.trailer_id, numar_auto: r.numar_auto }); setApproveStare("Activă"); }}>
                Aprobă
              </button>
            
              {isAdmin && <button className="lm-btn" style={{ ...S.btn, background: C.green + "20", color: C.green, padding: "8px 14px", fontSize: 13, whiteSpace: "nowrap", border: "1px solid " + C.green }}
                onClick={async e => { e.stopPropagation(); try { await api.post("/api/pvs/" + r.pv_id + "/dismiss-differences"); setReturnedTrailers(prev => prev.filter(x => x.pv_id !== r.pv_id)); } catch(ex) { alert("Eroare: " + ex.message); } }}>
                ✅ Marchează OK
              </button>}
            </div>
          ))}
        </div>
      )}

      {/* ═══ SERVICE ACTIV ═══ */}
      {activeServiceOrders.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 12 }}>Service Activ</h3>
          <div className="lm-service-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {activeServiceOrders.map(o => {
              const items = typeof o.items === "string" ? JSON.parse(o.items) : (o.items || []);
              const done = items.filter(i => i.status === "done" || i.done).length;
              const st = SERVICE_STATUS[o.status] || SERVICE_STATUS.pending;
              const needsReview = o.status === "mechanic_done";
              return (
                <div key={o.id} style={{
                  ...S.statCard, padding: "14px 16px", cursor: "pointer",
                  borderLeft: `4px solid ${needsReview ? C.orange : st.color}`,
                  background: needsReview ? "#fffbeb" : C.card,
                }}
                  onClick={() => onGoToService && onGoToService(o.id)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 4 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{o.numar_auto}</div>
                    <span style={{ ...S.badge(needsReview ? C.orange : st.color), fontSize: 11 }}>{st.icon} {st.label}</span>
                  </div>
                  <div style={{ fontSize: 12, color: C.text2, marginBottom: 6 }}>Mecanic: {o.mechanic_name || "—"}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: items.length ? `${(done / items.length) * 100}%` : "0%", height: "100%", background: done === items.length && items.length > 0 ? C.green : C.accent, borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: done === items.length && items.length > 0 ? C.green : C.text2 }}>{done}/{items.length}</span>
                  </div>
                  {needsReview && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.orange, marginTop: 4 }}>Verificare necesară</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}



      {/* Recent trailers — table on desktop, cards on mobile (admin only) */}
      

      {/* Approve modal */}
      {approveModal && (
        <div style={S.overlay} onClick={() => setApproveModal(null)}>
          <div style={{ ...S.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: C.text, marginBottom: 16 }}>Aprobă {approveModal.numar_auto}</h3>
            <p style={{ fontSize: 13, color: C.text2, marginBottom: 16 }}>Remorca nu are diferențe. Alege starea:</p>
            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              {["Activă", "De vânzare"].map(s => (
                <button key={s} className="lm-btn" style={{
                  ...S.btn, flex: 1, padding: "10px 0",
                  background: approveStare === s ? C.accent : "transparent",
                  color: approveStare === s ? C.white : C.text,
                  border: `2px solid ${approveStare === s ? C.accent : C.border}`,
                }} onClick={() => setApproveStare(s)}>
                  {s}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost }} onClick={() => setApproveModal(null)}>Anulează</button>
              <button className="lm-btn" style={{ ...S.btn, background: C.green, color: C.white, opacity: savingApprove ? 0.6 : 1 }} disabled={savingApprove} onClick={handleApprove}>
                {savingApprove ? "..." : "Confirmă"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TRAILER LIST
   ═══════════════════════════════════════════════════════════ */
function TrailerList({ trailers, onSelect, onAdd, onDelete, loading, userRole, onBack, onStartPV, vehicleType }) {
  const isTractor = vehicleType === "tractor";
  const [search, setSearch] = useState("");
  const [filterStare, setFilterStare] = useState(() => {
    const preset = window.__lmFilterStare;
    if (preset) { window.__lmFilterStare = null; return preset; }
    return "Toate";
  });
  const [cameFromDash] = useState(() => {
    const v = window.__lmFromDashboard;
    window.__lmFromDashboard = false;
    return !!v;
  });

  const filtered = (trailers || []).filter(t => {
    if (filterStare !== "Toate" && t.stare !== filterStare) return false;
    if (search) {
      const s = search.toLowerCase();
      return (t.numar_auto || "").toLowerCase().includes(s) || (t.marca || "").toLowerCase().includes(s) || (t.serie_sasiu || "").toLowerCase().includes(s) || (t.client_name || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div>
      <div className="lm-page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>{isTractor ? "Capete tractor" : "Remorci"} ({filtered.length})</h2>
          {cameFromDash && filterStare !== "Toate" && <div style={{ fontSize: 12, color: C.text2, marginTop: 4 }}>Filtru: {filterStare}</div>}
        </div>
        {userRole === "admin" && <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary }} onClick={onAdd}>{isTractor ? "+ Adaugă Cap tractor" : "+ Adaugă Remorcă"}</button>}
      </div>

      {/* Filters */}
      <div className="lm-filters" style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <input style={{ ...S.input, marginBottom: 0, maxWidth: 300, flex: 1, minWidth: 180 }} placeholder="Caută nr. auto, marcă, serie..." value={search} onChange={e => setSearch(e.target.value)} />
        <div className="lm-filter-btns" style={{ display: "flex", gap: 4 }}>
          {(userRole === "dispecer" ? ["Toate", "Activă", "Închiriată", "Returnată", "În service", "În așteptare"] : ["Toate", ...STARI]).map(s => (
            <button key={s} className="lm-btn" style={{
              padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: "pointer", transition: "all 0.15s",
              background: filterStare === s ? C.accent : "#f0f2f5",
              color: filterStare === s ? "#ffffff" : "#4a5568",
              border: `1px solid ${filterStare === s ? C.accent : "#dce3ec"}`,
              whiteSpace: "nowrap",
            }} onClick={() => setFilterStare(s)}>{s}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: C.text2 }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>🚛</div>Se încarcă...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: C.text2 }}>
          <div className="lm-empty-icon" style={{ fontSize: 48, marginBottom: 8, opacity: 0.3 }}>🔍</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{isTractor ? "Niciun cap tractor găsit" : "Nicio remorcă găsită"}</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Încearcă alte criterii de căutare</div>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <table className="lm-desktop-table" style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Nr. Auto</th>
                <th style={S.th}>Serie Șasiu</th>
                <th style={S.th}>Tip</th>
                <th style={S.th}>Marcă / Model</th>
                <th style={S.th}>An</th>
                <th style={S.th}>Stare</th>
                <th style={S.th}>Client</th>
                <th style={S.th}>ITP</th>
                <th style={S.th}>RCA</th>
                <th style={S.th}>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const itpD = daysTo(t.itp_expirare);
                const rcaD = daysTo(t.rca_expirare);
                return (
                  <tr key={t.id}>
                    <td style={{ ...S.td, borderRadius: "8px 0 0 8px" }}><a href={"#" + (isTractor ? "tractor" : "trailer") + "/" + t.id} onClick={(e) => { e.preventDefault(); onSelect(t); }} style={{ color: C.text, fontWeight: 600, textDecoration: "none", display: "block" }}>{t.numar_auto}</a></td>
                    <td style={{ ...S.td, fontSize: 12, fontFamily: "monospace" }}>{t.serie_sasiu}</td>
                    <td style={S.td}>{t.tip}</td>
                    <td style={S.td}>{t.marca} {t.model}</td>
                    <td style={S.td}>{t.an_fabricatie}</td>
                    <td style={S.td}><span style={S.badge(stareColor(t.stare))}>{t.stare}</span>{t.has_rate_contract && <span style={{ ...S.badge("#dc2626"), marginLeft: 4, fontSize: 10 }}>⚠ Rate</span>}</td>
                    <td style={S.td}>{t.client_name || "Nealocat"}</td>
                    <td style={S.td}><span style={S.badge(itpD < 0 ? C.red : itpD < 30 ? C.orange : C.green)}>{t.itp_expirare ? `${itpD}z` : "—"}</span></td>
                    <td style={S.td}><span style={S.badge(rcaD < 0 ? C.red : rcaD < 30 ? C.orange : C.green)}>{t.rca_expirare ? `${rcaD}z` : "—"}</span></td>
                    <td style={{ ...S.td, borderRadius: "0 8px 8px 0" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button style={{ ...S.btn, ...S.btnGhost, padding: "4px 10px", fontSize: 12 }} onClick={() => onSelect(t)}>Detalii</button>
                        {!isTractor && t.published_to_remorci && (
                          <span style={{ ...S.badge("#7c3aed"), fontSize: 10 }}>Pe site</span>
                        )}
                        {(t.has_rate_contract || t.has_rent_contract) && t.stare !== "Vânzare în rate" && t.stare !== "Închiriată" && <button style={{ ...S.btn, background: "#dc2626", color: "#fff", padding: "4px 10px", fontSize: 12 }} onClick={(e) => { e.stopPropagation(); onStartPV && onStartPV(t, "predare"); }}>📋 Predare</button>}
                        {userRole === "admin" && <button style={{ ...S.btn, background: C.red + "22", color: C.red, padding: "4px 10px", fontSize: 12, border: `1px solid ${C.red}44` }} onClick={() => { if (window.confirm(`Ești sigur că vrei să ștergi remorca ${t.numar_auto}? Această acțiune nu poate fi anulată.`)) onDelete(t.id); }}>Șterge</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Mobile cards */}
          <div className="lm-mobile-cards" style={{ display: "none", flexDirection: "column", gap: 8 }}>
            {filtered.map(t => {
              const itpD = daysTo(t.itp_expirare);
              const rcaD = daysTo(t.rca_expirare);
              return (
                <div key={t.id} style={{ ...S.statCard, padding: 14, cursor: "pointer" }} onClick={() => onSelect(t)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <a href={"#" + (isTractor ? "tractor" : "trailer") + "/" + t.id} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSelect(t); }} style={{ fontWeight: 800, fontSize: 17, color: C.text, textDecoration: "none" }}>{t.numar_auto}</a>
                    <span style={S.badge(stareColor(t.stare))}>{t.stare}</span>
                    {t.has_rate_contract && <span style={{ ...S.badge("#dc2626"), marginLeft: 4, fontSize: 10 }}>⚠ Rate</span>}
                    {t.has_rent_contract && <span style={{ ...S.badge("#ea580c"), marginLeft: 4, fontSize: 10 }}>⚠ Chirie</span>}
                  </div>
                  <div style={{ fontSize: 13, color: C.text2, marginBottom: 4 }}>{t.marca} {t.model} · {t.tip} · {t.an_fabricatie}</div>
                  <div style={{ fontSize: 13, color: C.text2 }}>{t.client_name || "Nealocat"}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <span style={S.badge(itpD < 0 ? C.red : itpD < 30 ? C.orange : C.green)}>ITP: {t.itp_expirare ? `${itpD}z` : "—"}</span>
                    <span style={S.badge(rcaD < 0 ? C.red : rcaD < 30 ? C.orange : C.green)}>RCA: {t.rca_expirare ? `${rcaD}z` : "—"}</span>
                    {t.km_actuali > 0 && <span style={{ fontSize: 11, color: C.text2, alignSelf: "center" }}>{t.km_actuali?.toLocaleString()} km</span>}
                  </div>
                  {!isTractor && t.published_to_remorci && (
                    <div style={{ marginTop: 6 }}><span style={{ ...S.badge("#7c3aed"), fontSize: 10 }}>Publicată pe remorci.lamaritec.ro</span></div>
                  )}
                  {(t.has_rate_contract || t.has_rent_contract) && t.stare !== "Vânzare în rate" && t.stare !== "Închiriată" && <button className="lm-btn" style={{ ...S.btn, background: "#dc2626", color: "#fff", padding: "6px 12px", fontSize: 12, marginTop: 8, width: "100%" }} onClick={(e) => { e.stopPropagation(); onStartPV && onStartPV(t, "predare"); }}>📋 Începe Predarea (Contract)</button>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PUBLISH TO REMORCI.LAMARITEC.RO MODAL
   ═══════════════════════════════════════════════════════════ */
const SEMI_EQUIPMENT = [
  "Prelată", "Scânduri lemn", "Scânduri aluminiu", "Bride fixare", "Benzi fixare",
  "Cric", "Roată de rezervă", "GPS tracker", "Led-uri laterale",
  "Bare antiîmpănare laterale", "Aripi noroi", "Sistem ABS", "Sistem EBS",
  "Frână de parcare pneumatică", "Uși spate", "Bară spate reglabilă"
];

/* ═══════════════════════════════════════════════════════════
   TRAILER FORM MODAL
   ═══════════════════════════════════════════════════════════ */
function TrailerForm({ trailer, onSave, onClose, clients, vehicleType }) {
  const isTractor = vehicleType === "tractor";
  const [form, setForm] = useState(trailer || (isTractor ? emptyTractor() : emptyTrailer()));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const isEdit = !!trailer?.id;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAnv = (i, k, v) => {
    const a = [...form.anvelope];
    a[i] = { ...a[i], [k]: v };
    setForm(f => ({ ...f, anvelope: a }));
  };

  const handleSave = async () => {
    if (!form.numar_auto || !form.serie_sasiu || !form.tip || !form.marca || (form.marca === "Altă marcă" && (!form.marca_custom || !form.marca_custom.trim()))) {
      setErr("Nr. auto, serie șasiu, tip și marcă sunt obligatorii. Pentru altă marcă, scrie numele.");
      return;
    }
    setSaving(true);
    setErr("");
    setSuccess(false);
    try {
      const payload = { ...form };
      if (isTractor) payload.vehicle_type = "tractor";
      if (payload.marca === "Altă marcă" && payload.marca_custom) { payload.marca = payload.marca_custom.trim(); }
      delete payload.marca_custom;
      if (isEdit) {
        await api.put(`/api/trailers/${trailer.id}`, payload);
      } else {
        await api.post("/api/trailers", payload);
      }
      setSuccess(true);
      setTimeout(() => onSave(), 1200);
    } catch (ex) {
      setErr(ex.message);
    }
    setSaving(false);
  };

  const field = (label, key, type = "text", opts) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>{label}</label>
      {opts ? (
        <select style={S.input} value={form[key] || ""} onChange={e => set(key, e.target.value)}>
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input style={S.input} type={type} value={form[key] || ""} onChange={e => set(key, type === "number" ? Number(e.target.value) : e.target.value)} />
      )}
    </div>
  );

  return (
    <div style={S.overlay} onClick={onClose}>
      <div className="lm-modal" style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, gap: 12, flexWrap: "wrap" }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>{isEdit ? (isTractor ? "Editează Cap tractor" : "Editează Remorcă") : (isTractor ? "Adaugă Cap tractor" : "Adaugă Remorcă")}</h2>
          <button style={{ ...S.btn, background: "transparent", color: C.text2, fontSize: 20 }} onClick={onClose}>✕</button>
        </div>

        {err && <div style={{ color: C.red, marginBottom: 16, padding: "8px 12px", background: C.red + "15", borderRadius: 8, fontSize: 14 }}>{err}</div>}
        {success && <div style={{ color: C.green, marginBottom: 16, padding: "12px 16px", background: C.green + "15", borderRadius: 8, fontSize: 15, fontWeight: 600, textAlign: "center" }}>✓ {isEdit ? (isTractor ? "Cap tractor actualizat!" : "Remorcă actualizată cu succes!") : (isTractor ? "Cap tractor adăugat!" : "Remorcă adăugată cu succes!")}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          {field("Nr. Înmatriculare *", "numar_auto")}
          {field("Serie Șasiu *", "serie_sasiu")}
          {field("Tip *", "tip", "text", isTractor ? TRACTOR_TIPURI : TIPURI)}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Marcă *</label>
            <select style={S.input} value={(form.marca === "Altă marcă" || (form.marca && !(isTractor ? TRACTOR_MARCI : MARCI).slice(0,-1).includes(form.marca))) ? "Altă marcă" : (form.marca || "")} onChange={e => { const v = e.target.value; set("marca", v); if (v !== "Altă marcă") set("marca_custom", ""); }}>
              <option value="">— Selectează —</option>
              {(isTractor ? TRACTOR_MARCI : MARCI).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            {(form.marca === "Altă marcă" || (form.marca && !(isTractor ? TRACTOR_MARCI : MARCI).slice(0,-1).includes(form.marca) && form.marca !== "")) && (
              <input style={{ ...S.input, marginTop: 6 }} value={form.marca_custom || ""} onChange={e => set("marca_custom", e.target.value)} placeholder="Scrie marca..." />
            )}
          </div>
          {isEdit && field("Model", "model")}
          {isEdit && field("An Fabricație", "an_fabricatie", "number")}
          {isEdit && field("Data Achiziție", "data_achizitie", "date")}
          {isEdit && field("Preț Achiziție (€)", "pret_achizitie", "number")}
          {field("Stare", "stare", "text", STARI.filter(s => s !== "În service"))}
          {isEdit && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Client Alocat</label>
              <select style={S.input} value={form.client_allocated_id || ""} onChange={e => set("client_allocated_id", e.target.value ? parseInt(e.target.value) : null)}>
                <option value="">— Niciun client —</option>
                {(clients || []).filter(c => c.active).map(c => (
                  <option key={c.id} value={c.id}>{c.company_name}{c.cui ? ` (${c.cui})` : ""}</option>
                ))}
              </select>
            </div>
          )}
          {isEdit && field("Nr. Axe", "nr_axe", "number")}
          {isEdit && field("Lungime (m)", "lungime", "number")}
          {isEdit && field("Lățime (m)", "latime", "number")}
          {isEdit && field("Înălțime (m)", "inaltime", "number")}
          {isEdit && field("Greutate (kg)", "greutate_proprie", "number")}
          {isEdit && field("Sarcina Max (kg)", "sarcina_maxima", "number")}
          {isEdit && field("Culoare", "culoare")}
          {isEdit && field("ITP Expirare", "itp_expirare", "date")}
          {isEdit && field("RCA Expirare", "rca_expirare", "date")}
          {isTractor && isEdit && field("Tahograf Expirare", "tahograf_expirare", "date")}
          {isTractor && isEdit && field("Copie Conformă Expirare", "copie_conforma_expirare", "date")}
          {isTractor && isEdit && field("Putere (CP)", "putere_cp", "number")}
          {isTractor && isEdit && field("Norma poluare", "norma_poluare", "text", ["Euro 3","Euro 4","Euro 5","Euro 6","Euro 6d"])}
          {isTractor && isEdit && field("Config. axe", "configuratie_axe", "text", ["4x2","6x2"])}
          {isTractor && isEdit && field("Sarcină pe șa (kg)", "sarcina_sa", "number")}
          {isTractor && isEdit && field("KM la achiziție", "km_achizitie", "number")}
        </div>

        {isEdit && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Dotări</label>
              <textarea style={{ ...S.input, minHeight: 60, resize: "vertical" }} value={form.dotari || ""} onChange={e => set("dotari", e.target.value)} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Observații</label>
              <textarea style={{ ...S.input, minHeight: 60, resize: "vertical" }} value={form.observatii || ""} onChange={e => set("observatii", e.target.value)} />
            </div>
          </>
        )}

        {/* Anvelope - only show on edit */}
        {isEdit && (
          <>
            <h3 style={{ color: C.accent2, fontSize: 16, marginBottom: 12 }}>{isTractor ? "Anvelope (configurație " + (form.configuratie_axe || "4x2") + ")" : "Anvelope"}</h3>
            <table style={{ ...S.table, marginBottom: 24 }}>
              <thead>
                <tr>
                  <th style={S.th}>Poziție</th>
                  <th style={S.th}>Profil (mm)</th>
                  <th style={S.th}>Marcă</th>
                  <th style={S.th}>Serie</th>
                  <th style={S.th}>Plăcuțe %</th>
                </tr>
              </thead>
              <tbody>
                {(form.anvelope || []).map((a, i) => (
                  <tr key={i}>
                    <td style={S.td}>{a.pozitie || a.poz}</td>
                    <td style={S.td}><input style={{ ...S.input, marginBottom: 0 }} value={a.profil || ""} onChange={e => setAnv(i, "profil", e.target.value)} /></td>
                    <td style={S.td}><input style={{ ...S.input, marginBottom: 0 }} value={a.marca || ""} onChange={e => setAnv(i, "marca", e.target.value)} /></td>
                    <td style={S.td}><input style={{ ...S.input, marginBottom: 0 }} value={a.serie || ""} onChange={e => setAnv(i, "serie", e.target.value)} /></td>
                    <td style={S.td}><input style={{ ...S.input, marginBottom: 0 }} inputMode="numeric" value={a.placute || ""} onChange={e => setAnv(i, "placute", e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button style={{ ...S.btn, ...S.btnGhost }} onClick={onClose}>Anulează</button>
          <button style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleSave}>
            {saving ? "Se salvează..." : (isEdit ? "Actualizează" : "Adaugă")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TRAILER DETAIL PAGE
   ═══════════════════════════════════════════════════════════ */
function TrailerDetail({ trailerId, onBack, onEdit, onStartPV, onSendToService, onShowDifferences, userRole, vehicleType }) {
  const isTractor = vehicleType === "tractor";
  const [trailer, setTrailer] = useState(null);
  const [tab, setTab] = useState("info");
  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [expandedPvs, setExpandedPvs] = useState({});
  const [latestPvData, setLatestPvData] = useState(null);
  const [allPvData, setAllPvData] = useState([]); // all PVs with full data for zone history
  const [showZoneHistory, setShowZoneHistory] = useState(false);

  const loadTrailer = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/trailers/${trailerId}`);
      setTrailer(data);
      // Load ALL PV full data for zone history display
      let allPvs = [];
      if (data.pvs && data.pvs.length > 0) {
        allPvs = (await Promise.all(data.pvs.map(async (pv) => {
          try {
            return await api.get(`/api/pvs/${pv.id}`);
          } catch {
            return null;
          }
        }))).filter(Boolean);
        setAllPvData(allPvs);
        if (allPvs.length > 0) setLatestPvData(allPvs[0]);
      } else {
        setAllPvData([]);
        setLatestPvData(null);
      }
    } catch (ex) {
      console.error(ex);
    }
    setLoading(false);
  }, [trailerId]);

  useEffect(() => { loadTrailer(); }, [loadTrailer]);

  const [assignModal, setAssignModal] = useState(false);
  const [assignClient, setAssignClient] = useState("");
  const [assignDate, setAssignDate] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignClientsList, setAssignClientsList] = useState([]);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignSelectedClient, setAssignSelectedClient] = useState(null);

  // Load clients list when assign modal opens
  useEffect(() => {
    if (assignModal) {
      api.get("/api/clients?active=true").then(setAssignClientsList).catch(() => {});
      setAssignSearch("");
      setAssignSelectedClient(null);
    }
  }, [assignModal]);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: C.text2 }}>Se încarcă...</div>;
  if (!trailer) return <div style={{ textAlign: "center", padding: 60, color: C.text2 }}>{isTractor ? "Cap tractor negăsit." : "Remorcă negăsită."}</div>;

  // Predarea e considerata FACUTA doar daca ultimul PV de predare/returnare este o predare
  // catre clientul curent al remorcii (clientul dosarului sincronizat din Cargo).
  // O predare veche - inchisa de o returnare sau emisa catre un client anterior - nu conteaza
  // pentru dosarul curent, deci remorca are nevoie de un PV de predare nou.
  const sameClient = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
  const lastHandoverPv = (trailer.pvs || [])
    .filter(p => p.type === "predare" || p.type === "returnare")
    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  const handoverDone = !!lastHandoverPv && lastHandoverPv.type === "predare" && sameClient(lastHandoverPv.client_name, trailer.client_name);

  const handleAssignClient = async () => {
    if (!assignSelectedClient) return alert("Selectează un client din listă.");
    setAssignSaving(true);
    try {
      await api.post("/api/trailers/" + trailerId + "/assign-client", {
        client_name: assignSelectedClient.company_name,
        client_email: assignSelectedClient.email || null,
        estimated_date: assignDate || null,
      });
      setAssignModal(false);
      const t = await api.get("/api/trailers/" + trailerId);
      setTrailer(prev => ({ ...prev, ...t, pending_handover: true, client_name: assignSelectedClient.company_name }));
    } catch (ex) { alert("Eroare: " + ex.message); }
    setAssignSaving(false);
  };

  const handleCancelHandover = async () => {
    if (!window.confirm("Sigur vrei să anulezi predarea pentru " + trailer?.numar_auto + "?")) return;
    try {
      await api.post("/api/trailers/" + trailerId + "/cancel-handover");
      setTrailer(prev => ({ ...prev, pending_handover: false, client_name: "Nealocat", client_email: null }));
    } catch (ex) { alert("Eroare: " + ex.message); }
  };

  const handlePrepareReturn = async () => {
    try {
      await api.post("/api/trailers/" + trailerId + "/prepare-return");
      setTrailer(prev => ({ ...prev, pending_return: true }));
    } catch (ex) { alert("Eroare: " + ex.message); }
  };

  const handleCancelReturn = async () => {
    if (!window.confirm("Sigur vrei să anulezi returnarea pentru " + trailer?.numar_auto + "?")) return;
    try {
      await api.post("/api/trailers/" + trailerId + "/cancel-return");
      setTrailer(prev => ({ ...prev, pending_return: false }));
    } catch (ex) { alert("Eroare: " + ex.message); }
  };

  const itpD = daysTo(trailer.itp_expirare);
  const rcaD = daysTo(trailer.rca_expirare);

  return (
    <div className="lm-detail-wrap" style={{ overflow: "hidden" }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <button style={{ ...S.btn, ...S.btnGhost, padding: "6px 14px", flexShrink: 0 }} onClick={onBack}>← Înapoi</button>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: C.text, margin: 0, wordBreak: "break-all" }}>{trailer.numar_auto}</h2>
          <span style={S.badge(stareColor(trailer.stare))}>{trailer.stare}</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {userRole === "admin" && <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => onEdit(trailer)}>✏️ Editează</button>}
          {!isTractor && trailer.stare === "Activă" && (userRole === "admin" || userRole === "dispecer") && (
            <button style={{ ...S.btn, background: C.green, color: C.white }} onClick={() => onStartPV(trailer, "inspectie")}>🔍 Inspecție</button>
          )}
          {isTractor && (trailer.stare === "Activă" || trailer.stare === "În așteptare") && (
            <button style={{ ...S.btn, background: "#7c3aed", color: C.white }} onClick={() => onStartPV(trailer, "predare")}>📋 Crează PV Predare</button>
          )}
          {userRole === "admin" && !trailer.pending_handover && (trailer.stare === "Activă" || ((trailer.has_rate_contract || trailer.has_rent_contract) && !handoverDone) || (trailer.stare === "Închiriată" && !handoverDone)) && (
            <button style={{ ...S.btn, background: "#7c3aed", color: C.white }} onClick={() => setAssignModal(true)}>📋 Pregătește Predare</button>
          )}
          {userRole === "admin" && trailer.pending_handover && (
            <button style={{ ...S.btn, background: "#7c3aed", color: C.white }} onClick={() => setAssignModal(true)}>✏️ Modifică Predare</button>
          )}
          {userRole === "admin" && trailer.pending_handover && (
            <button style={{ ...S.btn, background: C.red, color: C.white }} onClick={handleCancelHandover}>❌ Anulează Predarea</button>
          )}
          {userRole !== "admin" && trailer.pending_handover && (
            <button style={{ ...S.btn, ...S.btnPrimary }} onClick={() => onStartPV(trailer, "predare")}>📋 Predare</button>
          )}
          {userRole === "admin" && (trailer.stare === "Închiriată" || trailer.stare === "Vânzare în rate") && !trailer.pending_return && handoverDone && (
            <button style={{ ...S.btn, background: "#7c3aed", color: C.white }} onClick={handlePrepareReturn}>📋 Pregătește Returnare</button>
          )}
          {userRole === "admin" && trailer.pending_return && (
            <button style={{ ...S.btn, background: C.red, color: C.white }} onClick={handleCancelReturn}>❌ Anulează Returnarea</button>
          )}
          {userRole !== "admin" && (trailer.stare === "Închiriată" || trailer.stare === "Vânzare în rate") && trailer.pending_return && (
            <button style={{ ...S.btn, background: C.orange, color: C.white }} onClick={() => onStartPV(trailer, "returnare")}>📋 Returnare</button>
          )}
          {trailer.stare === "Returnată" && (
            <>
              <span style={{ ...S.badge("#8b5cf6"), fontSize: 12, padding: "6px 12px" }}>⏳ Returnată</span>
              <button style={{ ...S.btn, background: C.green, color: C.white }} onClick={async () => {
                if (!window.confirm("Sigur? Remorca va deveni Activă fără service.")) return;
                try { await api.post("/api/trailers/" + trailerId + "/clear-return"); const t = await api.get("/api/trailers/" + trailerId); setTrailer(t); } catch(ex) { alert("Eroare: " + ex.message); }
              }}>✅ Fără service necesar</button>
            </>
          )}
          {(trailer.stare === "Activă" || trailer.stare === "Închiriată" || trailer.stare === "Returnată" || trailer.stare === "Vânzare în rate") && !trailer.service_orders?.some(s => s.status !== "completed" && s.status !== "rejected" && s.type !== "interventie") && (
            <button style={{ ...S.btn, background: C.orange, color: C.white }} onClick={() => onSendToService && onSendToService(trailer.id, null)}>🔧 Trimite la Service</button>
          )}
        </div>
      </div>

      {/* Rate contract alert banner */}
      {trailer.has_rate_contract && !trailer.pending_handover && !handoverDone && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderLeft: "4px solid #dc2626", borderRadius: 8, padding: "10px 16px", marginBottom: 16 }}>
          <span style={{ fontWeight: 700, color: "#dc2626" }}>⚠️ Contract de rate activ — necesită PV predare-primire!</span>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>După finalizarea PV-ului, remorca va trece automat pe starea "Vânzare în rate".</div>
        </div>
      )}
      {/* Rent contract alert banner */}
      {trailer.has_rent_contract && !trailer.pending_handover && !handoverDone && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderLeft: "4px solid #ea580c", borderRadius: 8, padding: "10px 16px", marginBottom: 16 }}>
          <span style={{ fontWeight: 700, color: "#ea580c" }}>⚠️ Contract de chirie activ — necesită PV predare-primire!</span>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>După finalizarea PV-ului, remorca va trece automat pe starea "Închiriată".</div>
        </div>
      )}
      {/* Pending handover banner */}
      {trailer.pending_handover && (
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderLeft: "4px solid #2563eb", borderRadius: 8, padding: "10px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div>
            <span style={{ fontWeight: 700, color: "#1e3a5f" }}>🚛 De predat către: {trailer.client_name}</span>
            {trailer.estimated_handover_date && <span style={{ color: C.text2, fontSize: 13, marginLeft: 8 }}>📅 {new Date(trailer.estimated_handover_date).toLocaleDateString("ro-RO")}</span>}
          </div>
          {userRole !== "admin" && (
            <button style={{ ...S.btn, ...S.btnPrimary, padding: "6px 14px", fontSize: 13 }}
              onClick={() => onStartPV(trailer, "predare")}>
              📋 Începe Predarea
            </button>
          )}
        </div>
      )}

      {/* Pending return banner */}
      {trailer.pending_return && (trailer.stare === "Închiriată" || trailer.stare === "Vânzare în rate") && (
        <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderLeft: "4px solid #ea580c", borderRadius: 8, padding: "10px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div>
            <span style={{ fontWeight: 700, color: "#9a3412" }}>🚛 De returnat de la: {trailer.client_name}</span>
          </div>
          {userRole !== "admin" && (
            <button style={{ ...S.btn, background: "#ea580c", color: "#fff", padding: "6px 14px", fontSize: 13 }}
              onClick={() => onStartPV(trailer, "returnare")}>
              📋 Începe Returnarea
            </button>
          )}
        </div>
      )}

      {/* Assign client modal */}
      {assignModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setAssignModal(false)}>
          <div style={{ background: C.card, borderRadius: 16, padding: 24, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ color: C.text, margin: "0 0 16px", fontSize: 18 }}>📋 Pregătește Predare</h3>
            <p style={{ color: C.text2, fontSize: 13, marginBottom: 16 }}>Alocă un client pentru predarea remorcii <b>{trailer.numar_auto}</b>.</p>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 13, color: C.text2, marginBottom: 4, fontWeight: 600 }}>Client *</label>
              <input style={{ ...S.input, width: "100%", marginBottom: 0 }} value={assignSearch} onChange={e => { setAssignSearch(e.target.value); setAssignSelectedClient(null); }} placeholder="Caută client..." />
              {assignSearch.length > 0 && !assignSelectedClient && (
                <div style={{ border: "1px solid " + C.border, borderRadius: 8, maxHeight: 180, overflowY: "auto", background: C.card, marginTop: 4 }}>
                  {assignClientsList.filter(c => c.company_name.toLowerCase().includes(assignSearch.toLowerCase()) || (c.cui || "").toLowerCase().includes(assignSearch.toLowerCase())).length === 0 ? (
                    <div style={{ padding: 12, fontSize: 13, color: C.text2, textAlign: "center" }}>Niciun client găsit</div>
                  ) : assignClientsList.filter(c => c.company_name.toLowerCase().includes(assignSearch.toLowerCase()) || (c.cui || "").toLowerCase().includes(assignSearch.toLowerCase())).map(c => (
                    <div key={c.id} style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid " + C.border, fontSize: 13 }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      onClick={() => { setAssignSelectedClient(c); setAssignSearch(c.company_name); setAssignClient(c.company_name); }}>
                      <div style={{ fontWeight: 600, color: C.text }}>{c.company_name}</div>
                      <div style={{ fontSize: 11, color: C.text2 }}>{c.cui || ""} {c.email ? "· " + c.email : ""}</div>
                    </div>
                  ))}
                </div>
              )}
              {assignSelectedClient && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, padding: "6px 10px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
                  <span style={{ fontSize: 13, color: "#166534", fontWeight: 600 }}>✅ {assignSelectedClient.company_name}</span>
                  {assignSelectedClient.email && <span style={{ fontSize: 11, color: "#166534" }}>· {assignSelectedClient.email}</span>}
                </div>
              )}
              {!assignSelectedClient && assignSearch.length === 0 && (
                <div style={{ fontSize: 12, color: C.text2, marginTop: 6 }}>Clientul nu e în listă? Adaugă-l mai întâi din meniul <b>Clienți</b>.</div>
              )}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 13, color: C.text2, marginBottom: 4, fontWeight: 600 }}>Data estimată predare</label>
              <input type="date" style={{ ...S.input, width: "100%" }} value={assignDate} onChange={e => setAssignDate(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => setAssignModal(false)}>Anulează</button>
              <button style={{ ...S.btn, ...S.btnPrimary, opacity: (assignSaving || !assignSelectedClient) ? 0.6 : 1 }} disabled={assignSaving || !assignSelectedClient} onClick={handleAssignClient}>
                {assignSaving ? "Se salvează..." : "Salvează și Notifică"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Differences alert banner — hide if service completed/active or trailer Activă */}
      {(() => {
        // PVs that have any service order (active or completed)
        const servicedPvIds = (trailer.service_orders || []).map(s => s.pv_returnare_id);
        // Only show banner for returnare PVs with diffs that have NO service order at all
        const diffPvs = (trailer.pvs || []).filter(p =>
          p.type === "returnare" && p.differences_count > 0 && !servicedPvIds.includes(p.id)
        );
        // Also hide if trailer is back to Activă (diffs were dismissed)
        if (diffPvs.length === 0 || trailer.stare === "Activă") return null;
        const totalDiffs = diffPvs.reduce((s, p) => s + (p.differences_count || 0), 0);
        return (
          <div style={{ background: "#fef2f2", border: `1px solid #fecaca`, borderLeft: `4px solid ${C.red}`, borderRadius: 8, padding: "10px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span style={{ fontWeight: 700, color: C.red }}>📊 {totalDiffs} diferențe constatate</span>
              <span style={{ color: C.text2, fontSize: 13, marginLeft: 8 }}>la {diffPvs.length} PV returnare</span>
            </div>
            <button style={{ ...S.btn, background: C.red, color: C.white, padding: "5px 14px", fontSize: 13 }}
              onClick={() => onShowDifferences && onShowDifferences(diffPvs[0].id)}>
              Vezi Raport
            </button>
          </div>
        );
      })()}

      {/* Service status banner */}
      {trailer.stare === "În service" && (() => {
        const activeService = (trailer.service_orders || []).find(s => s.status !== "completed");
        if (!activeService) return null;
        const st = SERVICE_STATUS[activeService.status] || SERVICE_STATUS.pending;
        const items = typeof activeService.items === "string" ? JSON.parse(activeService.items) : (activeService.items || []);
        const done = items.filter(i => i.status === "done" || i.done).length;
        return (
          <div style={{ background: "#eff6ff", border: `1px solid #bfdbfe`, borderLeft: `4px solid ${C.accent}`, borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ fontWeight: 700, color: C.accent }}>🔧 În Service</span>
                <span style={{ ...S.badge(st.color), marginLeft: 8 }}>{st.icon} {st.label}</span>
              </div>
              <span style={{ fontSize: 13, color: C.text2 }}>{done}/{items.length} lucrări</span>
            </div>
            <div style={{ width: "100%", height: 6, background: C.border, borderRadius: 3, marginTop: 8, overflow: "hidden" }}>
              <div style={{ width: items.length ? `${(done / items.length) * 100}%` : "0%", height: "100%", background: C.accent, borderRadius: 3 }} />
            </div>
          </div>
        );
      })()}

      {/* Tabs */}
      <div className="lm-tabs" style={S.tabs}>
        {[
          { id: "info", label: "Informații" },
          { id: "anvelope", label: "Anvelope" },
          { id: "pvs", label: "Procese Verbale" },
          { id: "fotografii", label: "Fotografii" },
          { id: "km", label: "Kilometraj" },
          { id: "history", label: "Istoric" },
          { id: "service", label: "Service" },
        ].map(t => (
          <button key={t.id} style={{ ...S.tab, ...(tab === t.id ? S.tabActive : {}) }} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* Tab: Info — SVG left + details right */}
      {tab === "info" && (() => {
        const wheelZoneMap = isTractor ? { t_axa1_st: 0, t_axa1_dr: 1, t_axa2_st_ext: 2, t_axa2_st_int: 3, t_axa2_dr_int: 4, t_axa2_dr_ext: 5, t_axa3_st: 6, t_axa3_dr: 7 } : { axa1_st: 0, axa1_dr: 1, axa2_st: 2, axa2_dr: 3, axa3_st: 4, axa3_dr: 5 };
        const currentZones = isTractor ? getVehicleZones("tractor", trailer.configuratie_axe) : ZONES;
        const selZoneDef = selectedZone ? currentZones.find(z => z.id === selectedZone) : null;
        const isWheelZone = selZoneDef?.isWheel;
        const selTire = isWheelZone ? (trailer.anvelope || [])[wheelZoneMap[selectedZone]] : null;
        // Photos ONLY from latest PV for this zone (use allPvData which has full photo data)
        const latestZonePhotos = selectedZone && allPvData.length > 0
          ? (allPvData[0].photos || []).filter(p => p.zone_id === selectedZone) : [];
        // Older PV photos grouped by PV (for history)
        const olderPvPhotos = selectedZone ? allPvData.slice(1).map(pv => ({
          pvId: pv.id, pvType: pv.type, pvDate: pv.created_at || pv.date,
          photos: (pv.photos || []).filter(p => p.zone_id === selectedZone)
        })).filter(g => g.photos.length > 0) : [];
        // Checks from latest PV for the selected zone
        const latestChecks = selectedZone && latestPvData ? (latestPvData.checks || []).filter(c => c.zone_id === selectedZone) : [];
        // Wheel data from latest PV
        const latestWheelData = selectedZone && latestPvData ? (latestPvData.wheel_data || []).find(w => w.zone_id === selectedZone) : null;

        return (
          <div className="lm-2col" style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 28 }}>
            {/* Left: SVG */}
            <div>
              {isTractor ? (
                <TractorDetailSVG
                  anvelope={trailer.anvelope}
                  onZoneClick={(id) => { setSelectedZone(selectedZone === id ? null : id); setShowZoneHistory(false); }}
                  activeZone={selectedZone}
                  config={trailer.configuratie_axe || "4x2"}
                />
              ) : (
                <TrailerDetailSVG
                  anvelope={trailer.anvelope}
                  onZoneClick={(id) => { setSelectedZone(selectedZone === id ? null : id); setShowZoneHistory(false); }}
                  activeZone={selectedZone}
                />
              )}
              <div className="lm-zone-info-text" style={{ textAlign: "center", color: C.text2, fontSize: 12, marginTop: 8, wordWrap: "break-word" }}>
                Apasă pe o zonă pentru detalii
              </div>
            </div>

            {/* Right: Zone detail or General info */}
            <div style={{ minWidth: 0 }}>
              {selectedZone && selZoneDef ? (
                /* ── Zone detail panel ── */
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <h3 style={{ color: C.text, margin: 0, fontSize: 18 }}>{selZoneDef.label} — {selZoneDef.desc}</h3>
                    <button style={{ ...S.btn, ...S.btnGhost, padding: "4px 14px", fontSize: 13 }} onClick={() => setSelectedZone(null)}>
                      ← Informații generale
                    </button>
                  </div>

                  {/* Wheel tire data */}
                  {isWheelZone && selTire && (
                    <div className="lm-wheel-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                      <div style={S.infoItem}>
                        <div style={S.infoLabel}>Profil</div>
                        <div style={{ ...S.infoVal, fontSize: 22, fontWeight: 700 }}>{selTire.profil || "—"}</div>
                      </div>
                      <div style={S.infoItem}>
                        <div style={S.infoLabel}>Marcă</div>
                        <div style={{ ...S.infoVal, fontSize: 22, fontWeight: 700 }}>{selTire.marca || "—"}</div>
                      </div>
                      <div style={S.infoItem}>
                        <div style={S.infoLabel}>Serie</div>
                        <div style={{ ...S.infoVal, fontFamily: "monospace" }}>{selTire.serie || "—"}</div>
                      </div>
                      <div style={S.infoItem}>
                        <div style={S.infoLabel}>Plăcuțe</div>
                        {(() => {
                          const pl = Number(selTire.placute) || 0;
                          const color = pl > 50 ? C.green : pl > 25 ? C.orange : C.red;
                          return (
                            <div>
                              <div style={{ ...S.infoVal, fontSize: 22, fontWeight: 700, color }}>{pl}%</div>
                              <div style={{ width: "100%", height: 8, background: C.border, borderRadius: 4, marginTop: 6, overflow: "hidden" }}>
                                <div style={{ width: `${pl}%`, height: "100%", background: color, borderRadius: 4 }} />
                              </div>
                              <div style={{ fontSize: 12, color, marginTop: 4, fontWeight: 600 }}>
                                {pl > 50 ? "Stare bună" : pl > 25 ? "Uzată" : "Critică"}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Last PV checks for this zone */}
                  {latestChecks.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ color: C.accent, marginBottom: 8, fontSize: 14 }}>
                        Verificări din ultimul PV
                        {latestPvData && <span style={{ fontWeight: 400, color: C.text2, fontSize: 12 }}> ({fmtDT(latestPvData.created_at || latestPvData.date)})</span>}
                      </h4>
                      {latestChecks.sort((a, b) => a.check_index - b.check_index).map((c, i) => {
                        const checkLabel = selZoneDef.checks?.[c.check_index] || `Verificare ${c.check_index + 1}`;
                        const isOk = c.status === "ok";
                        const isProblem = c.status === "problem";
                        const color = isOk ? C.green : isProblem ? C.red : C.muted;
                        const icon = isOk ? "✓" : isProblem ? "✗" : "—";
                        return (
                          <div key={i} style={{
                            display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 10px",
                            background: isOk ? "#ecfdf5" : isProblem ? "#fef2f2" : "#f9fafb",
                            borderRadius: 6, marginBottom: 3,
                            borderLeft: isProblem ? `3px solid ${C.red}` : "none"
                          }}>
                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: "50%", background: color, color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                              {icon}
                            </span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, color: C.text }}>{checkLabel}</div>
                              {isProblem && c.check_note && (
                                <div style={{ marginTop: 3, padding: "3px 8px", background: "#fef2f2", borderLeft: `3px solid ${C.red}`, borderRadius: "0 4px 4px 0", color: C.red, fontSize: 12, fontWeight: 600 }}>
                                  {c.check_note}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {latestChecks.length === 0 && (
                    <div style={{ color: C.text2, fontSize: 13, padding: "8px 0", marginBottom: 16, fontStyle: "italic" }}>Nicio verificare din PV anterior pentru această zonă.</div>
                  )}

                  {/* Zone photos — ONLY from latest PV */}
                  {latestZonePhotos.length > 0 ? (
                    <div>
                      <h4 style={{ color: C.accent, marginBottom: 8, fontSize: 14 }}>
                        Fotografii din ultimul PV ({latestZonePhotos.length})
                      </h4>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {latestZonePhotos.map(p => (
                          <AuthPhotoThumb
                            key={p.id}
                            photo={p}
                            width={140}
                            height={110}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: C.text2, fontSize: 13, fontStyle: "italic", marginBottom: 12 }}>Nicio fotografie în ultimul PV pentru această zonă.</div>
                  )}

                  {/* Zone history — older PVs photos */}
                  {olderPvPhotos.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <button
                        style={{ ...S.btn, ...S.btnGhost, padding: "6px 14px", fontSize: 13, width: "100%" }}
                        onClick={() => setShowZoneHistory(!showZoneHistory)}
                      >
                        {showZoneHistory ? "🔽 Ascunde istoric zonă" : "📜 Vezi istoric zonă"} ({olderPvPhotos.length} PV-uri anterioare)
                      </button>
                      {showZoneHistory && (
                        <div style={{ marginTop: 12 }}>
                          {olderPvPhotos.map(g => {
                            const pvTypeLabel = g.pvType === "predare" ? "Predare" : g.pvType === "inspectie" ? "Inspecție" : "Returnare";
                            return (
                              <div key={g.pvId} style={{ marginBottom: 16, padding: 12, background: C.card2, borderRadius: 8, border: `1px solid ${C.border}` }}>
                                <div style={{ fontWeight: 600, color: C.text, fontSize: 13, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={S.badge(g.pvType === "predare" ? C.accent : g.pvType === "inspectie" ? C.green : C.orange)}>{pvTypeLabel}</span>
                                  <span>{fmtDT(g.pvDate)}</span>
                                </div>
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                  {g.photos.map(p => (
                                    <AuthPhotoThumb
                                      key={p.id}
                                      photo={p}
                                      width={120}
                                      height={90}
                                      radius={6}
                                      labelSize={9}
                                      placeholderText="..."
                                      loadingFontSize={10}
                                    />
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* ── General info panel ── */
                <div>
                  <h4 style={{ color: C.accent, marginBottom: 10, fontSize: 14 }}>Identificare</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10, marginBottom: 20 }}>
                    {[
                      { l: "Nr. Înmatriculare", v: trailer.numar_auto },
                      { l: "Serie Șasiu", v: trailer.serie_sasiu },
                      { l: "Tip", v: trailer.tip },
                      { l: "Marcă / Model", v: `${trailer.marca} ${trailer.model || ""}` },
                      { l: "An Fabricație", v: trailer.an_fabricatie },
                      { l: "Culoare", v: trailer.culoare || "—" },
                      { l: "Client", v: trailer.client_name || "Nealocat" },
                    ].map((item, i) => (
                      <div key={i} style={S.infoItem}>
                        <div style={S.infoLabel}>{item.l}</div>
                        <div style={S.infoVal}>{item.v}</div>
                      </div>
                    ))}
                  </div>

                  <h4 style={{ color: C.accent, marginBottom: 10, fontSize: 14 }}>Specificații</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10, marginBottom: 20 }}>
                    {[
                      { l: "Nr. Axe", v: trailer.nr_axe },
                      { l: "Dimensiuni (L×l×h)", v: `${trailer.lungime || "—"}×${trailer.latime || "—"}×${trailer.inaltime || "—"} m` },
                      { l: "Greutate proprie", v: trailer.greutate_proprie ? `${Number(trailer.greutate_proprie).toLocaleString()} kg` : "—" },
                      { l: "Sarcina maximă", v: trailer.sarcina_maxima ? `${Number(trailer.sarcina_maxima).toLocaleString()} kg` : "—" },
                      { l: "Preț achiziție", v: trailer.pret_achizitie ? eur(trailer.pret_achizitie) : "—" },
                      { l: "Data achiziție", v: trailer.data_achizitie || "—" },
                    ].map((item, i) => (
                      <div key={i} style={S.infoItem}>
                        <div style={S.infoLabel}>{item.l}</div>
                        <div style={S.infoVal}>{item.v}</div>
                      </div>
                    ))}
                  </div>

                  <h4 style={{ color: C.accent, marginBottom: 10, fontSize: 14 }}>Documente</h4>
                  <div className="lm-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                    <div style={{ ...S.infoItem, borderLeft: `3px solid ${itpD < 0 ? C.red : itpD < 30 ? C.orange : C.green}` }}>
                      <div style={S.infoLabel}>ITP Expirare</div>
                      <div style={{ ...S.infoVal, color: itpD < 0 ? C.red : itpD < 30 ? C.orange : C.green, fontWeight: 600 }}>
                        {trailer.itp_expirare || "—"}
                      </div>
                      <div style={{ fontSize: 12, color: itpD < 0 ? C.red : itpD < 30 ? C.orange : C.green, marginTop: 2 }}>
                        {itpD < 0 ? `Expirat de ${Math.abs(itpD)} zile` : `${itpD} zile rămase`}
                      </div>
                    </div>
                    <div style={{ ...S.infoItem, borderLeft: `3px solid ${rcaD < 0 ? C.red : rcaD < 30 ? C.orange : C.green}` }}>
                      <div style={S.infoLabel}>RCA Expirare</div>
                      <div style={{ ...S.infoVal, color: rcaD < 0 ? C.red : rcaD < 30 ? C.orange : C.green, fontWeight: 600 }}>
                        {trailer.rca_expirare || "—"}
                      </div>
                      <div style={{ fontSize: 12, color: rcaD < 0 ? C.red : rcaD < 30 ? C.orange : C.green, marginTop: 2 }}>
                        {rcaD < 0 ? `Expirat de ${Math.abs(rcaD)} zile` : `${rcaD} zile rămase`}
                      </div>
                    </div>
                  </div>

                  {(trailer.dotari || trailer.observatii) && (
                    <div className="lm-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      {trailer.dotari && (
                        <div style={S.infoItem}>
                          <div style={S.infoLabel}>Dotări</div>
                          <div style={S.infoVal}>{trailer.dotari}</div>
                        </div>
                      )}
                      {trailer.observatii && (
                        <div style={S.infoItem}>
                          <div style={S.infoLabel}>Observații</div>
                          <div style={S.infoVal}>{trailer.observatii}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Tab: Anvelope */}
      {tab === "anvelope" && (
        <div>
          {/* Desktop table */}
          <table className="lm-desktop-table" style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Poziție</th>
                <th style={S.th}>Profil (mm)</th>
                <th style={S.th}>Marcă</th>
                <th style={S.th}>Serie</th>
                <th style={S.th}>Plăcuțe %</th>
                <th style={S.th}>Stare</th>
              </tr>
            </thead>
            <tbody>
              {(trailer.anvelope || []).map((a, i) => {
                const pl = a.placute || 0;
                const plColor = pl > 50 ? C.green : pl > 25 ? C.orange : C.red;
                return (
                  <tr key={i}>
                    <td style={{ ...S.td, fontWeight: 600, borderRadius: "8px 0 0 8px" }}>{a.pozitie || a.poz}</td>
                    <td style={S.td}>{a.profil || "—"}</td>
                    <td style={S.td}>{a.marca || "—"}</td>
                    <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12 }}>{a.serie || "—"}</td>
                    <td style={S.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 70, height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ width: `${pl}%`, height: "100%", background: plColor, borderRadius: 3 }} />
                        </div>
                        <span style={{ color: plColor, fontWeight: 600 }}>{pl}%</span>
                      </div>
                    </td>
                    <td style={{ ...S.td, borderRadius: "0 8px 8px 0" }}><span style={S.badge(plColor)}>{pl > 50 ? "Bună" : pl > 25 ? "Uzată" : "Critică"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Mobile cards */}
          <div className="lm-mobile-cards" style={{ display: "none", flexDirection: "column", gap: 8 }}>
            {(trailer.anvelope || []).map((a, i) => {
              const pl = a.placute || 0;
              const plColor = pl > 50 ? C.green : pl > 25 ? C.orange : C.red;
              return (
                <div key={i} style={{ ...S.statCard, padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{a.pozitie || a.poz}</div>
                    <span style={S.badge(plColor)}>{pl > 50 ? "Bună" : pl > 25 ? "Uzată" : "Critică"}</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13 }}>
                    <div><span style={{ color: C.text2 }}>Profil: </span><span style={{ fontWeight: 600, color: C.text }}>{a.profil || "—"} mm</span></div>
                    <div><span style={{ color: C.text2 }}>Marcă: </span><span style={{ fontWeight: 600, color: C.text }}>{a.marca || "—"}</span></div>
                    <div><span style={{ color: C.text2 }}>Serie: </span><span style={{ fontFamily: "monospace", fontSize: 12, color: C.text }}>{a.serie || "—"}</span></div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: C.text2 }}>Plăcuțe: </span>
                      <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${pl}%`, height: "100%", background: plColor, borderRadius: 3 }} />
                      </div>
                      <span style={{ color: plColor, fontWeight: 700, fontSize: 13 }}>{pl}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab: PVs */}
      {tab === "pvs" && (
        <div>
          {(trailer.pvs || []).length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Niciun PV înregistrat.</div>
          ) : (
            <>
            {/* Desktop table */}
            <table className="lm-desktop-table" style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Tip</th>
                  <th style={S.th}>Data</th>
                  <th style={S.th}>Client</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {(trailer.pvs || []).map(pv => (
                  <tr key={pv.id}>
                    <td style={{ ...S.td, borderRadius: "8px 0 0 8px" }}>
                      <span style={S.badge(pv.type === "predare" ? C.accent : pv.type === "inspectie" ? C.green : C.orange)}>{pv.type === "predare" ? "Predare" : pv.type === "inspectie" ? "Inspecție" : "Returnare"}</span>
                    </td>
                    <td style={S.td}>{fmtDT(pv.created_at || pv.date)}</td>
                    <td style={S.td}>{pv.client_name}</td>
                    <td style={S.td}><span style={S.badge(C.green)}>Completat</span></td>
                    <td style={{ ...S.td, borderRadius: "0 8px 8px 0" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button style={{ ...S.btn, ...S.btnPrimary, padding: "5px 14px", fontSize: 13, opacity: loadingPdf ? 0.6 : 1 }}
                          disabled={loadingPdf}
                          onClick={async () => {
                            try {
                              setLoadingPdf(true);
                              const pvFull = await api.get(`/api/pvs/${pv.id}`);
                              await downloadPvPdf(pvFull, trailer, setLoadingPdf);
                            } catch (ex) { alert("Eroare la generare PDF: " + ex.message); setLoadingPdf(false); }
                          }}>
                          {loadingPdf ? "..." : "PDF"}
                        </button>
                        {pv.type === "returnare" && (pv.differences_count > 0) && (
                          <button style={{ ...S.btn, background: C.orange, color: C.white, padding: "5px 10px", fontSize: 12 }}
                            onClick={() => onShowDifferences && onShowDifferences(pv.id)}>
                            📊 Diferențe ({pv.differences_count})
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Mobile cards */}
            <div className="lm-mobile-cards" style={{ display: "none", flexDirection: "column", gap: 8 }}>
              {(trailer.pvs || []).map(pv => {
                const pvCol = pv.type === "predare" ? C.accent : pv.type === "inspectie" ? C.green : C.orange;
                const pvLbl = pv.type === "predare" ? "Predare" : pv.type === "inspectie" ? "Inspecție" : "Returnare";
                return (
                  <div key={pv.id} style={{ ...S.statCard, padding: 14, borderLeft: `4px solid ${pvCol}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={S.badge(pvCol)}>{pvLbl}</span>
                      <span style={{ fontSize: 12, color: C.text2 }}>{fmtDT(pv.created_at || pv.date)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: C.text, marginBottom: 8 }}>{pv.client_name}</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, padding: "6px 14px", fontSize: 13, opacity: loadingPdf ? 0.6 : 1 }}
                        disabled={loadingPdf}
                        onClick={async () => {
                          try {
                            setLoadingPdf(true);
                            const pvFull = await api.get(`/api/pvs/${pv.id}`);
                            await downloadPvPdf(pvFull, trailer, setLoadingPdf);
                          } catch (ex) { alert("Eroare la generare PDF: " + ex.message); setLoadingPdf(false); }
                        }}>
                        {loadingPdf ? "..." : "PDF"}
                      </button>
                      {pv.type === "returnare" && (pv.differences_count > 0) && (
                        <button className="lm-btn" style={{ ...S.btn, background: C.orange, color: C.white, padding: "6px 14px", fontSize: 13 }}
                          onClick={() => onShowDifferences && onShowDifferences(pv.id)}>
                          Diferențe ({pv.differences_count})
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      )}

      {/* Tab: Fotografii — grouped by PV, then by zone */}
      {tab === "fotografii" && (() => {
        const pvGroups = [...allPvData].sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date)).map(pv => ({
          pvId: pv.id, pvType: pv.type, pvDate: pv.created_at || pv.date,
          clientName: pv.client_name,
          photos: (pv.photos || []),
        })).filter(g => g.photos.length > 0);
        const ungrouped = (trailer.photos || []).filter(p => !p.pv_id);

        if (pvGroups.length === 0 && ungrouped.length === 0) {
          return <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Nicio fotografie înregistrată.</div>;
        }

        const pvNr = (id) => `#${String(id).padStart(3, "0")}`;
        const pvIcon = (t) => t === "predare" ? "📤" : t === "inspectie" ? "🔍" : "📥";
        const pvLabel = (t) => t === "predare" ? "PV Predare" : t === "inspectie" ? "PV Inspecție" : "PV Returnare";
        const pvColor = (t) => t === "predare" ? C.accent : t === "inspectie" ? C.green : C.orange;

        const renderPhotoCard = (p) => (
          <AuthPhotoThumb key={p.id} photo={p} />
        );

        return (
          <div>
            {pvGroups.map((g, gi) => {
              // Group photos by zone
              const byZone = {};
              g.photos.forEach(p => {
                const z = p.zone_id || "other";
                if (!byZone[z]) byZone[z] = [];
                byZone[z].push(p);
              });
              // Order zones by ZONES array order
              const zoneOrder = ZONES.map(z => z.id);
              const sortedZones = Object.keys(byZone).sort((a, b) => {
                const ia = zoneOrder.indexOf(a); const ib = zoneOrder.indexOf(b);
                return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
              });

              return (
                <div key={g.pvId}>
                  {/* PV Header — clickable accordion */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
                    background: `linear-gradient(135deg, ${pvColor(g.pvType)}11, ${pvColor(g.pvType)}08)`,
                    border: `1px solid ${pvColor(g.pvType)}33`, borderRadius: 10, marginBottom: expandedPvs[g.pvId] ? 12 : 0,
                    cursor: "pointer", userSelect: "none",
                  }} onClick={() => setExpandedPvs(prev => ({ ...prev, [g.pvId]: !prev[g.pvId] }))}>
                    <span style={{ fontSize: 22 }}>{pvIcon(g.pvType)}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>
                        {pvLabel(g.pvType)} {pvNr(g.pvId)}
                        <span style={{ fontWeight: 400, color: C.text2, fontSize: 13, marginLeft: 8 }}>— {fmtDT(g.pvDate)}</span>
                      </div>
                      {g.clientName && g.clientName !== "Inspecție internă" && (
                        <div style={{ fontSize: 12, color: C.text2, marginTop: 2 }}>Client: {g.clientName}</div>
                      )}
                    </div>
                    <span style={{ ...S.badge(pvColor(g.pvType)), fontSize: 12 }}>{g.photos.length} foto</span>
                    <span style={{ fontSize: 18, color: C.text2, transition: "transform 0.2s", transform: expandedPvs[g.pvId] ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                  </div>

                  {/* Photos grouped by zone — only visible when expanded */}
                  {expandedPvs[g.pvId] && sortedZones.map(zid => (
                    <div key={zid} style={{ marginBottom: 12, marginLeft: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.accent, marginBottom: 6, paddingBottom: 3, borderBottom: `1px solid ${C.border}` }}>
                        {ZONES_LABELS[zid] || zid}
                      </div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {byZone[zid].map(renderPhotoCard)}
                      </div>
                    </div>
                  ))}

                  {/* Separator between PV groups */}
                  {gi < pvGroups.length - 1 && (
                    <div style={{ borderTop: `2px solid ${C.border}`, margin: "16px 0" }} />
                  )}
                </div>
              );
            })}
            {ungrouped.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 22 }}>📷</span>
                  <div style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>Alte fotografii</div>
                  <span style={{ ...S.badge(C.muted), fontSize: 12, marginLeft: "auto" }}>{ungrouped.length} foto</span>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginLeft: 16 }}>
                  {ungrouped.map(renderPhotoCard)}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Tab: Kilometraj */}
      {tab === "km" && (() => {
        const KmTab = () => {
          const [kmStats, setKmStats] = useState(null);
          const [kmLogs, setKmLogs] = useState([]);
          const [showKmModal, setShowKmModal] = useState(false);
          const [newKm, setNewKm] = useState("");
          const [newKmNote, setNewKmNote] = useState("");
          const [kmSaving, setKmSaving] = useState(false);
          const [kmLoading, setKmLoading] = useState(true);

          const loadKmData = useCallback(async () => {
            setKmLoading(true);
            try {
              const [stats, logs] = await Promise.all([
                api.get(`/api/km/${trailerId}/stats`),
                api.get(`/api/km/${trailerId}`),
              ]);
              setKmStats(stats);
              setKmLogs(logs);
            } catch { /* ok */ }
            setKmLoading(false);
          }, []);

          useEffect(() => { loadKmData(); }, [loadKmData]);

          const handleAddKm = async () => {
            if (!newKm || isNaN(newKm)) { alert("Introduceți o valoare validă."); return; }
            setKmSaving(true);
            try {
              await api.post(`/api/km/${trailerId}`, { km_value: parseInt(newKm), note: newKmNote || null });
              setShowKmModal(false);
              setNewKm("");
              setNewKmNote("");
              loadKmData();
              loadTrailer(); // refresh trailer data
            } catch (ex) { alert(ex.message || "Eroare"); }
            setKmSaving(false);
          };

          if (kmLoading) return <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Se încarcă...</div>;

          // Simple SVG line chart
          const chartData = kmStats?.chart_data || [];
          const renderChart = () => {
            if (chartData.length < 2) return <div style={{ textAlign: "center", padding: 20, color: C.text2, fontSize: 13 }}>Insuficiente date pentru grafic (minim 2 luni)</div>;
            const values = chartData.map(d => parseInt(d.km));
            const minV = Math.min(...values);
            const maxV = Math.max(...values);
            const range = maxV - minV || 1;
            const W = 500, H = 160, PX = 40, PY = 20;
            const points = chartData.map((d, i) => {
              const x = PX + (i / (chartData.length - 1)) * (W - PX * 2);
              const y = PY + (1 - (parseInt(d.km) - minV) / range) * (H - PY * 2);
              return { x, y, km: parseInt(d.km), month: new Date(d.month).toLocaleDateString("ro-RO", { month: "short", year: "2-digit" }) };
            });
            const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
            return (
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: 500, height: "auto" }}>
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
                  const y = PY + (1 - f) * (H - PY * 2);
                  const val = Math.round(minV + f * range);
                  return <g key={i}><line x1={PX} y1={y} x2={W - PX} y2={y} stroke={C.border} strokeWidth="0.5" /><text x={PX - 4} y={y + 3} textAnchor="end" fill={C.text2} fontSize="8">{val.toLocaleString()}</text></g>;
                })}
                {/* Line */}
                <path d={linePath} fill="none" stroke={C.accent} strokeWidth="2.5" strokeLinejoin="round" />
                {/* Points + labels */}
                {points.map((p, i) => (
                  <g key={i}>
                    <circle cx={p.x} cy={p.y} r="4" fill={C.accent} stroke="#fff" strokeWidth="1.5" />
                    <text x={p.x} y={H - 4} textAnchor="middle" fill={C.text2} fontSize="7">{p.month}</text>
                  </g>
                ))}
              </svg>
            );
          };

          const sourceLabel = { manual: "Manual", gps: "GPS", pv: "Proces Verbal" };

          return (
            <div>
              {/* KM header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 36, fontWeight: 800, color: C.text }}>{(kmStats?.km_actuali || trailer.km_actuali || 0).toLocaleString()} <span style={{ fontSize: 16, fontWeight: 400, color: C.text2 }}>km</span></div>
                  {kmStats?.ultimo_update && <div style={{ fontSize: 12, color: C.text2 }}>Ultimul update: {fmtDT(kmStats.ultimo_update)}</div>}
                </div>
                <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary }} onClick={() => { setNewKm(""); setNewKmNote(""); setShowKmModal(true); }}>
                  + Actualizează KM
                </button>
              </div>

              {/* Stats cards */}
              <div className="lm-stat-cards" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
                <div style={{ ...S.statCard, padding: 14, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: C.text2, marginBottom: 4 }}>Km la predare</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{(kmStats?.km_la_predare || 0).toLocaleString()}</div>
                </div>
                <div style={{ ...S.statCard, padding: 14, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: C.text2, marginBottom: 4 }}>Total parcurși</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: C.accent }}>{(kmStats?.km_parcursi_total || 0).toLocaleString()}</div>
                </div>
                <div style={{ ...S.statCard, padding: 14, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: C.text2, marginBottom: 4 }}>Medie / lună</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{(kmStats?.km_per_luna || 0).toLocaleString()}</div>
                </div>
              </div>

              {/* Chart */}
              <div style={{ ...S.statCard, padding: 16, marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>Evoluție Km (6 luni)</div>
                <div className="lm-chart-wrap">{renderChart()}</div>
              </div>

              {/* Recent logs table */}
              <div style={{ ...S.statCard, padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14, color: C.text }}>Ultimele Înregistrări</div>
                {kmLogs.length === 0 ? (
                  <div style={{ padding: 24, textAlign: "center", color: C.text2 }}>Nicio înregistrare km.</div>
                ) : (
                  <div>
                    {kmLogs.slice(0, 10).map(log => (
                      <div key={log.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: `1px solid ${C.border}` }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{log.km_value.toLocaleString()} km</div>
                          <div style={{ fontSize: 11, color: C.text2 }}>{log.logged_by_name || "—"} {log.note ? `· ${log.note}` : ""}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span style={{ ...S.badge(log.source === "gps" ? C.green : log.source === "pv" ? C.accent : C.muted), fontSize: 10 }}>{sourceLabel[log.source] || log.source}</span>
                          <div style={{ fontSize: 11, color: C.text2, marginTop: 2 }}>{fmtDT(log.created_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add KM Modal */}
              {showKmModal && (
                <div style={S.overlay} onClick={() => setShowKmModal(false)}>
                  <div style={{ ...S.modal, maxWidth: 380 }} onClick={e => e.stopPropagation()}>
                    <h3 style={{ color: C.text, marginBottom: 16 }}>Actualizează Kilometraj</h3>
                    <div style={{ fontSize: 12, color: C.text2, marginBottom: 8 }}>Valoare curentă: <strong>{(trailer.km_actuali || 0).toLocaleString()} km</strong></div>
                    <label style={{ fontSize: 12, color: C.text2, fontWeight: 600, display: "block", marginBottom: 4 }}>Km noi *</label>
                    <input style={S.input} type="number" value={newKm} onChange={e => setNewKm(e.target.value)}
                      placeholder="Ex: 125000" min={trailer.km_actuali || 0} autoFocus />
                    <label style={{ fontSize: 12, color: C.text2, fontWeight: 600, display: "block", marginBottom: 4, marginTop: 8 }}>Notă (opțional)</label>
                    <input style={S.input} value={newKmNote} onChange={e => setNewKmNote(e.target.value)} placeholder="Ex: Verificare la service" />
                    <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
                      <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost }} onClick={() => setShowKmModal(false)}>Anulează</button>
                      <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, opacity: kmSaving ? 0.6 : 1 }} disabled={kmSaving} onClick={handleAddKm}>
                        {kmSaving ? "..." : "Salvează"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        };
        return <KmTab />;
      })()}

      {/* Tab: History */}
      {tab === "history" && (
        <div>
          {(trailer.history || []).length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Niciun eveniment înregistrat.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(trailer.history || []).map(h => (
                <div key={h.id} style={{ ...S.infoItem, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 14 }}>{h.description}</div>
                    <div style={{ color: C.text2, fontSize: 12, marginTop: 4 }}>{h.user_name || "System"}</div>
                  </div>
                  <div style={{ color: C.text2, fontSize: 12 }}>{fmtDT(h.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Service (detalii complete) */}
      {tab === "service" && (() => {
        const orders = trailer.service_orders || [];
        if (orders.length === 0) return <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Nicio comandă de service.</div>;
        const completedOrders = orders.filter(o => o.status === "completed" && o.total_cost > 0);
        const totalInvested = completedOrders.reduce((s, o) => s + (parseFloat(o.total_cost) || 0), 0);

        const ServiceOrderCard = ({ o }) => {
          const [expanded, setExpanded] = useState(o.status !== "completed");
          const [cardSaving, setCardSaving] = useState(false);
          const [cardRejectNotes, setCardRejectNotes] = useState("");
          const [showCardReject, setShowCardReject] = useState(false);
          const [showCardApprove, setShowCardApprove] = useState(false);
          const [cardItemCosts, setCardItemCosts] = useState({});
          const [addCostOrderId, setAddCostOrderId] = useState(null);
          const [newCost, setNewCost] = useState({ name: "", supplier: "", quantity: 1, price: "", date: new Date().toISOString().split("T")[0] });
          const cardCanApprove = (userRole === "admin" || userRole === "dispecer") && o.status === "mechanic_done";
          const st = SERVICE_STATUS[o.status] || SERVICE_STATUS.pending;
          const oItems = (() => { try { return typeof o.items === "string" ? JSON.parse(o.items) : (o.items || []); } catch { return []; } })();
          const done = oItems.filter(i => i.status === "done" || i.done).length;
          const hasCost = parseFloat(o.total_cost) > 0;

          return (
            <div style={{ ...S.statCard, marginBottom: 8, overflow: "hidden", borderLeft: "4px solid " + st.color }}>
              {/* Clickable header */}
              <div style={{ padding: "12px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
                onClick={() => setExpanded(!expanded)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>{o.type === "interventie" ? "Intervenție" : "Comandă"} #{o.id}</span>
                    {o.type === "interventie" ? <span style={{ ...S.badge("#7c3aed"), fontSize: 10 }}>🛞 Intervenție/Piese</span> : <span style={{ ...S.badge(C.orange), fontSize: 10 }}>🔧 Service</span>}
                    <span style={{ ...S.badge(st.color), fontSize: 11 }}>{st.icon} {st.label}</span>
                    {o.priority === "urgent" && <span style={{ ...S.badge(C.red), fontSize: 10 }}>🚨 URGENT</span>}
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 12, color: C.text2, flexWrap: "wrap" }}>
                    <span>{done}/{oItems.length} lucrări</span>
                    {hasCost && <span style={{ fontWeight: 600, color: C.accent }}>{fmtLei(o.total_cost)}</span>}
                    <span>{fmtDT(o.created_at)}</span>
                  </div>
                </div>
                <span style={{ fontSize: 18, color: C.text2, transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "rotate(0)" }}>▼</span>
              </div>

              {/* Expanded details */}
              {expanded && (
                <div style={{ padding: "0 16px 16px", borderTop: "1px solid " + C.border }}>

                  {/* Reject banner */}
                  {o.reject_notes && (
                    <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px", marginTop: 12, marginBottom: 8 }}>
                      <div style={{ fontWeight: 700, color: C.red, fontSize: 12 }}>Motiv respingere:</div>
                      <div style={{ fontSize: 12, color: C.text }}>{o.reject_notes}</div>
                    </div>
                  )}

                  {/* Info grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 12 }}><span style={{ color: C.text2 }}>Mecanic:</span> <b style={{ color: C.text }}>{o.mechanic_name || o.mechanic_username || "—"}</b></div>
                    <div style={{ fontSize: 12 }}><span style={{ color: C.text2 }}>Dispecer:</span> <b style={{ color: C.text }}>{o.dispecer_name || o.dispecer_username || "—"}</b></div>
                    {o.approved_by_name && <div style={{ fontSize: 12 }}><span style={{ color: C.text2 }}>Aprobat de:</span> <b style={{ color: C.text }}>{o.approved_by_name}</b></div>}
                    {hasCost && <div style={{ fontSize: 12 }}><span style={{ color: C.text2 }}>Cost total:</span> <b style={{ color: C.accent }}>{fmtLei(o.total_cost)}</b></div>}
                  </div>

                  {/* Description */}
                  {o.description && (
                    <div style={{ fontSize: 12, color: C.text2, marginBottom: 12, padding: "6px 10px", background: C.card2, borderRadius: 6 }}>
                      <span style={{ fontWeight: 600 }}>Descriere:</span> {o.description}
                    </div>
                  )}

                  {/* Items/Tasks */}
                  {oItems.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>Lucrări ({done}/{oItems.length})</div>
                      {/* Progress bar */}
                      <div style={{ width: "100%", height: 6, background: C.border, borderRadius: 3, overflow: "hidden", marginBottom: 10 }}>
                        <div style={{ width: oItems.length ? (done/oItems.length*100)+"%" : "0%", height: "100%", background: done === oItems.length && oItems.length > 0 ? C.green : C.accent, borderRadius: 3, transition: "width 0.3s" }} />
                      </div>
                      {oItems.map((item, idx) => {
                        const isDone = item.status === "done" || item.done;
                        return (
                          <div key={idx} style={{ padding: "8px 10px", marginBottom: 4, borderRadius: 6, background: isDone ? "#ecfdf5" : "#fff", border: "1px solid " + (isDone ? "#bbf7d0" : C.border) }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, color: C.text, fontWeight: isDone ? 400 : 600, textDecoration: isDone ? "line-through" : "none" }}>
                                  {isDone ? "✅" : "⬜"} {item.description || item.text}
                                </div>
                                {item.instructions && (
                                  <div style={{ fontSize: 11, color: C.text2, marginTop: 3, fontStyle: "italic" }}>📝 Instrucțiuni: {item.instructions}</div>
                                )}
                                {item.mechanic_note && (
                                  <div style={{ fontSize: 11, color: "#7c3aed", marginTop: 3, fontStyle: "italic" }}>🔧 Notă mecanic: {item.mechanic_note}</div>
                                )}
                              </div>
                              <div style={{ textAlign: "right", flexShrink: 0 }}>
                                {item.cost > 0 && <div style={{ fontWeight: 600, color: C.accent, fontSize: 13 }}>{fmtLei(item.cost)}</div>}
                                {item.cost_description && <div style={{ fontSize: 10, color: C.text2 }}>{item.cost_description}</div>}
                                {item.severity && <span style={{ ...S.badge(item.severity === "critical" ? C.red : item.severity === "major" ? C.orange : C.green), fontSize: 9 }}>{item.severity}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Mechanic notes */}
                  {o.mechanic_notes && (
                    <div style={{ fontSize: 12, color: C.text2, padding: "6px 10px", background: "#f0fdf4", borderRadius: 6, marginBottom: 8 }}>
                      <span style={{ fontWeight: 600 }}>🔧 Note mecanic:</span> {o.mechanic_notes}
                    </div>
                  )}

                  {/* Cheltuieli piese (costs JSONB) */}
                  {(() => {
                    const costsList = (() => { try { return typeof o.costs === "string" ? JSON.parse(o.costs) : (o.costs || []); } catch { return []; } })();
                    if (costsList.length === 0 && !hasCost && userRole !== "admin" && userRole !== "dispecer") return null;
                    return (
                      <div style={{ marginTop: 8, marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Cheltuieli piese ({costsList.length})</div>
                          {(userRole === "admin" || userRole === "dispecer") && (
                            <button className="lm-btn" style={{ ...S.btn, background: C.accent, color: "#fff", padding: "3px 10px", fontSize: 11, fontWeight: 700, borderRadius: 6 }}
                              onClick={e => { e.stopPropagation(); setAddCostOrderId(prev => prev === o.id ? null : o.id); }}>
                              + Adaugă
                            </button>
                          )}
                        </div>
                        {/* Inline add cost form */}
                        {addCostOrderId === o.id && (
                          <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: 12, marginBottom: 8 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
                              <input style={{ ...S.input, marginBottom: 0, fontSize: 12, padding: "6px 8px" }} placeholder="Denumire piesă *" value={newCost.name} onClick={e => e.stopPropagation()}
                                onChange={e => setNewCost(p => ({ ...p, name: e.target.value }))} />
                              <input style={{ ...S.input, marginBottom: 0, fontSize: 12, padding: "6px 8px" }} placeholder="Furnizor" value={newCost.supplier} onClick={e => e.stopPropagation()}
                                onChange={e => setNewCost(p => ({ ...p, supplier: e.target.value }))} />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
                              <input style={{ ...S.input, marginBottom: 0, fontSize: 12, padding: "6px 8px" }} type="number" min="1" placeholder="Cant." value={newCost.quantity} onClick={e => e.stopPropagation()}
                                onChange={e => setNewCost(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} />
                              <input style={{ ...S.input, marginBottom: 0, fontSize: 12, padding: "6px 8px" }} type="number" step="0.01" min="0" placeholder="Preț *" value={newCost.price} onClick={e => e.stopPropagation()}
                                onChange={e => setNewCost(p => ({ ...p, price: e.target.value }))} />
                              <input style={{ ...S.input, marginBottom: 0, fontSize: 12, padding: "6px 8px" }} type="date" value={newCost.date} onClick={e => e.stopPropagation()}
                                onChange={e => setNewCost(p => ({ ...p, date: e.target.value }))} />
                            </div>
                            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                              <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "4px 12px", fontSize: 11 }} onClick={e => { e.stopPropagation(); setAddCostOrderId(null); }}>Anulează</button>
                              <button className="lm-btn" style={{ ...S.btn, background: C.green, color: "#fff", padding: "4px 12px", fontSize: 11, fontWeight: 700, opacity: cardSaving ? 0.6 : 1 }}
                                disabled={cardSaving || !newCost.name.trim() || !newCost.price}
                                onClick={async e => {
                                  e.stopPropagation(); setCardSaving(true);
                                  try {
                                    const existingCosts = (() => { try { return typeof o.costs === "string" ? JSON.parse(o.costs) : (o.costs || []); } catch { return []; } })();
                                    const updatedCosts = [...existingCosts, { name: newCost.name.trim(), supplier: newCost.supplier.trim(), quantity: parseInt(newCost.quantity) || 1, price: parseFloat(newCost.price) || 0, total: (parseInt(newCost.quantity) || 1) * (parseFloat(newCost.price) || 0), date: newCost.date }];
                                    await api.put("/api/service/" + o.id + "/costs", { costs: updatedCosts });
                                    setNewCost({ name: "", supplier: "", quantity: 1, price: "", date: new Date().toISOString().split("T")[0] });
                                    setAddCostOrderId(null);
                                    loadTrailer();
                                  } catch (ex) { alert("Eroare: " + ex.message); }
                                  setCardSaving(false);
                                }}>
                                Salvează
                              </button>
                            </div>
                          </div>
                        )}

                        {costsList.length > 0 && (
                          <div style={{ border: "1px solid " + C.border, borderRadius: 8, overflow: "hidden" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                              <thead>
                                <tr style={{ background: C.card2 }}>
                                  <th style={{ padding: "6px 8px", textAlign: "left", color: C.text2, fontWeight: 600, fontSize: 11 }}>Denumire</th>
                                  <th style={{ padding: "6px 8px", textAlign: "left", color: C.text2, fontWeight: 600, fontSize: 11 }}>Furnizor</th>
                                  <th style={{ padding: "6px 8px", textAlign: "right", color: C.text2, fontWeight: 600, fontSize: 11 }}>Cant.</th>
                                  <th style={{ padding: "6px 8px", textAlign: "right", color: C.text2, fontWeight: 600, fontSize: 11 }}>Preț</th>
                                  <th style={{ padding: "6px 8px", textAlign: "right", color: C.text2, fontWeight: 600, fontSize: 11 }}>Total</th>
                                  <th style={{ padding: "6px 8px", textAlign: "left", color: C.text2, fontWeight: 600, fontSize: 11 }}>Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {costsList.map((c, i) => (
                                  <tr key={i} style={{ borderTop: "1px solid " + C.border }}>
                                    <td style={{ padding: "6px 8px", color: C.text, fontWeight: 500 }}>{c.name}</td>
                                    <td style={{ padding: "6px 8px", color: C.text2 }}>{c.supplier || "—"}</td>
                                    <td style={{ padding: "6px 8px", textAlign: "right", color: C.text }}>{c.quantity || 1}</td>
                                    <td style={{ padding: "6px 8px", textAlign: "right", color: C.text }}>{fmtLei(c.price)}</td>
                                    <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: C.accent }}>{fmtLei((c.quantity || 1) * (c.price || 0))}</td>
                                    <td style={{ padding: "6px 8px", color: C.text2, fontSize: 11 }}>{c.date || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr style={{ borderTop: "2px solid " + C.border, background: C.card2 }}>
                                  <td colSpan="4" style={{ padding: "6px 8px", fontWeight: 700, color: C.text, textAlign: "right" }}>TOTAL:</td>
                                  <td style={{ padding: "6px 8px", fontWeight: 800, color: C.accent, textAlign: "right", fontSize: 14 }}>{fmtLei(costsList.reduce((s, c) => s + ((c.quantity || 1) * (c.price || 0)), 0))}</td>
                                  <td />
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Timeline */}
                  <div style={{ marginTop: 8, padding: "8px 0", borderTop: "1px solid " + C.border }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.text2, marginBottom: 6, textTransform: "uppercase" }}>Timeline</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.accent, flexShrink: 0 }} />
                        <span style={{ color: C.text2 }}>Creat:</span>
                        <span style={{ color: C.text }}>{fmtDT(o.created_at)}</span>
                        {o.dispecer_name && <span style={{ color: C.muted }}>— {o.dispecer_name}</span>}
                      </div>
                      {o.started_at && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.orange, flexShrink: 0 }} />
                          <span style={{ color: C.text2 }}>Început:</span>
                          <span style={{ color: C.text }}>{fmtDT(o.started_at)}</span>
                        </div>
                      )}
                      {o.completed_at && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, flexShrink: 0 }} />
                          <span style={{ color: C.text2 }}>Finalizat mecanic:</span>
                          <span style={{ color: C.text }}>{fmtDT(o.completed_at)}</span>
                        </div>
                      )}
                      {o.verified_at && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a", flexShrink: 0 }} />
                          <span style={{ color: C.text2 }}>Aprobat:</span>
                          <span style={{ color: C.text }}>{fmtDT(o.verified_at)}</span>
                          {o.approved_by_name && <span style={{ color: C.muted }}>— {o.approved_by_name}</span>}
                        </div>
                      )}
                      {o.status === "rejected" && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.red, flexShrink: 0 }} />
                          <span style={{ color: C.red, fontWeight: 600 }}>Respins</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Approve/Reject buttons for dispatcher */}
                  {cardCanApprove && !showCardApprove && !showCardReject && (
                    <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 12, paddingTop: 12, borderTop: "1px solid " + C.border }}>
                      <button className="lm-btn" style={{ ...S.btn, background: C.green, color: "#fff", padding: "10px 20px", fontSize: 14, fontWeight: 700, flex: 1 }}
                        disabled={cardSaving} onClick={e => { e.stopPropagation(); setShowCardApprove(true); }}>
                        Aprobă cu costuri
                      </button>
                      <button className="lm-btn" style={{ ...S.btn, background: C.red, color: "#fff", padding: "10px 20px", fontSize: 14, fontWeight: 700, flex: 1 }}
                        disabled={cardSaving} onClick={e => { e.stopPropagation(); setShowCardReject(true); }}>
                        Respinge
                      </button>
                    </div>
                  )}

                  {/* Inline approve form */}
                  {cardCanApprove && showCardApprove && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "2px solid " + C.green }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 10 }}>Costuri per lucrare (opțional)</div>
                      {oItems.map((item, idx) => (
                        <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                          <span style={{ flex: 1, fontSize: 12, color: C.text }}>{item.description || item.text}</span>
                          <input style={{ ...S.input, marginBottom: 0, width: 90, fontSize: 12, padding: "4px 8px" }} type="number" step="0.01" min="0" placeholder="Cost"
                            value={cardItemCosts[idx]?.cost || ""} onClick={e => e.stopPropagation()}
                            onChange={e => setCardItemCosts(prev => ({ ...prev, [idx]: { ...prev[idx], cost: e.target.value } }))} />
                        </div>
                      ))}
                      <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 12 }}>
                        <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "8px 16px" }} onClick={e => { e.stopPropagation(); setShowCardApprove(false); }}>Anulează</button>
                        <button className="lm-btn" style={{ ...S.btn, background: C.green, color: "#fff", padding: "8px 16px", fontWeight: 700, opacity: cardSaving ? 0.6 : 1 }}
                          disabled={cardSaving} onClick={async e => {
                            e.stopPropagation(); setCardSaving(true);
                            try {
                              const updatedItems = oItems.map((item, idx) => ({ ...item, cost: parseFloat(cardItemCosts[idx]?.cost) || 0 }));
                              await api.put("/api/service/" + o.id + "/approve", { items: updatedItems });
                              window.location.reload();
                            } catch (ex) { alert("Eroare: " + ex.message); }
                            setCardSaving(false);
                          }}>
                          {cardSaving ? "..." : "Aprobă — Remorca devine Activă"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Inline reject form */}
                  {cardCanApprove && showCardReject && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "2px solid " + C.red }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.red, marginBottom: 8 }}>Motivul respingerii</div>
                      <textarea style={{ ...S.input, height: 80, resize: "vertical" }} value={cardRejectNotes}
                        onChange={e => setCardRejectNotes(e.target.value)} onClick={e => e.stopPropagation()}
                        placeholder="Ce trebuie refăcut?" autoFocus />
                      <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 8 }}>
                        <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "8px 16px" }} onClick={e => { e.stopPropagation(); setShowCardReject(false); }}>Anulează</button>
                        <button className="lm-btn" style={{ ...S.btn, background: C.red, color: "#fff", padding: "8px 16px", fontWeight: 700, opacity: cardSaving ? 0.6 : 1 }}
                          disabled={cardSaving || !cardRejectNotes.trim()} onClick={async e => {
                            e.stopPropagation(); setCardSaving(true);
                            try {
                              await api.put("/api/service/" + o.id + "/reject", { reject_notes: cardRejectNotes });
                              window.location.reload();
                            } catch (ex) { alert("Eroare: " + ex.message); }
                            setCardSaving(false);
                          }}>
                          {cardSaving ? "..." : "Respinge"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        };

        return (
          <div>
            {totalInvested > 0 && (
              <div style={{ ...S.statCard, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 11, color: C.text2, textTransform: "uppercase" }}>Total investit în service</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: C.accent, marginTop: 4 }}>{fmtLei(totalInvested)}</div>
                </div>
                <div style={{ fontSize: 13, color: C.text2 }}>{completedOrders.length} reparații completate</div>
              </div>
            )}
            {orders.map(o => <ServiceOrderCard key={o.id} o={o} />)}
          </div>
        );
      })()}

      {/* Tab SVG removed — integrated into Info tab */}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PV WIZARD
   ═══════════════════════════════════════════════════════════ */
function PVWizard({ trailer, pvType, onClose, onSaved, clients, userRole, userName, vehicleType }) {
  const isTractor = vehicleType === "tractor";
  const isFinancing = !!trailer.cargo_dossier_id;
  const PV_ZONES = isTractor ? getVehicleZones("tractor", trailer.configuratie_axe) : ZONES;
  const [step, setStep] = useState(pvType === "inspectie" ? 1 : 0); // 0=info, 1=zones, 2=signature
  const [activeZone, setActiveZone] = useState(null);
  const isInspectie = pvType === "inspectie";
  const isPendingHandover = (pvType === "predare" || pvType === "returnare") && userRole !== "admin";

  // Auto-fill client from client_allocated_id
  const allocatedClient = trailer.client_allocated_id ? (clients || []).find(c => c.id === trailer.client_allocated_id) : null;
  const [clientName, setClientName] = useState(
    isInspectie ? "Inspecție internă" : (pvType === "returnare" ? (trailer.client_name || "") : (allocatedClient?.company_name || trailer.client_name || ""))
  );
  const [clientEmail, setClientEmail] = useState(
    (pvType === "predare" || pvType === "returnare") && trailer.client_email ? trailer.client_email : (allocatedClient?.email || "")
  );
  const [checks, setChecks] = useState({});
  const [wheelData, setWheelData] = useState({});
  const [wheelSource, setWheelSource] = useState(null);
  const [wheelConfirmed, setWheelConfirmed] = useState({});
  const [mentions, setMentions] = useState({});
  const [photos, setPhotos] = useState({}); // { slotId: { file, preview, uploaded, serverId } }
  const [uploading, setUploading] = useState({});
  const [ladaSculeCount, setLadaSculeCount] = useState(1); // 1 or 2 for "Ladă scule"
  const [scanduri, setScanduri] = useState({ present: null, lemn: 0, aluminiu: 0 }); // Scanduri dotări
  const [docVal, setDocVal] = useState({ rca: null, rcaExp: '', rov: null, rovExp: '', itp: null, itpExp: '' });
  const [kmValue, setKmValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [driverName, setDriverName] = useState("");
  const [driverIdCard, setDriverIdCard] = useState("");
  const fileInputRefs = useRef({});
  const sigCanvasRef = useRef(null);
  const sigDrawing = useRef(false);
  const zoneDetailRef = useRef(null);
  const finalizeRef = useRef(null);
  const incompleteListRef = useRef(null);
  // PV-ul creat de o incercare anterioara care a cazut dupa POST (legare poze /
  // generare PDF). La reincercare se reia pe el, ca sa nu se creeze un duplicat.
  const createdPvRef = useRef(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const draftKey = "pv_draft_" + trailer.id + "_" + pvType;
  const draftLoaded = useRef(false);

  // Pre-fill wheel data from latest PV (cascading: inspectie -> predare -> returnare)
  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/api/trailers/" + trailer.id + "/latest-wheels");
        if (data && data.wheels && Object.keys(data.wheels).length > 0) {
          setWheelData(data.wheels);
          const pvTypeLabel = data.pvType === "predare" ? "Predare" : data.pvType === "inspectie" ? "Inspecție" : data.pvType === "returnare" ? "Returnare" : "";
          setWheelSource(data.source === "pv"
            ? "Din ultimul PV (" + pvTypeLabel + " din " + new Date(data.pvDate).toLocaleDateString("ro-RO") + ")"
            : data.source === "tires" ? "Din inventarul remorcii" : null);
        } else if (trailer.anvelope && trailer.anvelope.length > 0) {
          // Final fallback: local anvelope data
          const wd = {};
          PV_ZONES.filter(z => z.isWheel).forEach(z => {
            const inv = trailer.anvelope[z.wheelIdx];
            if (inv && (inv.profil || inv.marca)) {
              wd[z.id] = { profil: inv.profil || "", marca: inv.marca || "", serie: inv.serie || "", placute: inv.placute || "" };
            }
          });
          if (Object.keys(wd).length > 0) {
            setWheelData(wd);
            setWheelSource("Din inventarul remorcii");
          }
        }
      } catch {
        // Fallback: use trailer.anvelope
        if (trailer.anvelope && trailer.anvelope.length > 0) {
          const wd = {};
          PV_ZONES.filter(z => z.isWheel).forEach(z => {
            const inv = trailer.anvelope[z.wheelIdx];
            if (inv && (inv.profil || inv.marca)) {
              wd[z.id] = { profil: inv.profil || "", marca: inv.marca || "", serie: inv.serie || "", placute: inv.placute || "" };
            }
          });
          if (Object.keys(wd).length > 0) setWheelData(wd);
        }
      }
    })();
  }, [trailer.id]);

  // Check for existing draft on mount
  useEffect(() => {
    if (draftLoaded.current) return;
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft && draft.savedAt) {
          setShowDraftModal(draft);
        }
      }
    } catch { /* ignore corrupt data */ }
    draftLoaded.current = true;
  }, [draftKey]);

  // Auto-save to localStorage on every meaningful change
  useEffect(() => {
    if (!draftLoaded.current) return;
    if (showDraftModal) return;
    const timer = setTimeout(() => {
      try {
        const photoRefs = {};
        Object.entries(photos).forEach(([slotId, p]) => {
          if (p.uploaded && p.serverId) {
            photoRefs[slotId] = { serverId: p.serverId, uploaded: true };
          }
        });
        const draft = {
          savedAt: new Date().toISOString(),
          step, checks, wheelData, wheelConfirmed, mentions,
          photoRefs, clientName, clientEmail, kmValue, ladaSculeCount, scanduri,
        };
        localStorage.setItem(draftKey, JSON.stringify(draft));
      } catch { /* localStorage full or not available */ }
    }, 1000);
    return () => clearTimeout(timer);
  }, [step, checks, wheelData, wheelConfirmed, mentions, photos, clientName, clientEmail, kmValue, ladaSculeCount, scanduri, draftKey, showDraftModal]);

  const setCheck = (zoneId, idx, status) => {
    setChecks(prev => ({
      ...prev,
      [zoneId]: { ...(prev[zoneId] || {}), [idx]: { ...(prev[zoneId]?.[idx] || {}), status, label: PV_ZONES.find(z => z.id === zoneId)?.checks[idx] || "" } }
    }));
  };

  const setCheckNote = (zoneId, idx, note) => {
    setChecks(prev => ({
      ...prev,
      [zoneId]: { ...(prev[zoneId] || {}), [idx]: { ...(prev[zoneId]?.[idx] || {}), note } }
    }));
  };

  const setWD = (zoneId, key, val) => {
    setWheelData(prev => ({ ...prev, [zoneId]: { ...(prev[zoneId] || {}), [key]: val } }));
    // Un-confirm if data changes
    setWheelConfirmed(prev => ({ ...prev, [zoneId]: false }));
  };

  // Photo upload handler

  // Compresie imagine in browser inainte de upload (reduce 5-15MB la ~200-500KB)
  function compressImage(file, maxWidth = 1920, quality = 0.7) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob(blob => {
          if (blob) resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
          else reject(new Error("Compresie esuata"));
        }, "image/jpeg", quality);
      };
      img.onerror = () => reject(new Error("Imagine invalida"));
      img.src = URL.createObjectURL(file);
    });
  }
  const handlePhotoSelect = async (zoneId, slot, file) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setPhotos(prev => ({ ...prev, [slot.id]: { file, preview, uploaded: false, serverId: null } }));

    // Upload immediately
    setUploading(prev => ({ ...prev, [slot.id]: true }));
    try {
      const compressed = await compressImage(file).catch(() => file);      const fd = new FormData();      fd.append("photo", compressed);      fd.append("trailer_id", trailer.id);

      fd.append("zone_id", zoneId);
      fd.append("slot_id", slot.id);
      fd.append("label", slot.label);
      const result = await api.uploadPhoto(fd);
      setPhotos(prev => ({ ...prev, [slot.id]: { ...prev[slot.id], uploaded: true, serverId: result.id } }));
    } catch (ex) {
      console.error("Upload failed:", ex);
      setPhotos(prev => ({ ...prev, [slot.id]: { ...prev[slot.id], uploaded: false, error: ex.message } }));
    }
    setUploading(prev => ({ ...prev, [slot.id]: false }));
  };

  // Dynamic photo slots for DOTĂRI zone
  const getDynamicPhotoSlots = (z) => {
    if (!z.dynamicPhotos) return z.photoSlots || [];
    // dotari_zona: photos depend on which checks are OK
    const zc = checks[z.id] || {};
    const slots = [];
    // check 0 = "Suport roată de rezervă"
    if (zc[0]?.status === "ok") slots.push({ id: "dotari_roata_rezerva", label: "Roată rezervă", required: true });
    // check 1 = "Ladă paleți"
    if (zc[1]?.status === "ok") slots.push({ id: "dotari_lada_paleti", label: "Ladă paleți", required: true });
    // check 2 = "Ladă scule"
    if (zc[2]?.status === "ok") {
      for (let i = 1; i <= ladaSculeCount; i++) {
        slots.push({ id: `dotari_lada_scule_${i}`, label: `Ladă scule ${ladaSculeCount > 1 ? i : ""}`.trim(), required: true });
      }
    }
    return slots;
  };

  // Zone completion logic
  const isZoneComplete = (z) => {
    // All checks filled
    const zc = checks[z.id];
    if (!zc) return false;
    const allChecksFilled = z.checks.every((_, idx) => zc[idx]?.status);
    if (!allChecksFilled) return false;

    // All problem checks must have notes
    const allProblemsNoted = z.checks.every((_, idx) => {
      if (zc[idx]?.status === "problem") return zc[idx]?.note && zc[idx].note.trim().length > 0;
      return true;
    });
    if (!allProblemsNoted) return false;

    // Scanduri validation for dotari_zona
    if (z.id === "dotari_zona") {
      if (scanduri.present === null) return false; // must choose DA/NU
      if (scanduri.present && (scanduri.lemn || 0) + (scanduri.aluminiu || 0) === 0) return false; // at least one > 0
    }

    // All required photos uploaded (use dynamic slots for dotari)
    const effectiveSlots = z.dynamicPhotos ? getDynamicPhotoSlots(z) : (z.photoSlots || []);
    const allPhotos = effectiveSlots.filter(s => s.required).every(s => photos[s.id]?.uploaded);
    if (!allPhotos) return false;

    // Wheel zones: data confirmed + profil + marca + placute filled
    if (z.isWheel) {
      const wd = wheelData[z.id];
      if (!wd || !wd.profil || !wd.marca || !wd.placute) return false;
      if (!wheelConfirmed[z.id]) return false;
    }

    return true;
  };

  const getZoneStatus = () => {
    const status = {};
    PV_ZONES.forEach(z => {
      if (isZoneComplete(z)) { status[z.id] = "done"; return; }
      const zc = checks[z.id];
      const hasAnyCheck = zc && Object.values(zc).some(c => c.status);
      const eSlots = z.dynamicPhotos ? getDynamicPhotoSlots(z) : (z.photoSlots || []);
      const hasAnyPhoto = eSlots.some(s => photos[s.id]);
      if (hasAnyCheck || hasAnyPhoto) status[z.id] = "partial";
    });
    return status;
  };

  const completedZones = PV_ZONES.filter(z => isZoneComplete(z)).length;
  const totalZones = PV_ZONES.length;
  const allZonesComplete = completedZones === totalZones;
  const incompleteZones = PV_ZONES.filter(z => !isZoneComplete(z));

  // Auto-scroll when zone completed
  const prevCompletedRef = useRef(completedZones);
  useEffect(() => {
    if (step !== 1) return;
    if (completedZones > prevCompletedRef.current) {
      setTimeout(() => {
        if (completedZones === totalZones) {
          if (finalizeRef.current) finalizeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          const nextIncomplete = PV_ZONES.find(z => !isZoneComplete(z));
          if (nextIncomplete) {
            setActiveZone(nextIncomplete.id);
            if (zoneDetailRef.current) zoneDetailRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }, 500);
    }
    prevCompletedRef.current = completedZones;
  }, [completedZones, step, totalZones]);

  // What's missing in a zone (for display)
  const getZoneMissing = (z) => {
    const missing = [];
    const zc = checks[z.id] || {};
    const unchecked = z.checks.filter((_, idx) => !zc[idx]?.status).length;
    if (unchecked > 0) missing.push(`${unchecked} verificări`);
    const unnotedProblems = z.checks.filter((_, idx) => zc[idx]?.status === "problem" && (!zc[idx]?.note || !zc[idx].note.trim())).length;
    if (unnotedProblems > 0) missing.push(`${unnotedProblems} probleme fără descriere`);
    const effectiveSlots2 = z.dynamicPhotos ? getDynamicPhotoSlots(z) : (z.photoSlots || []);
    const unphoto = effectiveSlots2.filter(s => s.required && !photos[s.id]?.uploaded).length;
    if (unphoto > 0) missing.push(`${unphoto} poze`);
    if (z.isWheel) {
      const wd = wheelData[z.id];
      if (!wd || !wd.profil || !wd.marca || !wd.placute) missing.push("date anvelopă");
      else if (!wheelConfirmed[z.id]) missing.push("confirmare date");
    }
    if (z.id === "dotari_zona") {
      if (scanduri.present === null) missing.push("scanduri DA/NU");
      else if (scanduri.present && (scanduri.lemn || 0) + (scanduri.aluminiu || 0) === 0) missing.push("cantitate scanduri");
    }
    return missing;
  };

  // Signature canvas
  const initSigCanvas = useCallback((canvas) => {
    if (!canvas) return;
    sigCanvasRef.current = canvas;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = "#1a2332";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const getPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };

    const start = (e) => { e.preventDefault(); sigDrawing.current = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!sigDrawing.current) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { sigDrawing.current = false; setSignatureData(canvas.toDataURL("image/png")); };

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("mouseup", end);
    canvas.addEventListener("mouseleave", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);
  }, []);

  const clearSignature = () => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  // Generate PDF (client-side, async for base64 photos)
  const generatePDF = async (pvResult) => {
    if (!openPvHtmlWindow(pvResult?.id)) {
      throw new Error("Popup blocat");
    }
  };

  // Final save
  const handleSave = async () => {
    if (!isInspectie && !clientName) { alert("Introduceți firma transportatorului."); return; }
    if (!allZonesComplete) { alert("Nu toate zonele sunt complete!"); return; }
    if (!signatureData) { alert("Semnătura este obligatorie!"); return; }
    if (!isInspectie && !driverName.trim()) { alert("Introduceți numele șoferului!"); return; }
    if (!isInspectie && !driverIdCard.trim()) { alert("Introduceți seria și numărul C.I.!"); return; }

    setSaving(true);
    try {
      // Daca o incercare anterioara a creat deja PV-ul dar a cazut pe un pas de
      // dupa (legare poze / generare PDF), NU mai cerem unul nou: reluam pe el.
      // Serverul blocheaza oricum al doilea PV de acelasi tip sub 2 minute si
      // intoarce 409 cu existing_pv_id (routes/pvs.js).
      let pvResult;
      if (createdPvRef.current) {
        pvResult = createdPvRef.current;
      } else {
        try {
          pvResult = await api.post("/api/pvs", {
        trailer_id: trailer.id,
        type: pvType,
        client_name: clientName || "Inspecție internă",
        client_email: clientEmail || null,
        date: new Date().toISOString(),
        checks,
        wheel_data: wheelData,
        mentions,
        signature_url: signatureData,
        driver_name: driverName || null,
        driver_id_card: driverIdCard || null,
        km_value: kmValue ? parseInt(kmValue) : null,
        scanduri_present: scanduri.present,
        scanduri_lemn_count: scanduri.present ? (scanduri.lemn || 0) : 0,
        scanduri_aluminiu_count: scanduri.present ? (scanduri.aluminiu || 0) : 0,
        ...(isFinancing ? {
          doc_rca_valid: docVal.rca,
          doc_rca_expiry: docVal.rcaExp || null,
          doc_rovinieta_valid: docVal.rov,
          doc_rovinieta_expiry: docVal.rovExp || null,
          doc_itp_valid: docVal.itp,
          doc_itp_expiry: docVal.itpExp || null,
        } : {}),
      });
        } catch (ex) {
          // 409 de la guard-ul anti dublu-submit: PV-ul exista deja, continuam pe el.
          const dup = (ex?.status === 409 && ex?.data?.duplicate) ? ex.data : null;
          if (dup && dup.existing_pv_id) {
            console.warn("PV deja creat (id " + dup.existing_pv_id + ") — se reia pe el, fara duplicat");
            pvResult = { id: dup.existing_pv_id, trailer_id: trailer.id, type: pvType, client_name: clientName || "Inspecție internă", date: new Date().toISOString() };
          } else {
            throw ex;
          }
        }
        createdPvRef.current = pvResult;
      }

      // Link uploaded photos to this PV
      const photoIds = Object.values(photos).filter(p => p.serverId).map(p => p.serverId);
      if (photoIds.length > 0) {
        try {
          await api.post("/api/photos/link-pv", { pv_id: pvResult.id, photo_ids: photoIds });
        } catch (ex) { console.error("Link photos error:", ex); }
      }

      // Generate PDF (async — fetches photos as base64)
      await generatePDF(pvResult);

      // Email is sent automatically by the server

      // Clear draft on success
      try { localStorage.removeItem(draftKey); } catch {}
      createdPvRef.current = null;
      onSaved(pvResult);
    } catch (ex) {
      alert("Eroare: " + ex.message);
    }
    setSaving(false);
  };

  const activeZoneData = PV_ZONES.find(z => z.id === activeZone);
  const zoneStatus = getZoneStatus();

  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 2000, overflow: "auto" }}>
      {/* Draft restore modal */}
      {showDraftModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: C.card, borderRadius: 16, padding: 24, maxWidth: 400, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ color: C.text, margin: "0 0 12px", fontSize: 18 }}>💾 PV nefinalizat găsit</h3>
            <p style={{ color: C.text2, fontSize: 13, marginBottom: 16 }}>
              Ai un PV nefinalizat din <b>{new Date(showDraftModal.savedAt).toLocaleDateString("ro-RO")}</b>, ora <b>{new Date(showDraftModal.savedAt).toLocaleTimeString("ro-RO", {hour:"2-digit",minute:"2-digit"})}</b>.
              <br/>Vrei să continui de unde ai rămas?
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={{ ...S.btn, ...S.btnGhost, flex: 1 }} onClick={() => {
                try { localStorage.removeItem(draftKey); } catch {}
                setShowDraftModal(false);
              }}>
                🆕 De la zero
              </button>
              <button style={{ ...S.btn, ...S.btnPrimary, flex: 1 }} onClick={async () => {
                const d = showDraftModal;
                if (d.checks) setChecks(d.checks);
                if (d.wheelData) setWheelData(d.wheelData);
                if (d.wheelConfirmed) setWheelConfirmed(d.wheelConfirmed);
                if (d.mentions) setMentions(d.mentions);
                if (d.clientName) setClientName(d.clientName);
                if (d.clientEmail) setClientEmail(d.clientEmail);
                if (d.kmValue) setKmValue(d.kmValue);
                if (d.ladaSculeCount) setLadaSculeCount(d.ladaSculeCount);
                if (d.scanduri) setScanduri(d.scanduri);
                if (d.step !== undefined) setStep(d.step);
                if (d.photoRefs) {
                  const restored = {};
                  const token = localStorage.getItem("lm_token");
                  await Promise.all(Object.entries(d.photoRefs).map(async ([slotId, p]) => {
                    let preview = null;
                    try {
                      const r = await fetch("/api/photos/file/" + p.serverId, {
                        headers: token ? { Authorization: "Bearer " + token } : {}
                      });
                      if (r.ok) {
                        const blob = await r.blob();
                        preview = URL.createObjectURL(blob);
                      }
                    } catch { /* skip preview */ }
                    restored[slotId] = { file: null, preview, uploaded: true, serverId: p.serverId };
                  }));
                  setPhotos(restored);
                }
                setShowDraftModal(false);
              }}>
                ✅ Continuă
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PV Header — responsive */}
      <div style={{
        background: pvType === "predare" ? "linear-gradient(135deg, #1e3a5f, #2563eb)" : pvType === "inspectie" ? "linear-gradient(135deg, #14532d, #16a34a)" : "linear-gradient(135deg, #7c2d12, #ea580c)",
        padding: "10px 16px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
        gap: 8, position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0, flex: 1 }}>
          <button style={{ ...S.btn, background: "rgba(255,255,255,0.15)", color: "#fff", padding: "5px 10px", fontSize: 13, border: "1px solid rgba(255,255,255,0.3)", flexShrink: 0 }} onClick={onClose}>✕</button>
          <span style={{ fontFamily: "'Bira'", fontSize: 14, color: "#ffffff", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.3 }}>
            {pvType === "predare" ? "PV Predare" : pvType === "inspectie" ? "Inspecție" : "PV Ret."} — {trailer.numar_auto}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 12, fontWeight: 600 }}>{completedZones}/{totalZones}</span>
          <div style={{ width: 60, height: 5, background: "rgba(255,255,255,0.2)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${(completedZones / totalZones) * 100}%`, height: "100%", background: allZonesComplete ? "#22c55e" : "#fbbf24", borderRadius: 3, transition: "width 0.3s" }} />
          </div>
        </div>
      </div>

      <div className="lm-content" style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto" }}>
        {/* Steps bar */}
        <div className="lm-wizard-steps" style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
          {(isInspectie ? [
            { label: "1. Inspecție Zone", ok: allZonesComplete, step: 1 },
            { label: "2. Semnătură & Finalizare", ok: !!signatureData && (isInspectie || (driverName.trim() && driverIdCard.trim())), step: 2 },
          ] : [
            { label: "1. Transportator", ok: !!clientName, step: 0 },
            { label: "2. Inspecție Zone", ok: allZonesComplete, step: 1 },
            { label: "3. Semnătură & Finalizare", ok: !!signatureData && (isInspectie || (driverName.trim() && driverIdCard.trim())), step: 2 },
          ]).map((s, _i) => (
            <button key={s.step} onClick={() => {
              if (s.step === 1 && !isInspectie && !clientName) { alert("Completează mai întâi firma transportatorului."); return; }
              if (s.step === 2 && !allZonesComplete) { alert("Completează mai întâi toate zonele de inspecție!"); return; }
              setStep(s.step);
            }} style={{
              ...S.btn, flex: 1, minWidth: 160,
              background: step === s.step ? C.accent : s.ok ? C.green + "18" : C.card,
              color: step === s.step ? "#fff" : s.ok ? C.green : C.text,
              border: `2px solid ${step === s.step ? C.accent : s.ok ? C.green : C.border}`,
            }}>
              {s.ok && step !== s.step ? "✓ " : ""}{s.label}
            </button>
          ))}
        </div>

        {/* ═══ STEP 0: CLIENT INFO ═══ */}
        {step === 0 && (
          <div style={{ ...S.statCard, maxWidth: 520 }}>
            <h3 style={{ color: C.text, marginBottom: 20, fontSize: 18 }}>Informații Transportator</h3>
            <label style={{ fontSize: 13, color: C.text2, display: "block", marginBottom: 4, fontWeight: 600 }}>Firma Transportator *</label>
            <input style={S.input} value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Ex: Transport Ionescu SRL" autoFocus />
            <label style={{ fontSize: 13, color: C.text2, display: "block", marginBottom: 4, fontWeight: 600 }}>Email Transportator</label>
            <input style={S.input} type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="contact@transportator.ro" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 4 }}>
              <div>
                <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Data</label>
                <input style={S.input} type="date" value={new Date().toISOString().split("T")[0]} readOnly />
              </div>
              <div>
                <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Remorcă</label>
                <input style={S.input} value={`${trailer.numar_auto} — ${trailer.marca} ${trailer.model || ""}`} readOnly />
              </div>
            </div>
            {isPendingHandover && (
              <div style={{ fontSize: 12, color: "#64748b", background: "#f1f5f9", padding: "6px 10px", borderRadius: 6, marginTop: 4, marginBottom: 4 }}>
                🔒 Clientul a fost alocat de admin. Nu poate fi modificat.
              </div>
            )}
            <label style={{ fontSize: 13, color: C.text2, display: "block", marginBottom: 4, marginTop: 8, fontWeight: 600 }}>Kilometraj (opțional)</label>
            <input style={S.input} type="number" value={kmValue} onChange={e => setKmValue(e.target.value)}
              placeholder={`Km actuali${trailer.km_actuali ? ` (curent: ${trailer.km_actuali.toLocaleString()} km)` : ""}`} min="0" />
            <button style={{ ...S.btn, ...S.btnPrimary, marginTop: 8, width: "100%" }}
              onClick={() => { if (!clientName.trim()) { alert("Firma transportatorului este obligatorie!"); return; } setStep(1); }}>
              Continuă la Inspecție →
            </button>
          </div>
        )}

        {/* ═══ STEP 1: ZONE INSPECTION ═══ */}
        {step === 1 && (
          <div>
            <div className="lm-zone-layout" style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {/* Left: SVG + Zone list */}
              <div className="lm-zone-sidebar" style={{ flex: "0 0 280px", minWidth: 240 }}>
                <div style={{ position: "sticky", top: 88 }}>
                  <h3 style={{ color: C.accent, marginBottom: 12, fontSize: 16 }}>Selectează Zona</h3>
                  {isTractor ? <TractorSVG onZoneClick={setActiveZone} activeZone={activeZone} zoneStatus={zoneStatus} config={trailer.configuratie_axe || "4x2"} /> : <TrailerSVG onZoneClick={setActiveZone} activeZone={activeZone} zoneStatus={zoneStatus} />}
                </div>
              </div>

              {/* Right: Zone detail panel */}
              <div ref={zoneDetailRef} style={{ flex: 1, minWidth: 0 }}>
                {!activeZoneData ? (
                  <div style={{ ...S.statCard, textAlign: "center", padding: "48px 24px" }}>
                    <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>👆</div>
                    <div style={{ color: C.text2, fontSize: 16 }}>Selectează o zonă de pe remorcă</div>
                  </div>
                ) : (
                  <div style={S.statCard}>
                    {/* Zone header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
                      <div>
                        <h3 style={{ color: C.text, margin: 0, fontSize: 18 }}>{activeZoneData.label}</h3>
                        <div style={{ color: C.text2, fontSize: 13, marginTop: 2 }}>{activeZoneData.desc}</div>
                      </div>
                      <span style={S.badge(isZoneComplete(activeZoneData) ? C.green : zoneStatus[activeZone] === "partial" ? C.orange : C.text2)}>
                        {isZoneComplete(activeZoneData) ? "✓ Complet" : zoneStatus[activeZone] === "partial" ? "◐ Parțial" : "○ Necompletat"}
                      </span>
                    </div>

                    {/* Wheel data for wheel zones */}
                    {activeZoneData.isWheel && (
                      <div style={{ background: C.card2, borderRadius: 10, padding: 16, marginBottom: 16, border: `1px solid ${wheelConfirmed[activeZone] ? C.green : C.border}` }}>
                        {wheelSource && wheelData[activeZone]?.marca && (
                          <div style={{ fontSize: 11, color: C.accent, marginBottom: 8, padding: "4px 8px", background: "#eff6ff", borderRadius: 4 }}>
                            📋 {wheelSource} — verifică și confirmă
                          </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                          <h4 style={{ color: C.accent, margin: 0, fontSize: 15 }}>Date Anvelopă</h4>
                          {wheelConfirmed[activeZone] && <span style={S.badge(C.green)}>✓ Confirmat</span>}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          <div>
                            <label style={{ fontSize: 12, color: C.text2, fontWeight: 600 }}>Profil (mm) *</label>
                            <input style={S.input} inputMode="numeric" value={wheelData[activeZone]?.profil || ""} onChange={e => setWD(activeZone, "profil", e.target.value)} placeholder="Ex: 1110" />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, color: C.text2, fontWeight: 600 }}>Marcă *</label>
                            <input style={S.input} value={wheelData[activeZone]?.marca || ""} onChange={e => setWD(activeZone, "marca", e.target.value)} placeholder="Obligatoriu" />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, color: C.text2 }}>Serie</label>
                            <input style={S.input} value={wheelData[activeZone]?.serie || ""} onChange={e => setWD(activeZone, "serie", e.target.value)} placeholder="Opțional" />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, color: C.text2, fontWeight: 600 }}>Plăcuțe % *</label>
                            <input style={S.input} inputMode="numeric" value={wheelData[activeZone]?.placute || ""} onChange={e => setWD(activeZone, "placute", e.target.value)} placeholder="Ex: 70" />
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const wd = wheelData[activeZone];
                            if (!wd?.profil || !wd?.marca || !wd?.placute) { alert("Profil, Marcă și Plăcuțe sunt obligatorii!"); return; }
                            setWheelConfirmed(prev => ({ ...prev, [activeZone]: true }));
                          }}
                          style={{
                            ...S.btn, marginTop: 8, width: "100%",
                            background: wheelConfirmed[activeZone] ? C.green : C.accent,
                            color: "#fff", padding: "10px 20px",
                          }}>
                          {wheelConfirmed[activeZone] ? "✓ Date Confirmate" : "Confirmă Datele Anvelopei"}
                        </button>
                      </div>
                    )}

                    {/* Checks */}
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ color: C.accent, margin: "0 0 10px", fontSize: 14 }}>Verificări ({z_checksDone(checks[activeZone], activeZoneData)}/{activeZoneData.checks.length})</h4>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {activeZoneData.checks.map((check, idx) => {
                          const st = checks[activeZone]?.[idx]?.status;
                          const needsNote = st === "problem" && (!checks[activeZone]?.[idx]?.note || !checks[activeZone][idx].note.trim());
                          return (
                            <div key={idx} style={{ background: C.card2, borderRadius: 8, padding: "10px 14px", border: `1px solid ${st === "ok" ? C.green + "44" : st === "problem" ? C.red + "44" : C.border}` }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span className="lm-check-text" style={{ color: C.text, fontSize: 14, flex: 1, minWidth: 0, wordWrap: "break-word", overflowWrap: "break-word" }}>{check}</span>
                                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                  <button onClick={() => setCheck(activeZone, idx, "ok")} style={{
                                    ...S.btn, padding: "5px 16px", fontSize: 14,
                                    background: st === "ok" ? C.green : C.white,
                                    color: st === "ok" ? C.white : C.green,
                                    border: `2px solid ${C.green}`, fontWeight: 700,
                                  }}>✓</button>
                                  <button onClick={() => setCheck(activeZone, idx, "problem")} style={{
                                    ...S.btn, padding: "5px 16px", fontSize: 14,
                                    background: st === "problem" ? C.red : C.white,
                                    color: st === "problem" ? C.white : C.red,
                                    border: `2px solid ${C.red}`, fontWeight: 700,
                                  }}>✗</button>
                                </div>
                              </div>
                              {st === "problem" && (
                                <div style={{ marginTop: 8 }}>
                                  <input style={{ ...S.input, marginBottom: 0, borderColor: needsNote ? C.red : C.border }}
                                    placeholder="Descriere problemă (obligatoriu)..."
                                    value={checks[activeZone]?.[idx]?.note || ""}
                                    onChange={e => setCheckNote(activeZone, idx, e.target.value)} />
                                  {needsNote && <div style={{ color: C.red, fontSize: 11, marginTop: 4 }}>Descrierea este obligatorie pentru probleme!</div>}
                                  {/* Optional photo for problem */}
                                  {(() => {
                                    const pSlotId = `problem_${activeZone}_${idx}`;
                                    const pPhoto = photos[pSlotId];
                                    return (
                                      <div style={{ marginTop: 6 }}>
                                        {pPhoto?.preview ? (
                                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <img src={pPhoto.preview} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, border: `1px solid ${C.border}` }} />
                                            <span style={{ fontSize: 11, color: pPhoto.uploaded ? C.green : C.orange }}>{pPhoto.uploaded ? "✓ Încărcată" : uploading[pSlotId] ? "Se încarcă..." : "Eroare"}</span>
                                            <button onClick={() => setPhotos(prev => { const n = { ...prev }; delete n[pSlotId]; return n; })} style={{ ...S.btn, padding: "2px 8px", fontSize: 10, background: C.white, color: C.red, border: `1px solid ${C.red}` }}>✗</button>
                                          </div>
                                        ) : (
                                          <button onClick={() => {
                                            const inp = document.createElement("input");
                                            inp.type = "file"; inp.accept = "image/*"; inp.capture = "environment";
                                            inp.onchange = (ev) => {
                                              const file = ev.target.files[0];
                                              if (!file) return;
                                              const preview = URL.createObjectURL(file);
                                              setPhotos(prev => ({ ...prev, [pSlotId]: { file, preview, uploaded: false } }));
                                              handlePhotoSelect(activeZone, { id: pSlotId, label: `Problemă ${check}`, required: false }, file);
                                            };
                                            inp.click();
                                          }} style={{ ...S.btn, padding: "4px 12px", fontSize: 12, background: C.white, color: C.accent, border: `1px solid ${C.accent}` }}>
                                            📷 Adaugă poză (opțional)
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Ladă scule count - only for dotari_zona when check 2 is OK */}
                    {activeZoneData.dynamicPhotos && checks[activeZone]?.[2]?.status === "ok" && (
                      <div style={{ background: C.card2, borderRadius: 10, padding: 14, marginBottom: 16, border: `1px solid ${C.border}` }}>
                        <label style={{ fontSize: 13, color: C.text, fontWeight: 600, display: "block", marginBottom: 8 }}>Câte lăzi scule? </label>
                        <div style={{ display: "flex", gap: 8 }}>
                          {[1, 2].map(n => (
                            <button key={n} onClick={() => setLadaSculeCount(n)} style={{
                              ...S.btn, padding: "8px 24px",
                              background: ladaSculeCount === n ? C.accent : C.white,
                              color: ladaSculeCount === n ? "#fff" : C.text,
                              border: `2px solid ${ladaSculeCount === n ? C.accent : C.border}`,
                            }}>{n}</button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Scanduri — only for dotari_zona */}
                    {activeZone === "dotari_zona" && (
                      <div style={{ background: C.card2, borderRadius: 10, padding: 14, marginBottom: 16, border: `1px solid ${scanduri.present === null ? C.border : scanduri.present ? C.green + "44" : C.red + "44"}` }}>
                        <label style={{ fontSize: 14, color: C.text, fontWeight: 700, display: "block", marginBottom: 10 }}>Sunt scanduri?</label>
                        <div style={{ display: "flex", gap: 8, marginBottom: scanduri.present ? 12 : 0 }}>
                          <button onClick={() => setScanduri(p => ({ ...p, present: true }))} style={{
                            ...S.btn, padding: "8px 24px",
                            background: scanduri.present === true ? C.green : C.white,
                            color: scanduri.present === true ? "#fff" : C.green,
                            border: `2px solid ${C.green}`, fontWeight: 700,
                          }}>DA</button>
                          <button onClick={() => setScanduri({ present: false, lemn: 0, aluminiu: 0 })} style={{
                            ...S.btn, padding: "8px 24px",
                            background: scanduri.present === false ? C.red : C.white,
                            color: scanduri.present === false ? "#fff" : C.red,
                            border: `2px solid ${C.red}`, fontWeight: 700,
                          }}>NU</button>
                        </div>
                        {scanduri.present && (
                          <div>
                            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
                              <label style={{ fontSize: 13, color: C.text, fontWeight: 600, minWidth: 70 }}>Lemn:</label>
                              <input type="number" min="0" value={scanduri.lemn || ""} placeholder="0"
                                onChange={e => setScanduri(p => ({ ...p, lemn: parseInt(e.target.value) || 0 }))}
                                style={{ ...S.input, width: 80, marginBottom: 0, textAlign: "center" }} />
                              <span style={{ fontSize: 13, color: C.text2 }}>buc.</span>
                            </div>
                            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                              <label style={{ fontSize: 13, color: C.text, fontWeight: 600, minWidth: 70 }}>Aluminiu:</label>
                              <input type="number" min="0" value={scanduri.aluminiu || ""} placeholder="0"
                                onChange={e => setScanduri(p => ({ ...p, aluminiu: parseInt(e.target.value) || 0 }))}
                                style={{ ...S.input, width: 80, marginBottom: 0, textAlign: "center" }} />
                              <span style={{ fontSize: 13, color: C.text2 }}>buc.</span>
                            </div>
                            {(scanduri.lemn || 0) + (scanduri.aluminiu || 0) === 0 && (
                              <div style={{ color: C.red, fontSize: 11, marginTop: 6 }}>Cel puțin un tip de scandură trebuie să fie {'>'} 0!</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Photo upload */}
                    <div style={{ marginBottom: 16 }}>
                      {(() => {
                        const effectiveSlots = activeZoneData.dynamicPhotos ? getDynamicPhotoSlots(activeZoneData) : (activeZoneData.photoSlots || []);
                        return (
                          <>
                      <h4 style={{ color: C.accent, margin: "0 0 10px", fontSize: 14 }}>
                        Fotografii ({effectiveSlots.filter(s => photos[s.id]?.uploaded).length}/{effectiveSlots.length})
                      </h4>
                      {effectiveSlots.length === 0 && activeZoneData.dynamicPhotos && (
                        <div style={{ color: C.text2, fontSize: 13, padding: "8px 0" }}>Bifează OK la elementele de mai sus pentru a adăuga poze.</div>
                      )}
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {effectiveSlots.map(slot => {
                          const photo = photos[slot.id];
                          const isUploading = uploading[slot.id];
                          return (
                            <div key={slot.id} style={{ position: "relative" }}>
                              <div
                                onClick={() => {
                                  if (!fileInputRefs.current[slot.id]) {
                                    const input = document.createElement("input");
                                    input.type = "file";
                                    input.accept = "image/*";
                                    input.capture = "environment";
                                    input.onchange = (e) => handlePhotoSelect(activeZone, slot, e.target.files[0]);
                                    fileInputRefs.current[slot.id] = input;
                                  }
                                  fileInputRefs.current[slot.id].click();
                                }}
                                className="lm-photo-slot"
                                style={{
                                  width: 110, height: 90, borderRadius: 8, overflow: "hidden", cursor: "pointer",
                                  border: photo?.uploaded ? `2px solid ${C.green}` : `2px dashed ${slot.required ? C.accent : C.border}`,
                                  background: photo?.preview ? `url(${photo.preview}) center/cover` : photo?.uploaded ? C.green + "22" : C.card2,
                                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                  position: "relative",
                                }}>
                                {!photo && (
                                  <>
                                    <div style={{ fontSize: 24, opacity: 0.5 }}>📷</div>
                                    <div style={{ fontSize: 10, color: C.text2, textAlign: "center", padding: "0 4px", marginTop: 2 }}>{slot.label}</div>
                                    {slot.required && <div style={{ fontSize: 9, color: C.red, marginTop: 1 }}>obligatoriu</div>}
                                  </>
                                )}
                                {photo?.uploaded && !photo?.preview && (
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                    <div style={{ fontSize: 20 }}>✅</div>
                                    <div style={{ fontSize: 9, color: C.green, fontWeight: 600, marginTop: 2 }}>Salvată</div>
                                  </div>
                                )}
                                {isUploading && (
                                  <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: C.accent, fontWeight: 600 }}>
                                    Se urcă...
                                  </div>
                                )}
                                {photo?.uploaded && (
                                  <div style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: C.green, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>✓</div>
                                )}
                                {photo?.error && (
                                  <div onClick={(e) => { e.stopPropagation(); if (photo?.file) handlePhotoSelect(activeZone, slot, photo.file); }} style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: C.red, color: "#fff", fontSize: 9, textAlign: "center", padding: "3px 2px", cursor: "pointer" }}>{photo.error.includes("Timeout") ? "Timeout — apasă pt retry" : photo.error.includes("prea mare") ? "Poza prea mare" : "Eroare — apasă pt retry"}</div>
                                )}
                              </div>
                              <div style={{ fontSize: 10, color: C.text2, textAlign: "center", marginTop: 3, maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{slot.label}</div>
                            </div>
                          );
                        })}
                      </div>
                          </>
                        );
                      })()}
                    </div>

                    {/* Mentions */}
                    <div>
                      <label style={{ fontSize: 13, color: C.text2, display: "block", marginBottom: 4, fontWeight: 600 }}>Mențiuni suplimentare</label>
                      <textarea style={{ ...S.input, minHeight: 56, resize: "vertical" }} placeholder="Observații pentru această zonă..." value={mentions[activeZone] || ""} onChange={e => setMentions(prev => ({ ...prev, [activeZone]: e.target.value }))} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Incomplete zones list */}
            {incompleteZones.length > 0 && (
              <div style={{ ...S.statCard, marginTop: 24, borderColor: incompleteZones.length === totalZones ? C.border : C.orange + "66" }}>
                <h4 style={{ color: incompleteZones.length === totalZones ? C.text2 : C.orange, margin: "0 0 12px", fontSize: 14 }}>
                  Zone incomplete ({incompleteZones.length})
                </h4>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {incompleteZones.map(z => {
                    const missing = getZoneMissing(z);
                    return (
                      <button key={z.id} onClick={() => { setActiveZone(z.id); setTimeout(() => { if (zoneDetailRef.current) zoneDetailRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); }, 100); }} style={{
                        ...S.btn, padding: "6px 14px", fontSize: 12,
                        background: C.card2, color: C.text, border: `1px solid ${C.border}`,
                        textAlign: "left", display: "flex", flexDirection: "column", gap: 2,
                      }}>
                        <span style={{ fontWeight: 600 }}>{z.label}</span>
                        {missing.length > 0 && <span style={{ fontSize: 10, color: C.red }}>{missing.join(", ")}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Continue button */}
            <div ref={finalizeRef} style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => setStep(0)}>← Transportator</button>
              <button
                disabled={!allZonesComplete}
                onClick={() => setStep(2)}
                style={{
                  ...S.btn, ...S.btnPrimary,
                  opacity: allZonesComplete ? 1 : 0.4,
                  cursor: allZonesComplete ? "pointer" : "not-allowed",
                  padding: "12px 32px",
                }}>
                {allZonesComplete ? "Continuă la Semnătură →" : `Încă ${incompleteZones.length} zone incomplete`}
              </button>
            </div>
          </div>
        )}

        {/* ═══ STEP 2: SIGNATURE & FINALIZE ═══ */}
        {step === 2 && (
          <div>
            {/* Review summary */}
            <div style={{ ...S.statCard, marginBottom: 20 }}>
              <h3 style={{ color: C.text, marginBottom: 16, fontSize: 18 }}>Rezumat {pvType === "predare" ? "PV Predare" : pvType === "inspectie" ? "Inspecție" : "PV Returnare"}</h3>
              <div style={S.infoGrid}>
                {!isInspectie && <div style={S.infoItem}><div style={S.infoLabel}>Transportator</div><div style={S.infoVal}>{clientName}</div></div>}
                <div style={S.infoItem}><div style={S.infoLabel}>Remorcă</div><div style={S.infoVal}>{trailer.numar_auto}</div></div>
                <div style={S.infoItem}><div style={S.infoLabel}>Tip</div><div style={S.infoVal}>{pvType === "predare" ? "Predare" : pvType === "inspectie" ? "Inspecție" : "Returnare"}</div></div>
                <div style={S.infoItem}><div style={S.infoLabel}>Data</div><div style={S.infoVal}>{new Date().toLocaleDateString("ro-RO")}</div></div>
              </div>

              {/* Zone summary */}
              <h4 style={{ color: C.accent, marginTop: 20, marginBottom: 10, fontSize: 14 }}>Zone Inspecție</h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
                {PV_ZONES.map(z => {
                  const zc = checks[z.id] || {};
                  const problems = Object.values(zc).filter(c => c.status === "problem");
                  const okCount = Object.values(zc).filter(c => c.status === "ok").length;
                  const zSlots = z.dynamicPhotos ? getDynamicPhotoSlots(z) : (z.photoSlots || []);
                  const photoCount = zSlots.filter(s => photos[s.id]?.uploaded).length;
                  return (
                    <div key={z.id} style={{ ...S.infoItem, padding: "10px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>{z.label}</span>
                        <div style={{ display: "flex", gap: 4 }}>
                          <span style={{ ...S.badge(C.green), fontSize: 10, padding: "2px 8px" }}>{okCount}✓</span>
                          {problems.length > 0 && <span style={{ ...S.badge(C.red), fontSize: 10, padding: "2px 8px" }}>{problems.length}✗</span>}
                          <span style={{ ...S.badge(C.accent), fontSize: 10, padding: "2px 8px" }}>{photoCount}📷</span>
                        </div>
                      </div>
                      {problems.map((p, i) => (
                        <div key={i} style={{ color: C.red, fontSize: 12, marginTop: 3, paddingLeft: 8 }}>• {p.label}{p.note ? ` — ${p.note}` : ""}</div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Driver info — only for predare/returnare */}
            {!isInspectie && (
              <div style={{ ...S.statCard, marginBottom: 16 }}>
                <h3 style={{ color: C.text, marginBottom: 12, fontSize: 18 }}>Date Șofer</h3>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: C.text2, marginBottom: 4 }}>Nume și prenume șofer <span style={{ color: C.red }}>*</span></label>
                  <input value={driverName} onChange={e => setDriverName(e.target.value)} placeholder="ex: Popescu Ion" style={{ width: "100%", padding: "14px 16px", fontSize: 18, borderRadius: 10, border: `2px solid ${driverName.trim() ? C.green : C.border}`, background: C.card, color: C.text, outline: "none" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: C.text2, marginBottom: 4 }}>Serie și număr C.I. <span style={{ color: C.red }}>*</span></label>
                  <input value={driverIdCard} onChange={e => setDriverIdCard(e.target.value)} placeholder="ex: RX 123456" style={{ width: "100%", padding: "14px 16px", fontSize: 18, borderRadius: 10, border: `2px solid ${driverIdCard.trim() ? C.green : C.border}`, background: C.card, color: C.text, outline: "none", textTransform: "uppercase" }} />
                </div>
                {(!driverName.trim() || !driverIdCard.trim()) && <div style={{ color: C.orange, fontSize: 13, marginTop: 8 }}>Completați datele șoferului pentru a putea finaliza PV-ul.</div>}
              </div>
            )}

            {/* Document validity — only for financing vehicles */}
            {isFinancing && !isInspectie && (
              <div style={{ ...S.statCard, marginBottom: 16 }}>
                <h3 style={{ color: C.text, marginBottom: 4, fontSize: 18 }}>Documente și Valabilitate</h3>
                <p style={{ color: C.text2, fontSize: 13, marginBottom: 12 }}>Completați starea documentelor la data predării vehiculului.</p>
                {[
                  { key: 'rca', expKey: 'rcaExp', label: 'Asigurare RCA valabilă' },
                  { key: 'rov', expKey: 'rovExp', label: 'Rovinietă valabilă' },
                  { key: 'itp', expKey: 'itpExp', label: 'ITP valabil' },
                ].map(doc => (
                  <div key={doc.key} style={{ background: C.card2, borderRadius: 10, padding: 12, marginBottom: 10, border: `1px solid ${docVal[doc.key] === null ? C.border : docVal[doc.key] ? C.green + "44" : C.red + "44"}` }}>
                    <label style={{ fontSize: 14, color: C.text, fontWeight: 700, display: "block", marginBottom: 8 }}>{doc.label}</label>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <button onClick={() => setDocVal(p => ({ ...p, [doc.key]: true }))} style={{
                        ...S.btn, padding: "8px 24px",
                        background: docVal[doc.key] === true ? C.green : C.white,
                        color: docVal[doc.key] === true ? "#fff" : C.green,
                        border: `2px solid ${C.green}`, fontWeight: 700,
                      }}>DA</button>
                      <button onClick={() => setDocVal(p => ({ ...p, [doc.key]: false, [doc.expKey]: '' }))} style={{
                        ...S.btn, padding: "8px 24px",
                        background: docVal[doc.key] === false ? C.red : C.white,
                        color: docVal[doc.key] === false ? "#fff" : C.red,
                        border: `2px solid ${C.red}`, fontWeight: 700,
                      }}>NU</button>
                      {docVal[doc.key] === true && (
                        <input type="date" value={docVal[doc.expKey]} onChange={e => setDocVal(p => ({ ...p, [doc.expKey]: e.target.value }))}
                          style={{ padding: "8px 12px", fontSize: 14, borderRadius: 8, border: `2px solid ${docVal[doc.expKey] ? C.green : C.border}`, background: C.card, color: C.text, outline: "none" }} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Signature */}
            <div style={{ ...S.statCard }}>
              <h3 style={{ color: C.text, marginBottom: 4, fontSize: 18 }}>Semnătură</h3>
              <p style={{ color: C.text2, fontSize: 13, marginBottom: 16 }}>Semnați mai jos cu degetul sau mouse-ul pentru a confirma inspecția.</p>

              <div style={{ position: "relative", marginBottom: 16 }}>
                <canvas
                  ref={initSigCanvas}
                  style={{
                    width: "100%", height: 180, borderRadius: 10,
                    border: `2px solid ${signatureData ? C.green : C.border}`,
                    background: "#fff", cursor: "crosshair", touchAction: "none",
                  }}
                />
                <button onClick={clearSignature} style={{
                  position: "absolute", top: 8, right: 8,
                  ...S.btn, padding: "4px 12px", fontSize: 12,
                  background: C.card2, color: C.text2, border: `1px solid ${C.border}`,
                }}>Șterge</button>
              </div>

              {!signatureData && <div style={{ color: C.orange, fontSize: 13, marginBottom: 12 }}>Semnătura este obligatorie pentru finalizare.</div>}

              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => setStep(1)}>← Înapoi la Inspecție</button>
                <button
                  disabled={saving || !signatureData || (!isInspectie && (!driverName.trim() || !driverIdCard.trim()))}
                  onClick={handleSave}
                  style={{
                    ...S.btn, padding: "14px 36px", fontSize: 16,
                    background: signatureData && (isInspectie || (driverName.trim() && driverIdCard.trim())) ? C.green : C.border,
                    color: "#fff", opacity: saving ? 0.6 : 1,
                    cursor: signatureData && !saving && (isInspectie || (driverName.trim() && driverIdCard.trim())) ? "pointer" : "not-allowed",
                  }}>
                  {saving ? "Se finalizează..." : "Finalizează PV"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* helper for check count */
function z_checksDone(zc, zoneData) {
  if (!zc || !zoneData) return 0;
  return zoneData.checks.filter((_, idx) => zc[idx]?.status).length;
}

/* ═══════════════════════════════════════════════════════════
   REPORTS PAGE
   ═══════════════════════════════════════════════════════════ */
function ReportsPage({ onSelectTrailer }) {
  const [tab, setTab] = useState("summary");
  const [summaryData, setSummaryData] = useState(null);
  const [trailersData, setTrailersData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [tiresData, setTiresData] = useState([]);
  const [costsData, setCostsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stareFilter, setStareFilter] = useState("Toate");
  const [searchFilter, setSearchFilter] = useState("");
  const [tiresFilter, setTiresFilter] = useState("all"); // all, sub50, sub30
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const loadTab = useCallback(async (t) => {
    setLoading(true);
    try {
      switch (t) {
        case "summary": {
          const data = await api.get("/api/reports/fleet-summary");
          setSummaryData(data);
          break;
        }
        case "trailers": {
          const qs = new URLSearchParams();
          if (stareFilter !== "Toate") qs.set("stare", stareFilter);
          if (searchFilter) qs.set("search", searchFilter);
          const data = await api.get(`/api/reports/trailers-status?${qs}`);
          setTrailersData(data);
          break;
        }
        case "clients": {
          const data = await api.get("/api/reports/clients");
          setClientsData(data);
          break;
        }
        case "tires": {
          const data = await api.get("/api/reports/tires-wear");
          setTiresData(data);
          break;
        }
        case "costs": {
          const data = await api.get("/api/reports/costs");
          setCostsData(data);
          break;
        }
        default: break;
      }
    } catch (ex) { console.error("Report load error:", ex); }
    setLoading(false);
  }, [stareFilter, searchFilter]);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const handleExport = async (type) => {
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`/api/reports/export/${type}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (ex) { alert("Eroare export: " + ex.message); }
  };

  const doSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };
  const sortIcon = (col) => sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const sortRows = (rows, col, dir) => {
    if (!col) return rows;
    return [...rows].sort((a, b) => {
      const va = a[col] ?? 0, vb = b[col] ?? 0;
      if (typeof va === "number" && typeof vb === "number") return dir === "asc" ? va - vb : vb - va;
      return dir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  };

  const pctBadge = (pct) => {
    if (pct === 0 || pct === null) return null;
    const positive = pct > 0;
    return (
      <span style={{ fontSize: 11, fontWeight: 600, color: positive ? C.green : C.red, marginLeft: 6 }}>
        {positive ? "+" : ""}{pct}%
      </span>
    );
  };

  // SVG bar chart helper
  const BarChart = ({ data }) => {
    if (!data || data.length === 0) return null;
    const maxVal = Math.max(...data.map(d => Math.max(d.pvs || 0, d.service_orders || 0)), 1);
    const W = 540, H = 180, PY = 20, PX = 36;
    const barW = Math.min(28, (W - PX * 2) / data.length / 2.5);
    const gap = (W - PX * 2) / data.length;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: 540, height: "auto" }}>
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
          const y = PY + (1 - f) * (H - PY * 2 - 20);
          return <g key={i}><line x1={PX} y1={y} x2={W - PX} y2={y} stroke={C.border} strokeWidth="0.5" /><text x={PX - 4} y={y + 3} textAnchor="end" fill={C.text2} fontSize="8">{Math.round(maxVal * f)}</text></g>;
        })}
        {data.map((d, i) => {
          const x = PX + i * gap + gap / 2;
          const hPv = ((d.pvs || 0) / maxVal) * (H - PY * 2 - 20);
          const hSo = ((d.service_orders || 0) / maxVal) * (H - PY * 2 - 20);
          const baseY = H - PY - 20;
          const month = new Date(d.month).toLocaleDateString("ro-RO", { month: "short" });
          return (
            <g key={i}>
              <rect x={x - barW - 1} y={baseY - hPv} width={barW} height={hPv} fill={C.accent} rx="3" opacity="0.85" />
              <rect x={x + 1} y={baseY - hSo} width={barW} height={hSo} fill={C.orange} rx="3" opacity="0.85" />
              <text x={x} y={H - 6} textAnchor="middle" fill={C.text2} fontSize="8">{month}</text>
            </g>
          );
        })}
        {/* Legend */}
        <rect x={W - 140} y={4} width={10} height={10} fill={C.accent} rx="2" />
        <text x={W - 126} y={13} fill={C.text2} fontSize="8">PV-uri</text>
        <rect x={W - 80} y={4} width={10} height={10} fill={C.orange} rx="2" />
        <text x={W - 66} y={13} fill={C.text2} fontSize="8">Service</text>
      </svg>
    );
  };

  const tabs = [
    { id: "summary", label: "Rezumat Flotă" },
    { id: "trailers", label: "Status Remorci" },
    { id: "clients", label: "Transportatori" },
    { id: "tires", label: "Anvelope" },
    { id: "costs", label: "Costuri" },
  ];

  const exportMap = { summary: "fleet-summary", trailers: "trailers-status", clients: "clients", tires: "tires-wear", costs: "costs" };

  return (
    <div>
      <div className="lm-page-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text }}>Rapoarte</h2>
        <button className="lm-btn" style={{ ...S.btn, background: C.green, color: C.white, fontSize: 13, padding: "8px 16px" }}
          onClick={() => handleExport(exportMap[tab])}>
          Export CSV
        </button>
      </div>

      <div className="lm-tabs" style={S.tabs}>
        {tabs.map(t => (
          <button key={t.id} style={{ ...S.tab, ...(tab === t.id ? S.tabActive : {}) }} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {loading && <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Se încarcă...</div>}

      {/* ═══ TAB: REZUMAT FLOTA ═══ */}
      {!loading && tab === "summary" && summaryData && (() => {
        const d = summaryData;
        return (
          <div>
            {/* 4 summary cards */}
            <div className="lm-stat-cards" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
              <div style={S.statCard}>
                <div style={{ fontSize: 11, color: C.text2, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Remorci</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: C.text, marginTop: 4 }}>{d.trailers.total}</div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 4 }}>
                  {d.trailers.active} active · {d.trailers.inchiriate} închiriate · {d.trailers.service} service
                </div>
              </div>
              <div style={S.statCard}>
                <div style={{ fontSize: 11, color: C.text2, textTransform: "uppercase", letterSpacing: 0.5 }}>KM Luna Asta</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: C.accent, marginTop: 4 }}>
                  {(d.km.this_month || 0).toLocaleString()}
                  {pctBadge(d.km.change_pct)}
                </div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 4 }}>Luna trecută: {(d.km.last_month || 0).toLocaleString()}</div>
              </div>
              <div style={S.statCard}>
                <div style={{ fontSize: 11, color: C.text2, textTransform: "uppercase", letterSpacing: 0.5 }}>PV-uri Luna Asta</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: C.green, marginTop: 4 }}>
                  {d.pvs.c}
                  {pctBadge(d.pvs.change_pct)}
                </div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 4 }}>
                  {d.pvs.predari} predări · {d.pvs.returnari} returnări · {d.pvs.inspectii} inspecții
                </div>
              </div>
              <div style={S.statCard}>
                <div style={{ fontSize: 11, color: C.text2, textTransform: "uppercase", letterSpacing: 0.5 }}>Service Luna Asta</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: C.orange, marginTop: 4 }}>
                  {d.service.c}
                  {pctBadge(d.service.change_pct)}
                </div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 4 }}>
                  {d.service.completate} complete · {d.service.in_progres} în progres · {d.service.pending} pending
                </div>
              </div>
            </div>

            {/* Activity chart */}
            <div style={{ ...S.statCard, padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 12 }}>Activitate Lunară (6 luni)</div>
              <div className="lm-chart-wrap"><BarChart data={d.monthly_activity} /></div>
            </div>
          </div>
        );
      })()}

      {/* ═══ TAB: STATUS REMORCI ═══ */}
      {!loading && tab === "trailers" && (() => {
        return (
          <div>
            {/* Filters */}
            <div className="lm-filters" style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
              <select style={{ ...S.input, width: "auto", marginBottom: 0, minWidth: 140 }}
                value={stareFilter} onChange={e => setStareFilter(e.target.value)}>
                {["Toate", "Activă", "Închiriată", "În service", "De vânzare", "Scoasă din uz"].map(s => <option key={s}>{s}</option>)}
              </select>
              <input style={{ ...S.input, width: "auto", marginBottom: 0, flex: 1, minWidth: 160 }}
                placeholder="Caută nr. auto..." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
              <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, padding: "10px 18px" }} onClick={() => loadTab("trailers")}>Filtrează</button>
            </div>

            {/* Table - desktop */}
            <div className="lm-desktop-table" style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Nr Auto</th>
                    <th style={S.th}>Stare</th>
                    <th style={S.th}>Client</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("km_actuali")}>KM{sortIcon("km_actuali")}</th>
                    <th style={S.th}>Ultima Activitate</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("zile_fara_pv")}>Zile fără PV{sortIcon("zile_fara_pv")}</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("probleme_deschise")}>Probleme{sortIcon("probleme_deschise")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortRows(trailersData, sortCol, sortDir).map(r => {
                    const warn = r.zile_fara_pv > 90;
                    return (
                      <tr key={r.id} style={{ cursor: "pointer", ...(warn ? { background: "#fef2f2" } : {}) }}
                        onClick={() => onSelectTrailer && onSelectTrailer(r.id)}>
                        <td style={{ ...S.td, fontWeight: 700, color: C.text, borderRadius: "8px 0 0 8px" }}>{r.numar_auto}</td>
                        <td style={S.td}><span style={S.badge(stareColor(r.stare))}>{r.stare}</span></td>
                        <td style={S.td}>{r.client_name || "Nealocat"}</td>
                        <td style={S.td}>{(r.km_actuali || 0).toLocaleString()}</td>
                        <td style={S.td}>{r.ultima_activitate ? fmtDT(r.ultima_activitate) : "—"}</td>
                        <td style={{ ...S.td, color: warn ? C.red : C.text, fontWeight: warn ? 700 : 400 }}>{r.zile_fara_pv || 0}</td>
                        <td style={{ ...S.td, borderRadius: "0 8px 8px 0" }}>
                          {r.probleme_deschise > 0 ? <span style={S.badge(C.red)}>{r.probleme_deschise}</span> : <span style={{ color: C.text2 }}>0</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {trailersData.length === 0 && <div style={{ textAlign: "center", padding: 32, color: C.text2 }}>Nicio remorcă găsită.</div>}
            </div>
            {/* Mobile cards */}
            <div className="lm-mobile-cards" style={{ display: "none", flexDirection: "column", gap: 8 }}>
              {sortRows(trailersData, sortCol, sortDir).map(r => {
                const warn = r.zile_fara_pv > 90;
                return (
                  <div key={r.id} style={{ ...S.statCard, padding: 14, cursor: "pointer", ...(warn ? { borderLeft: `3px solid ${C.red}` } : {}) }}
                    onClick={() => onSelectTrailer && onSelectTrailer(r.id)}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{r.numar_auto}</span>
                      <span style={S.badge(stareColor(r.stare))}>{r.stare}</span>
                    </div>
                    <div style={{ fontSize: 13, color: C.text2, marginBottom: 4 }}>{r.client_name || "Nealocat"} · {(r.km_actuali || 0).toLocaleString()} km</div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.muted }}>
                      <span>{r.ultima_activitate ? fmtDT(r.ultima_activitate) : "—"}</span>
                      <span style={{ color: warn ? C.red : C.text2, fontWeight: warn ? 700 : 400 }}>{r.zile_fara_pv || 0}z fără PV</span>
                      {r.probleme_deschise > 0 && <span style={S.badge(C.red)}>{r.probleme_deschise} prob.</span>}
                    </div>
                  </div>
                );
              })}
              {trailersData.length === 0 && <div style={{ textAlign: "center", padding: 32, color: C.text2 }}>Nicio remorcă găsită.</div>}
            </div>
          </div>
        );
      })()}

      {/* ═══ TAB: TRANSPORTATORI ═══ */}
      {!loading && tab === "clients" && (() => {
        const scorColor = (s) => s > 50 ? C.red : s > 20 ? C.yellow : C.green;
        const sorted = sortRows(clientsData, sortCol, sortDir);
        return (
          <div>
            <div className="lm-desktop-table" style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("client_name")}>Client{sortIcon("client_name")}</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("remorci_active")}>Remorci Active{sortIcon("remorci_active")}</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("total_km")}>Total KM{sortIcon("total_km")}</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("total_pvs")}>PV-uri{sortIcon("total_pvs")}</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("diferente")}>Diferențe{sortIcon("diferente")}</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("service_orders")}>Service{sortIcon("service_orders")}</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("cost_estimativ_diferente")}>Cost Est.{sortIcon("cost_estimativ_diferente")}</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("scor_grija")}>Scor Grijă{sortIcon("scor_grija")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r, i) => (
                    <tr key={i}>
                      <td style={{ ...S.td, fontWeight: 700, color: C.text, borderRadius: "8px 0 0 8px" }}>{r.client_name}</td>
                      <td style={S.td}>{r.remorci_active}</td>
                      <td style={S.td}>{(r.total_km || 0).toLocaleString()}</td>
                      <td style={S.td}>{r.total_pvs}</td>
                      <td style={S.td}>{r.diferente > 0 ? <span style={S.badge(C.red)}>{r.diferente}</span> : "0"}</td>
                      <td style={S.td}>{r.service_orders}</td>
                      <td style={S.td}>{parseFloat(r.cost_estimativ_diferente) > 0 ? <span style={{ fontWeight: 600, color: "#f59e0b" }}>{fmtLei(r.cost_estimativ_diferente)}</span> : "—"}</td>
                      <td style={{ ...S.td, borderRadius: "0 8px 8px 0" }}>
                        <span style={S.badge(scorColor(r.scor_grija))}>{r.scor_grija}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {clientsData.length === 0 && <div style={{ textAlign: "center", padding: 32, color: C.text2 }}>Niciun transportator găsit.</div>}
            </div>
            {/* Mobile cards */}
            <div className="lm-mobile-cards" style={{ display: "none", flexDirection: "column", gap: 8 }}>
              {sorted.map((r, i) => (
                <div key={i} style={{ ...S.statCard, padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{r.client_name}</span>
                    <span style={S.badge(scorColor(r.scor_grija))}>{r.scor_grija}% grijă</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, fontSize: 12, color: C.text2 }}>
                    <div><strong>{r.remorci_active}</strong> remorci</div>
                    <div><strong>{(r.total_km || 0).toLocaleString()}</strong> km</div>
                    <div><strong>{r.total_pvs}</strong> PV-uri</div>
                  </div>
                  {r.diferente > 0 && <div style={{ fontSize: 12, color: C.red, marginTop: 4 }}>{r.diferente} diferențe · {r.service_orders} service{parseFloat(r.cost_estimativ_diferente) > 0 ? " · Cost: " + fmtLei(r.cost_estimativ_diferente) : ""}</div>}
                </div>
              ))}
              {clientsData.length === 0 && <div style={{ textAlign: "center", padding: 32, color: C.text2 }}>Niciun transportator găsit.</div>}
            </div>
          </div>
        );
      })()}

      {/* ═══ TAB: ANVELOPE ═══ */}
      {!loading && tab === "tires" && (() => {
        let filtered = tiresData;
        if (tiresFilter === "excessive") filtered = tiresData.filter(t => t.last_wear_status === "excessive");
        else if (tiresFilter === "sub30") filtered = tiresData.filter(t => (t.placute || 0) < 30);
        else if (tiresFilter === "sub50") filtered = tiresData.filter(t => (t.placute || 0) < 50);
        const sorted = sortRows(filtered, sortCol, sortDir);
        const placuteColor = (p) => p < 30 ? C.red : p < 50 ? C.yellow : C.green;
        const excessiveCount = tiresData.filter(t => t.last_wear_status === "excessive").length;
        return (
          <div>
            <div className="lm-filter-btns" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {[{ id: "all", label: "Toate" }, { id: "excessive", label: `Uzură Excesivă (${excessiveCount})` }, { id: "sub50", label: "Sub 50%" }, { id: "sub30", label: "Sub 30%" }].map(f => (
                <button key={f.id} className="lm-btn" style={{
                  ...S.btn, padding: "8px 16px", fontSize: 13,
                  background: tiresFilter === f.id ? (f.id === "excessive" ? C.red : C.accent) : "#f0f2f5",
                  color: tiresFilter === f.id ? "#fff" : C.text,
                }} onClick={() => setTiresFilter(f.id)}>{f.label}</button>
              ))}
            </div>
            <div className="lm-desktop-table" style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Remorcă</th>
                    <th style={S.th}>Poziție</th>
                    <th style={S.th}>Marcă</th>
                    <th style={S.th}>Profil</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("placute")}>Plăcuțe %{sortIcon("placute")}</th>
                    <th style={S.th}>Evaluare</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("km_actuali")}>KM Total{sortIcon("km_actuali")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r, i) => {
                    const p = r.placute || 0;
                    const isExcessive = r.last_wear_status === "excessive";
                    return (
                      <tr key={i} style={{ background: isExcessive ? "#fef2f2" : p < 30 ? "#fef2f2" : p < 50 ? "#fffbeb" : undefined }}>
                        <td style={{ ...S.td, fontWeight: 700, color: C.text, borderRadius: "8px 0 0 8px", cursor: "pointer" }}
                          onClick={() => onSelectTrailer && onSelectTrailer(r.trailer_id)}>{r.numar_auto}</td>
                        <td style={S.td}>{r.pozitie}</td>
                        <td style={S.td}>{r.marca || "—"}</td>
                        <td style={S.td}>{r.profil || "—"}</td>
                        <td style={S.td}><span style={S.badge(placuteColor(p))}>{p}%</span></td>
                        <td style={S.td}>{isExcessive
                          ? <span style={{ ...S.badge(C.red), fontSize: 10, padding: "2px 8px" }}>🔴 EXCESIVĂ</span>
                          : <span style={{ ...S.badge(C.green), fontSize: 10, padding: "2px 8px" }}>🟢 OK</span>
                        }</td>
                        <td style={{ ...S.td, borderRadius: "0 8px 8px 0" }}>{(r.km_actuali || 0).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {sorted.length === 0 && <div style={{ textAlign: "center", padding: 32, color: C.text2 }}>Nicio anvelopă găsită.</div>}
            </div>
            {/* Mobile cards */}
            <div className="lm-mobile-cards" style={{ display: "none", flexDirection: "column", gap: 8 }}>
              {sorted.map((r, i) => {
                const p = r.placute || 0;
                const isExcessive = r.last_wear_status === "excessive";
                return (
                  <div key={i} style={{ ...S.statCard, padding: 14, cursor: "pointer", borderLeft: `3px solid ${isExcessive ? C.red : placuteColor(p)}` }}
                    onClick={() => onSelectTrailer && onSelectTrailer(r.trailer_id)}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{r.numar_auto}</span>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {isExcessive && <span style={{ fontSize: 11, color: C.red, fontWeight: 700 }}>🔴 EXCESIVĂ</span>}
                        <span style={S.badge(placuteColor(p))}>{p}%</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: C.text2 }}>{r.pozitie} · {r.marca || "—"} · {r.profil || "—"}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{(r.km_actuali || 0).toLocaleString()} km</div>
                  </div>
                );
              })}
              {sorted.length === 0 && <div style={{ textAlign: "center", padding: 32, color: C.text2 }}>Nicio anvelopă găsită.</div>}
            </div>
          </div>
        );
      })()}

      {/* ═══ TAB: COSTURI ═══ */}
      {!loading && tab === "costs" && costsData && (() => {
        const d = costsData;
        const top5 = [...d.per_trailer].sort((a, b) => b.total_cost_all - a.total_cost_all).slice(0, 5);
        const top5Ids = new Set(top5.map(t => t.id));

        // SVG line chart for monthly costs
        const LineChart = ({ data }) => {
          if (!data || data.length < 2) return <div style={{ textAlign: "center", padding: 20, color: C.text2, fontSize: 13 }}>Insuficiente date pentru grafic</div>;
          const values = data.map(d => d.total);
          const maxV = Math.max(...values, 1);
          const W = 540, H = 180, PX = 50, PY = 20;
          const points = data.map((d, i) => {
            const x = PX + (i / (data.length - 1)) * (W - PX * 2);
            const y = PY + (1 - d.total / maxV) * (H - PY * 2 - 20);
            return { x, y, total: d.total, month: new Date(d.month).toLocaleDateString("ro-RO", { month: "short" }) };
          });
          const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
          const areaPath = linePath + ` L${points[points.length - 1].x},${H - PY - 20} L${points[0].x},${H - PY - 20} Z`;
          return (
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: 540, height: "auto" }}>
              {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
                const y = PY + (1 - f) * (H - PY * 2 - 20);
                const val = Math.round(maxV * f);
                return <g key={i}><line x1={PX} y1={y} x2={W - PX} y2={y} stroke={C.border} strokeWidth="0.5" /><text x={PX - 4} y={y + 3} textAnchor="end" fill={C.text2} fontSize="7">{val.toLocaleString()}</text></g>;
              })}
              <path d={areaPath} fill={C.accent + "15"} />
              <path d={linePath} fill="none" stroke={C.accent} strokeWidth="2.5" strokeLinejoin="round" />
              {points.map((p, i) => (
                <g key={i}>
                  <circle cx={p.x} cy={p.y} r="4" fill={C.accent} stroke="#fff" strokeWidth="1.5" />
                  <text x={p.x} y={H - 4} textAnchor="middle" fill={C.text2} fontSize="7">{p.month}</text>
                </g>
              ))}
            </svg>
          );
        };

        return (
          <div>
            {/* Summary card */}
            <div className="lm-stat-cards" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 24 }}>
              <div style={S.statCard}>
                <div style={{ fontSize: 11, color: C.text2, textTransform: "uppercase" }}>Cost Total Luna Asta</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: C.accent, marginTop: 4 }}>
                  {fmtLei(d.this_month)}
                  {pctBadge(d.change_pct)}
                </div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 4 }}>Luna trecută: {fmtLei(d.last_month)}</div>
              </div>
              <div style={S.statCard}>
                <div style={{ fontSize: 11, color: C.text2, textTransform: "uppercase" }}>Total 12 Luni</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: C.text, marginTop: 4 }}>{fmtLei(d.monthly.reduce((s, m) => s + m.total, 0))}</div>
              </div>
              <div style={S.statCard}>
                <div style={{ fontSize: 11, color: C.text2, textTransform: "uppercase" }}>Medie / Lună</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: C.text, marginTop: 4 }}>{fmtLei(d.monthly.reduce((s, m) => s + m.total, 0) / Math.max(d.monthly.filter(m => m.total > 0).length, 1))}</div>
              </div>
            </div>

            {/* Monthly chart */}
            <div style={{ ...S.statCard, padding: 20, marginBottom: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 12 }}>Evoluție Costuri (12 luni)</div>
              <div className="lm-chart-wrap"><LineChart data={d.monthly} /></div>
            </div>

            {/* Top 5 expensive */}
            {top5.filter(t => t.total_cost_all > 0).length > 0 && (
              <div style={{ ...S.statCard, marginBottom: 24 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: C.red, marginBottom: 10 }}>Top 5 Cele Mai Costisitoare Remorci</h3>
                {top5.filter(t => t.total_cost_all > 0).map((t, i) => (
                  <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                    <div>
                      <span style={{ fontWeight: 800, color: C.text, fontSize: 16, marginRight: 8 }}>#{i + 1}</span>
                      <span style={{ fontWeight: 700, color: C.text, cursor: "pointer" }} onClick={() => onSelectTrailer && onSelectTrailer(t.id)}>{t.numar_auto}</span>
                      <span style={{ color: C.text2, fontSize: 12, marginLeft: 8 }}>{t.marca}</span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontWeight: 800, color: C.accent, fontSize: 16 }}>{fmtLei(t.total_cost_all)}</div>
                      <div style={{ fontSize: 11, color: C.text2 }}>{t.nr_reparatii} reparații · medie {fmtLei(t.cost_mediu)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Per-trailer table - desktop */}
            <div className="lm-desktop-table" style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Nr Auto</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("total_cost_all")}>Cost Total{sortIcon("total_cost_all")}</th>
                    <th style={S.th}>Piese (12 luni)</th>
                    <th style={S.th}>Manoperă (12 luni)</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("nr_reparatii")}>Reparații{sortIcon("nr_reparatii")}</th>
                    <th style={{ ...S.th, cursor: "pointer" }} onClick={() => doSort("cost_mediu")}>Cost Mediu{sortIcon("cost_mediu")}</th>
                    <th style={S.th}>Ultima Reparație</th>
                  </tr>
                </thead>
                <tbody>
                  {sortRows(d.per_trailer, sortCol, sortDir).map(r => (
                    <tr key={r.id} style={{ ...(top5Ids.has(r.id) && r.total_cost_all > 0 ? { background: "#fef2f2" } : {}) }}>
                      <td style={{ ...S.td, fontWeight: 700, color: C.text, borderRadius: "8px 0 0 8px", cursor: "pointer" }}
                        onClick={() => onSelectTrailer && onSelectTrailer(r.id)}>{r.numar_auto}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: r.total_cost_all > 0 ? C.accent : C.text2 }}>{fmtLei(r.total_cost_all)}</td>
                      <td style={S.td}>{fmtLei(r.cost_parts_12m)}</td>
                      <td style={S.td}>{fmtLei(r.cost_labor_12m)}</td>
                      <td style={S.td}>{r.nr_reparatii}</td>
                      <td style={S.td}>{fmtLei(r.cost_mediu)}</td>
                      <td style={{ ...S.td, borderRadius: "0 8px 8px 0" }}>{r.ultima_reparatie ? fmtDT(r.ultima_reparatie) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="lm-mobile-cards" style={{ display: "none", flexDirection: "column", gap: 8 }}>
              {sortRows(d.per_trailer, sortCol, sortDir).map(r => (
                <div key={r.id} style={{ ...S.statCard, padding: 14, cursor: "pointer", ...(top5Ids.has(r.id) && r.total_cost_all > 0 ? { borderLeft: `3px solid ${C.red}` } : {}) }}
                  onClick={() => onSelectTrailer && onSelectTrailer(r.id)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{r.numar_auto}</span>
                    <span style={{ fontWeight: 700, color: r.total_cost_all > 0 ? C.accent : C.text2 }}>{fmtLei(r.total_cost_all)}</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: 12, color: C.text2 }}>
                    <span>Piese: {fmtLei(r.cost_parts_12m)}</span>
                    <span>Manoperă: {fmtLei(r.cost_labor_12m)}</span>
                    <span>{r.nr_reparatii} reparații</span>
                    <span>Medie: {fmtLei(r.cost_mediu)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CLIENT LIST & FORM
   ═══════════════════════════════════════════════════════════ */
function ClientList({ onRefresh }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editClient, setEditClient] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/clients");
      if (data) setClients(data);
    } catch (ex) { console.error(ex); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Ștergi acest client?")) return;
    try { await api.del(`/api/clients/${id}`); load(); if (onRefresh) onRefresh(); } catch (ex) { alert(ex.message); }
  };

  const handleSave = async (form) => {
    try {
      if (form.id) {
        await api.put(`/api/clients/${form.id}`, form);
      } else {
        await api.post("/api/clients", form);
      }
      setShowForm(false);
      setEditClient(null);
      load();
      if (onRefresh) onRefresh();
    } catch (ex) { throw ex; }
  };

  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.company_name || "").toLowerCase().includes(s) || (c.cui || "").toLowerCase().includes(s) || (c.contact_person || "").toLowerCase().includes(s);
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Clienți ({filtered.length})</h2>
        <button style={{ ...S.btn, ...S.btnPrimary }} onClick={() => { setEditClient(null); setShowForm(true); }}>+ Adaugă Client</button>
      </div>

      <input style={{ ...S.input, maxWidth: 400, marginBottom: 20 }} placeholder="Caută firmă, CUI, persoană contact..." value={search} onChange={e => setSearch(e.target.value)} />

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: C.text2 }}>Se încarcă...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: C.text2 }}>Niciun client găsit.</div>
      ) : (<>
        <div className="lm-desktop-table">
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Firmă</th>
                <th style={S.th}>CUI</th>
                <th style={S.th}>Email</th>
                <th style={S.th}>Telefon</th>
                <th style={S.th}>Persoană Contact</th>
                <th style={S.th}>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td style={{ ...S.td, fontWeight: 600, borderRadius: "8px 0 0 8px" }}>{c.company_name}</td>
                  <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12 }}>{c.cui || "—"}</td>
                  <td style={S.td}>{c.email || "—"}</td>
                  <td style={S.td}>{c.phone || "—"}</td>
                  <td style={S.td}>{c.contact_person || "—"}</td>
                  <td style={{ ...S.td, borderRadius: "0 8px 8px 0" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button style={{ ...S.btn, ...S.btnGhost, padding: "4px 10px", fontSize: 12 }} onClick={() => { setEditClient(c); setShowForm(true); }}>Editează</button>
                      <button style={{ ...S.btn, background: C.red + "22", color: C.red, padding: "4px 10px", fontSize: 12, border: `1px solid ${C.red}44` }} onClick={() => handleDelete(c.id)}>Șterge</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="lm-mobile-cards" style={{ display: "none", flexDirection: "column", gap: 8 }}>
          {filtered.map(c => (
            <div key={c.id} style={{ ...S.statCard, padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{c.company_name}</span>
                {c.cui && <span style={{ fontFamily: "monospace", fontSize: 11, color: C.text2 }}>{c.cui}</span>}
              </div>
              <div style={{ fontSize: 13, color: C.text2, marginBottom: 4 }}>
                {c.contact_person && <div>{c.contact_person}</div>}
                {c.phone && <div>{c.phone}</div>}
                {c.email && <div>{c.email}</div>}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "6px 14px", fontSize: 13, flex: 1 }} onClick={() => { setEditClient(c); setShowForm(true); }}>Editează</button>
                <button className="lm-btn" style={{ ...S.btn, background: C.red + "22", color: C.red, padding: "6px 14px", fontSize: 13, border: `1px solid ${C.red}44` }} onClick={() => handleDelete(c.id)}>Șterge</button>
              </div>
            </div>
          ))}
        </div>
      </>)}

      {/* Client Form Modal */}
      {showForm && (
        <ClientForm client={editClient} onSave={handleSave} onClose={() => { setShowForm(false); setEditClient(null); }} />
      )}
    </div>
  );
}

function ClientForm({ client, onSave, onClose }) {
  const [form, setForm] = useState(client || { company_name: "", cui: "", email: "", phone: "", contact_person: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [anafLoading, setAnafLoading] = useState(false);
  const [anafMsg, setAnafMsg] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const lookupAnaf = async (cuiVal) => {
    const cui = (cuiVal || "").trim().replace(/^RO/i, "");
    if (!/^\d{2,10}$/.test(cui)) return;
    setAnafLoading(true); setAnafMsg(""); setErr("");
    try {
      const data = await api.get(`/api/clients/anaf/${cui}`);
      setForm(f => ({
        ...f,
        company_name: data.denumire || f.company_name,
        address: data.adresa || f.address,
        cui: cui,
      }));
      const stareText = data.stare ? (data.stare.toLowerCase().includes("inactiv") ? "INACTIVĂ" : "ACTIVĂ") : "";
      setAnafMsg(`ANAF: ${data.denumire}${stareText ? ` — ${stareText}` : ""}${data.judet ? ` — ${data.judet}` : ""}`);
    } catch (ex) {
      setAnafMsg("");
      setErr(ex.message || "CUI negăsit în ANAF.");
    }
    setAnafLoading(false);
  };

  const handleSave = async () => {
    if (!form.company_name) { setErr("Numele firmei este obligatoriu."); return; }
    setSaving(true); setErr(""); setSuccess(false);
    try {
      const payload = { ...form };
      if (payload.marca === "Altă marcă" && payload.marca_custom) { payload.marca = payload.marca_custom.trim(); }
      delete payload.marca_custom;
      await onSave(form);
      setSuccess(true);
    } catch (ex) { setErr(ex.message); }
    setSaving(false);
  };

  const field = (label, key, required) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>{label}{required ? " *" : ""}</label>
      <input style={S.input} value={form[key] || ""} onChange={e => set(key, e.target.value)} />
    </div>
  );

  return (
    <div style={S.overlay} onClick={onClose}>
      <div className="lm-modal" style={{ ...S.modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>{client?.id ? "Editează Client" : "Adaugă Client"}</h2>
          <button style={{ ...S.btn, background: "transparent", color: C.text2, fontSize: 20 }} onClick={onClose}>✕</button>
        </div>

        {err && <div style={{ color: C.red, marginBottom: 16, padding: "8px 12px", background: C.red + "15", borderRadius: 8, fontSize: 14 }}>{err}</div>}
        {success && <div style={{ color: C.green, marginBottom: 16, padding: "12px 16px", background: C.green + "15", borderRadius: 8, fontSize: 15, fontWeight: 600, textAlign: "center" }}>✓ {client?.id ? "Client actualizat!" : "Client adăugat!"}</div>}
        {anafMsg && <div style={{ color: C.green, marginBottom: 16, padding: "10px 14px", background: C.green + "12", borderRadius: 8, fontSize: 13, border: `1px solid ${C.green}33` }}>{anafMsg}</div>}

        {/* CUI + ANAF lookup */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>CUI</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ ...S.input, marginBottom: 0, flex: 1 }}
              value={form.cui || ""}
              onChange={e => set("cui", e.target.value)}
              placeholder="Ex: RO12345678 sau 12345678"
            />
            <button
              type="button"
              disabled={anafLoading || !form.cui}
              onClick={() => lookupAnaf(form.cui)}
              style={{
                ...S.btn, padding: "8px 16px", fontSize: 13, flexShrink: 0, whiteSpace: "nowrap",
                background: anafLoading ? C.border : C.accent, color: "#fff",
                opacity: (!form.cui || anafLoading) ? 0.5 : 1,
              }}
            >
              {anafLoading ? "Se caută..." : "Verifică ANAF"}
            </button>
          </div>
        </div>

        {field("Nume Firmă", "company_name", true)}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          {field("Telefon", "phone")}
          {field("Email", "email")}
          {field("Persoană Contact", "contact_person")}
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Adresă</label>
          <textarea style={{ ...S.input, minHeight: 50, resize: "vertical" }} value={form.address || ""} onChange={e => set("address", e.target.value)} />
        </div>

        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button style={{ ...S.btn, ...S.btnGhost }} onClick={onClose}>Anulează</button>
          <button style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleSave}>
            {saving ? "Se salvează..." : (client?.id ? "Actualizează" : "Adaugă")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SERVICE PAGE
   ═══════════════════════════════════════════════════════════ */
function ServicePage({ user, onSelectTrailer }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const loadOrders = useCallback(async () => {
    try {
      const url = filter === "all" ? "/api/service" : `/api/service?status=${filter}`;
      const data = await api.get(url);
      if (data) setOrders(data);
    } catch (ex) { console.error(ex); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  if (selectedOrder) {
    return <ServiceDetail orderId={selectedOrder} user={user} onBack={() => { setSelectedOrder(null); loadOrders(); }} onSelectTrailer={onSelectTrailer} />;
  }

  return (
    <div>
      <div className="lm-page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Service</h2>
        <div className="lm-filter-btns" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[{ v: "all", l: "Toate" }, { v: "pending", l: "Așteptare" }, { v: "in_progress", l: "În lucru" }, { v: "mechanic_done", l: "De verificat" }, { v: "completed", l: "Finalizate" }].map(f => (
            <button key={f.v} className="lm-btn" style={{ ...S.btn, padding: "6px 14px", fontSize: 12, ...(filter === f.v ? S.btnPrimary : S.btnGhost) }} onClick={() => setFilter(f.v)}>{f.l}</button>
          ))}
        </div>
      </div>
      {loading ? <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Se încarcă...</div> :
        orders.length === 0 ? <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Nicio comandă de service.</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {orders.map(o => {
              const st = SERVICE_STATUS[o.status] || SERVICE_STATUS.pending;
              const items = typeof o.items === "string" ? JSON.parse(o.items) : (o.items || []);
              const done = items.filter(i => i.status === "done" || i.done).length;
              return (
                <div key={o.id} style={{ ...S.statCard, padding: "16px 20px", cursor: "pointer", display: "flex", gap: 16, alignItems: "center", transition: "box-shadow 0.2s", flexWrap: "wrap" }}
                  onClick={() => setSelectedOrder(o.id)}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                  <div style={{ fontSize: 28 }}>{st.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{o.numar_auto || "—"}</span>
                      <span style={S.badge(st.color)}>{st.label}</span>
                      {o.status === "rejected" && <span style={S.badge(C.red)}>Respins</span>}
                      {o.priority === "urgent" && <span style={{ ...S.badge(C.red), fontSize: 10 }}>🚨 URGENT</span>}
                    </div>
                    <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.5 }}>
                      {o.marca} {o.tip} &bull; Mecanic: <b>{o.mechanic_name || "—"}</b> &bull; Dispecer: {o.dispecer_name || "—"}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                      Creat: {fmtDT(o.created_at)} &bull; {done}/{items.length} lucrări finalizate
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ width: 60, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: items.length ? `${(done / items.length) * 100}%` : "0%", height: "100%", background: done === items.length && items.length > 0 ? C.green : C.accent, borderRadius: 3 }} />
                    </div>
                    <div style={{ fontSize: 11, color: C.text2, marginTop: 2 }}>{items.length ? Math.round((done / items.length) * 100) : 0}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SERVICE DETAIL
   ═══════════════════════════════════════════════════════════ */
function ServiceDetail({ orderId, user, onBack, onSelectTrailer }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mechNotes, setMechNotes] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [itemCosts, setItemCosts] = useState({});
  const [costParts, setCostParts] = useState("");
  const [costLabor, setCostLabor] = useState("");
  const [costNotes, setCostNotes] = useState("");
  const [brakePads, setBrakePads] = useState({ axa1_st: "", axa1_dr: "", axa2_st: "", axa2_dr: "", axa3_st: "", axa3_dr: "" });
  const [brakePadsLoaded, setBrakePadsLoaded] = useState(false);
  const [showBrakePads, setShowBrakePads] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemDesc, setNewItemDesc] = useState("");
  const [newItemSev, setNewItemSev] = useState("minor");
  const [newItemObs, setNewItemObs] = useState("");
  const [addingItem, setAddingItem] = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      const data = await api.get(`/api/service/${orderId}`);
      setOrder(data);
      setMechNotes(data.mechanic_notes || "");
    } catch (ex) { console.error(ex); }
    setLoading(false);
  }, [orderId]);

  const loadBrakePads = useCallback(async () => {
    try {
      const data = await api.get(`/api/service/${orderId}/brake-pads`);
      if (data) {
        const bp = { axa1_st: "", axa1_dr: "", axa2_st: "", axa2_dr: "", axa3_st: "", axa3_dr: "" };
        Object.entries(data).forEach(([k, v]) => { if (bp.hasOwnProperty(k)) bp[k] = v ?? ""; });
        setBrakePads(bp);
      }
      setBrakePadsLoaded(true);
    } catch (ex) { console.error(ex); setBrakePadsLoaded(true); }
  }, [orderId]);

  useEffect(() => { loadOrder(); loadBrakePads(); }, [loadOrder, loadBrakePads]);

  const items = order ? (typeof order.items === "string" ? JSON.parse(order.items) : (order.items || [])) : [];

  const toggleItem = async (idx) => {
    const newItems = [...items];
    const cur = newItems[idx];
    newItems[idx] = { ...cur, status: cur.status === "done" ? "pending" : "done", done: cur.status !== "done" };
    try {
      await api.put(`/api/service/${orderId}/update-items`, { items: newItems });
      setOrder(prev => ({ ...prev, items: newItems }));
    } catch (ex) { alert("Eroare: " + ex.message); }
  };

  const updateItemNote = async (idx, note) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], mechanic_note: note };
    setOrder(prev => ({ ...prev, items: newItems }));
  };

  const saveItemNote = async (idx) => {
    try {
      await api.put(`/api/service/${orderId}/update-items`, { items });
    } catch (ex) { alert("Eroare: " + ex.message); }
  };

  const handleAddItem = async () => {
    if (!newItemDesc.trim()) return alert("Denumirea lucrării este obligatorie.");
    setAddingItem(true);
    try {
      await api.post(`/api/service/${orderId}/add-item`, { description: newItemDesc, severity: newItemSev, observations: newItemObs });
      setNewItemDesc(""); setNewItemSev("minor"); setNewItemObs(""); setShowAddItem(false);
      await loadOrder();
    } catch (ex) { alert("Eroare: " + (ex.message || "Eroare server")); }
    setAddingItem(false);
  };

  const handleStart = async () => {
    setSaving(true);
    try {
      await api.put(`/api/service/${orderId}/start`, {});
      await loadOrder();
    } catch (ex) { alert("Eroare: " + ex.message); }
    setSaving(false);
  };

  const undoneCount = items.filter(i => i.status !== "done" && !i.done).length;

  const BRAKE_LABELS = { axa1_st: "Axa 1 Stânga", axa1_dr: "Axa 1 Dreapta", axa2_st: "Axa 2 Stânga", axa2_dr: "Axa 2 Dreapta", axa3_st: "Axa 3 Stânga", axa3_dr: "Axa 3 Dreapta" };
  const allBrakeFilled = Object.values(brakePads).every(v => v !== "" && !isNaN(parseInt(v)) && parseInt(v) >= 0 && parseInt(v) <= 100);

  const handleComplete = async () => {
    if (undoneCount > 0) return alert("Nu poți finaliza - ai " + undoneCount + " lucrări incomplete.");
    if (!allBrakeFilled) return alert("Completează nivelul plăcuțe frână pentru toate cele 6 roți!");
    setSaving(true);
    try {
      const bp = {};
      Object.entries(brakePads).forEach(([k, v]) => { bp[k] = parseInt(v); });
      await api.put(`/api/service/${orderId}/complete`, { mechanic_notes: mechNotes, items, brake_pads: bp });
      await loadOrder();
    } catch (ex) { alert("Eroare: " + ex.message); }
    setSaving(false);
  };

  const handleApprove = async () => {
    setSaving(true);
    try {
      const updatedItems = items.map((item, idx) => ({
        ...item,
        cost: parseFloat(itemCosts[idx]?.cost) || 0,
        cost_description: itemCosts[idx]?.desc || "",
      }));
      await api.put(`/api/service/${orderId}/approve`, { items: updatedItems });
      setShowApprove(false);
      onBack();
    } catch (ex) { alert("Eroare: " + ex.message); }
    setSaving(false);
  };

  const handleReject = async () => {
    if (!rejectNotes.trim()) return alert("Scrie motivul respingerii.");
    setSaving(true);
    try {
      await api.put(`/api/service/${orderId}/reject`, { reject_notes: rejectNotes });
      setShowReject(false);
      await loadOrder();
    } catch (ex) { alert("Eroare: " + ex.message); }
    setSaving(false);
  };

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: C.text2 }}>Se încarcă...</div>;
  if (!order) return <div style={{ textAlign: "center", padding: 60, color: C.text2 }}>Comandă negăsită.</div>;

  const st = SERVICE_STATUS[order.status] || SERVICE_STATUS.pending;
  const doneCount = items.filter(i => i.status === "done" || i.done).length;
  const isMechanic = user.role === "mecanic";
  const isDispatcher = user.role === "admin" || user.role === "dispecer";
  const canDoMechanicWork = isMechanic || user.role === "dispecer";
  const canEdit = canDoMechanicWork && (order.status === "in_progress" || order.status === "rejected");
  const canStart = canDoMechanicWork && (order.status === "pending" || order.status === "rejected");
  const canComplete = canDoMechanicWork && (order.status === "in_progress" || order.status === "rejected");
  const canApprove = isDispatcher && order.status === "mechanic_done";

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", paddingBottom: canApprove ? 80 : 0 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div>
          <button style={{ ...S.btn, ...S.btnGhost, padding: "4px 14px", fontSize: 13, marginBottom: 8 }} onClick={onBack}>← Înapoi</button>
          <h2 style={{ color: C.text, margin: 0, fontSize: 20 }}>
            Comandă Service #{order.id} — {order.numar_auto}
          </h2>
        </div>
        <span style={{ ...S.badge(st.color), fontSize: 14, padding: "6px 16px" }}>{st.icon} {st.label}</span>
      </div>

      {/* Reject notes banner */}
      {order.status === "rejected" && order.reject_notes && (
        <div style={{ background: "#fef2f2", border: `1px solid #fecaca`, borderLeft: `4px solid ${C.red}`, borderRadius: 8, padding: "10px 16px", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: C.red, fontSize: 13, marginBottom: 4 }}>Motiv respingere:</div>
          <div style={{ color: C.text, fontSize: 13 }}>{order.reject_notes}</div>
        </div>
      )}

      {/* Info */}
      <div className="lm-detail-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        <div style={S.infoItem}><div style={S.infoLabel}>Remorcă</div><div style={{ ...S.infoVal, fontWeight: 700, fontSize: 18 }}>{order.numar_auto}</div></div>
        <div style={S.infoItem}><div style={S.infoLabel}>Mecanic</div><div style={S.infoVal}>{order.mechanic_name || "—"}</div></div>
        <div style={S.infoItem}><div style={S.infoLabel}>Dispecer</div><div style={S.infoVal}>{order.dispecer_name || "—"}</div></div>
        <div style={S.infoItem}><div style={S.infoLabel}>Creat</div><div style={S.infoVal}>{fmtDT(order.created_at)}</div></div>
        <div style={S.infoItem}><div style={S.infoLabel}>Început</div><div style={S.infoVal}>{order.started_at ? fmtDT(order.started_at) : "—"}</div></div>
        <div style={S.infoItem}><div style={S.infoLabel}>Finalizat</div><div style={S.infoVal}>{order.completed_at ? fmtDT(order.completed_at) : "—"}</div></div>
      </div>

      {order.description && (
        <div style={{ ...S.infoItem, marginBottom: 16 }}>
          <div style={S.infoLabel}>Descriere</div>
          <div style={S.infoVal}>{order.description}</div>
        </div>
      )}

      {/* Progress bar */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Progres lucrări</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: doneCount === items.length && items.length > 0 ? C.green : C.accent }}>{doneCount}/{items.length}</span>
        </div>
        <div style={{ width: "100%", height: 10, background: C.border, borderRadius: 5, overflow: "hidden" }}>
          <div style={{ width: items.length ? `${(doneCount / items.length) * 100}%` : "0%", height: "100%", background: doneCount === items.length && items.length > 0 ? C.green : C.accent, borderRadius: 5, transition: "width 0.3s" }} />
        </div>
      </div>

      {/* Items checklist */}
      <div style={{ ...S.statCard, marginBottom: 20 }}>
        <h3 style={{ color: C.text, marginBottom: 12, fontSize: 16 }}>Lucrări de efectuat</h3>
        {items.map((item, idx) => {
          const isDone = item.status === "done" || item.done;
          return (
            <div key={idx} className="lm-checkbox-item" style={{
              padding: "10px 14px", marginBottom: 4,
              background: isDone ? "#ecfdf5" : "#fff", borderRadius: 8, border: `1px solid ${isDone ? "#bbf7d0" : C.border}`,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: canEdit ? "pointer" : "default", opacity: isDone ? 0.8 : 1 }}
                onClick={() => canEdit && toggleItem(idx)}>
                <div className="lm-checkbox-box" style={{
                  width: 26, height: 26, borderRadius: 6, border: `2px solid ${isDone ? C.green : C.border}`,
                  background: isDone ? C.green : "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 15, fontWeight: 700, flexShrink: 0, marginTop: 1
                }}>
                  {isDone ? "✓" : ""}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: C.text, textDecoration: isDone ? "line-through" : "none" }}>{item.description || item.text}</div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 2, flexWrap: "wrap" }}>
                    {item.severity && <span style={{ ...S.badge(item.severity === "critical" ? C.red : item.severity === "major" ? C.orange : C.green), fontSize: 10 }}>{item.severity}</span>}
                    {item.from_difference && <span style={{ ...S.badge("#7c3aed"), fontSize: 10 }}>din diferențe</span>}
                    {item.zone && <span style={{ fontSize: 10, color: C.muted }}>{ZONES_LABELS[item.zone] || item.zone}</span>}
                  </div>
                  {item.instructions && (
                    <div style={{ marginTop: 4, fontSize: 12, color: C.text2, fontStyle: "italic" }}>📝 Instrucțiuni: {item.instructions}</div>
                  )}
                </div>
              </div>
              {/* Per-task mechanic note */}
              {canEdit && (
                <div style={{ marginTop: 6, marginLeft: 32 }}>
                  <input style={{ ...S.input, fontSize: 12, padding: "4px 8px", marginBottom: 0 }}
                    value={item.mechanic_note || ""} onChange={e => updateItemNote(idx, e.target.value)}
                    onBlur={() => saveItemNote(idx)} placeholder="Notă mecanic (opțional)..." onClick={e => e.stopPropagation()} />
                </div>
              )}
              {!canEdit && item.mechanic_note && (
                <div style={{ marginTop: 4, marginLeft: 32, fontSize: 12, color: C.muted, fontStyle: "italic" }}>Notă: {item.mechanic_note}</div>
              )}
            </div>
          );
        })}
        {/* Add item button + form (admin/dispecer, open orders only) */}
        {isDispatcher && (order.status === "pending" || order.status === "in_progress") && !showAddItem && (
          <button style={{ ...S.btn, ...S.btnGhost, width: "100%", marginTop: 8, fontSize: 13, border: "1px dashed " + C.accent, color: C.accent }}
            onClick={() => setShowAddItem(true)}>+ Adaugă lucrare</button>
        )}
        {showAddItem && (
          <div style={{ marginTop: 8, padding: 14, borderRadius: 8, border: "1px solid " + C.accent, background: "#f0f7ff" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 10 }}>Lucrare nouă</div>
            <input style={{ ...S.input, marginBottom: 8 }} placeholder="Denumire lucrare *" value={newItemDesc} onChange={e => setNewItemDesc(e.target.value)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div>
                <label style={{ fontSize: 11, color: C.text2, display: "block", marginBottom: 4 }}>Severitate</label>
                <select style={{ ...S.input, marginBottom: 0 }} value={newItemSev} onChange={e => setNewItemSev(e.target.value)}>
                  <option value="minor">Minor</option>
                  <option value="major">Major</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.text2, display: "block", marginBottom: 4 }}>Observații</label>
                <input style={{ ...S.input, marginBottom: 0 }} placeholder="Observații (opțional)" value={newItemObs} onChange={e => setNewItemObs(e.target.value)} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button style={{ ...S.btn, ...S.btnGhost, fontSize: 12, padding: "6px 14px" }}
                onClick={() => { setShowAddItem(false); setNewItemDesc(""); setNewItemSev("minor"); setNewItemObs(""); }}>Anulează</button>
              <button style={{ ...S.btn, ...S.btnPrimary, fontSize: 12, padding: "6px 14px", opacity: addingItem ? 0.6 : 1 }}
                disabled={addingItem} onClick={handleAddItem}>{addingItem ? "Se salvează..." : "Salvează"}</button>
            </div>
          </div>
        )}
      </div>

      {/* Mechanic notes */}
      {(canEdit || mechNotes) && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6, display: "block" }}>Note mecanic</label>
          {canEdit ? (
            <textarea style={{ ...S.input, height: 80, resize: "vertical", marginBottom: 0 }} value={mechNotes} onChange={e => setMechNotes(e.target.value)} placeholder="Note despre lucrări efectuate..." />
          ) : (
            <div style={{ ...S.infoItem, whiteSpace: "pre-wrap" }}>{mechNotes || "—"}</div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: 32 }}>
        {canStart && (
          <button style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleStart}>
            {saving ? "..." : "▶️ Începe lucrul"}
          </button>
        )}
        {canComplete && undoneCount === 0 && !showBrakePads && (
          <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary }} onClick={() => setShowBrakePads(true)}>
            Finalizează și trimite la verificare
          </button>
        )}
        {canComplete && undoneCount > 0 && (
          <div style={{ fontSize: 12, color: C.red, fontWeight: 600 }}>Mai ai {undoneCount} lucrări nefinalizate</div>
        )}
      </div>

      {/* Fixed bottom bar for approve/reject on mobile */}
      {canApprove && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
          background: "#fff", borderTop: "1px solid #dce3ec",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          padding: "12px 20px",
          display: "flex", gap: 10, justifyContent: "center", alignItems: "center",
        }}>
          <button className="lm-btn" style={{ ...S.btn, background: C.green, color: "#fff", padding: "12px 24px", fontSize: 15, fontWeight: 700, flex: 1, maxWidth: 250, opacity: saving ? 0.6 : 1 }}
            disabled={saving} onClick={() => setShowApprove(true)}>
            Aprobă cu costuri
          </button>
          <button className="lm-btn" style={{ ...S.btn, background: C.red, color: "#fff", padding: "12px 24px", fontSize: 15, fontWeight: 700, flex: 1, maxWidth: 250, opacity: saving ? 0.6 : 1 }}
            disabled={saving} onClick={() => setShowReject(true)}>
            Respinge
          </button>
        </div>
      )}

      {/* Brake pads form - shown when mechanic wants to finalize */}
      {canComplete && showBrakePads && (
        <div style={{ ...S.statCard, marginBottom: 20, padding: 20, borderLeft: "4px solid " + C.accent }}>
          <h3 style={{ color: C.text, marginBottom: 4, fontSize: 16 }}>Plăcuțe frână — obligatoriu</h3>
          <div style={{ fontSize: 13, color: C.text2, marginBottom: 16 }}>Completează procentul de uzură al plăcuțelor pe fiecare roată (0-100%)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {Object.entries(BRAKE_LABELS).map(([key, label]) => (
              <div key={key}>
                <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>{label}</label>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input style={{ ...S.input, marginBottom: 0, flex: 1, textAlign: "center", fontSize: 16, fontWeight: 700, borderColor: brakePads[key] !== "" && parseInt(brakePads[key]) >= 0 && parseInt(brakePads[key]) <= 100 ? C.green : C.border }}
                    type="number" min="0" max="100" placeholder="%" value={brakePads[key]}
                    onChange={e => setBrakePads(prev => ({ ...prev, [key]: e.target.value }))} />
                  <span style={{ fontSize: 14, color: C.text2, minWidth: 20 }}>%</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
            <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost }} onClick={() => setShowBrakePads(false)}>Anulează</button>
            <button className="lm-btn" style={{ ...S.btn, background: allBrakeFilled ? C.green : "#9ca3af", color: C.white, cursor: allBrakeFilled ? "pointer" : "not-allowed", opacity: saving ? 0.6 : 1 }}
              disabled={saving || !allBrakeFilled} onClick={handleComplete}>
              {saving ? "Se trimite..." : "Finalizează și trimite la verificare"}
            </button>
          </div>
          {!allBrakeFilled && <div style={{ textAlign: "center", fontSize: 12, color: C.red, marginTop: 8, fontWeight: 600 }}>Completează toate cele 6 valori de plăcuțe</div>}
        </div>
      )}

      {/* Cost summary for completed orders */}
      {order.status === "completed" && order.total_cost > 0 && (
        <div style={{ ...S.statCard, marginBottom: 20 }}>
          <h3 style={{ color: C.text, marginBottom: 12, fontSize: 16 }}>Costuri Service</h3>
          <div style={{ ...S.infoItem, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: C.text2, textTransform: "uppercase" }}>Cost total</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: C.accent, marginTop: 4 }}>{fmtLei(order.total_cost)}</div>
            <div style={{ fontSize: 11, color: C.text2 }}>(fără TVA)</div>
          </div>
          {items.some(i => i.cost > 0) && (
            <div style={{ marginTop: 12 }}>
              {items.filter(i => i.cost > 0).map((item, idx) => (
                <div key={idx} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.text2, padding: "3px 0", borderBottom: "1px solid " + C.border }}>
                  <span>{item.description || item.text}</span>
                  <span style={{ fontWeight: 600, color: C.text }}>{fmtLei(item.cost)}{item.cost_description ? " — " + item.cost_description : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Approve modal with costs */}
      {showApprove && (
        <div style={S.overlay} onClick={() => setShowApprove(false)}>
          <div className="lm-modal" style={{ ...S.modal, maxWidth: 650 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: C.text, marginBottom: 16 }}>Aprobă Service — #{order.id} {order.numar_auto}</h3>
            <div style={{ fontSize: 13, color: C.text2, marginBottom: 16 }}>Adaugă costurile pentru fiecare lucrare (fără TVA):</div>

            {/* Per-item costs */}
            <div style={{ maxHeight: 300, overflow: "auto", marginBottom: 16 }}>
              {items.map((item, idx) => {
                const isDone = item.status === "done" || item.done;
                return (
                  <div key={idx} style={{ padding: "8px 12px", marginBottom: 4, borderRadius: 6, border: `1px solid ${C.border}`, background: isDone ? "#ecfdf5" : "#fff" }}>
                    <div style={{ fontSize: 13, color: C.text, marginBottom: 6 }}>
                      {isDone ? "✅" : "⬜"} {item.description || item.text}
                      {item.severity && <span style={{ ...S.badge(item.severity === "critical" ? C.red : item.severity === "major" ? C.orange : C.green), fontSize: 10, marginLeft: 6 }}>{item.severity}</span>}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 8 }}>
                      <input style={{ ...S.input, fontSize: 12, padding: "4px 8px", marginBottom: 0 }} type="number" step="0.01" min="0"
                        placeholder="Cost (lei)" value={itemCosts[idx]?.cost || ""}
                        onChange={e => setItemCosts(prev => ({ ...prev, [idx]: { ...prev[idx], cost: e.target.value } }))} />
                      <input style={{ ...S.input, fontSize: 12, padding: "4px 8px", marginBottom: 0 }}
                        placeholder="Detalii cost / piese folosite" value={itemCosts[idx]?.desc || ""}
                        onChange={e => setItemCosts(prev => ({ ...prev, [idx]: { ...prev[idx], desc: e.target.value } }))} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total calculated from items */}
            {(() => {
              const itemsTotal = Object.values(itemCosts).reduce((s, v) => s + (parseFloat(v?.cost) || 0), 0);
              return (
                <div style={{ textAlign: "right", padding: "10px 0", borderTop: `1px solid ${C.border}`, marginBottom: 12 }}>
                  <span style={{ fontSize: 13, color: C.text2 }}>Total estimat: </span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: C.accent }}>{fmtLei(itemsTotal)}</span>
                  <div style={{ fontSize: 11, color: C.text2 }}>(fără TVA)</div>
                </div>
              );
            })()}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost }} onClick={() => setShowApprove(false)}>Anulează</button>
              <button className="lm-btn" style={{ ...S.btn, background: C.green, color: C.white, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleApprove}>
                {saving ? "..." : "✅ Aprobă — Remorca devine Activă"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {showReject && (
        <div style={S.overlay} onClick={() => setShowReject(false)}>
          <div className="lm-modal" style={{ ...S.modal, maxWidth: 450 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: C.text, marginBottom: 16 }}>Respinge lucrarea</h3>
            <textarea style={{ ...S.input, height: 100, resize: "vertical" }} value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} placeholder="Motivul respingerii... Ce trebuie refăcut?" autoFocus />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => setShowReject(false)}>Anulează</button>
              <button style={{ ...S.btn, ...S.btnDanger, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleReject}>
                {saving ? "..." : "Respinge"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CREATE SERVICE ORDER MODAL
   ═══════════════════════════════════════════════════════════ */
function CreateServiceModal({ trailerId, pvId, differences, onClose, onCreated }) {
  const [mechanics, setMechanics] = useState([]);
  const [mechanicId, setMechanicId] = useState("");
  const [description, setDescription] = useState("");
  const [selectedDiffs, setSelectedDiffs] = useState({});
  const [extraTasks, setExtraTasks] = useState([]);
  const [newTaskText, setNewTaskText] = useState("");
  const [saving, setSaving] = useState(false);
  const [priority, setPriority] = useState("normal");
  const [orderType, setOrderType] = useState("service");
  const [parts, setParts] = useState([]);
  const [newPart, setNewPart] = useState({ name: "", supplier: "", quantity: 1, price: "", date: new Date().toISOString().split("T")[0] });
  const [diffInstructions, setDiffInstructions] = useState({});
  const [extraInstructions, setExtraInstructions] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/api/service/mechanics/list");
        if (data) setMechanics(data);
        if (data && data.length > 0) setMechanicId(data[0].id);
      } catch { /* ok */ }
    })();
  }, []);

  const toggleDiff = (id) => setSelectedDiffs(prev => ({ ...prev, [id]: !prev[id] }));
  const selectAllDiffs = () => {
    const all = {};
    (differences || []).forEach(d => { all[d.id] = true; });
    setSelectedDiffs(all);
  };
  const deselectAllDiffs = () => setSelectedDiffs({});
  const selectedCount = Object.values(selectedDiffs).filter(Boolean).length;

  const addExtraTask = () => {
    if (!newTaskText.trim()) return;
    setExtraTasks(prev => [...prev, newTaskText.trim()]);
    setNewTaskText("");
  };
  const removeExtraTask = (idx) => setExtraTasks(prev => prev.filter((_, i) => i !== idx));

  const addPart = () => {
    if (!newPart.name.trim() || !newPart.price) return;
    setParts(prev => [...prev, { name: newPart.name.trim(), supplier: newPart.supplier.trim(), quantity: parseInt(newPart.quantity) || 1, price: parseFloat(newPart.price) || 0, date: newPart.date }]);
    setNewPart({ name: "", supplier: "", quantity: 1, price: "", date: new Date().toISOString().split("T")[0] });
  };
  const removePart = (idx) => setParts(prev => prev.filter((_, i) => i !== idx));
  const partsTotal = parts.reduce((s, p) => s + (p.quantity || 1) * (p.price || 0), 0);

  const handleCreate = async () => {
    if (orderType === "service" && !mechanicId) return alert("Selectează un mecanic.");
    const totalTasks = selectedCount + extraTasks.length;
    if (orderType === "service" && totalTasks === 0) return alert("Adaugă cel puțin un task.");
    if (orderType === "interventie" && totalTasks === 0 && parts.length === 0) return alert("Adaugă cel puțin o piesă sau un task.");
    setSaving(true);
    try {
      let itemId = 1;
      const items = [];
      // Add selected differences as tasks
      (differences || []).forEach(d => {
        if (selectedDiffs[d.id]) {
          items.push({
            id: itemId++,
            description: `${ZONES_LABELS[d.zone_id] || d.zone_id}: ${d.description}`,
            instructions: diffInstructions[d.id] || "",
            severity: d.severity,
            zone: d.zone_id,
            from_difference: true,
            difference_id: d.id,
            status: "pending",
            mechanic_note: "",
          });
        }
      });
      // Add extra manual tasks
      extraTasks.forEach((text, eIdx) => {
        items.push({
          id: itemId++,
          description: text,
          instructions: extraInstructions[eIdx] || "",
          severity: "minor",
          zone: null,
          from_difference: false,
          difference_id: null,
          status: "pending",
          mechanic_note: "",
        });
      });
      const result = await api.post("/api/service", {
        trailer_id: trailerId,
        pv_returnare_id: pvId || null,
        description: description || null,
        items,
        mechanic_id: mechanicId ? parseInt(mechanicId) : null,
        priority,
        type: orderType,
      });
      // Save parts via PUT costs if any
      if (parts.length > 0 && result && result.id) {
        try {
          const costsPayload = parts.map(p => ({ name: p.name, supplier: p.supplier, quantity: p.quantity, price: p.price, total: (p.quantity || 1) * (p.price || 0), date: p.date }));
          await api.put("/api/service/" + result.id + "/costs", { costs: costsPayload });
        } catch (costErr) {
          alert("Comanda creată (#" + result.id + "), dar piesele nu s-au salvat. Adaugă-le pe card.\n" + (costErr.message || ""));
        }
      }
      // [29.10.2026] Intrarea in service anuleaza automat o predare deschisa pe remorca
      // (server/routes/service.js). Fara semnal explicit, alocarea clientului ar disparea
      // din fluxul dispecerului fara nicio urma vizibila. Incident BR35TEC 29.10.2026.
      if (result && result.handover_cancelled) {
        alert("Aten\u021bie: remorca avea o predare \u00EEn curs c\u0103tre \"" + result.handover_cancelled
          + "\".\nPredarea a fost anulat\u0103 automat la intrarea \u00EEn service.");
      }
      onCreated(result);
    } catch (ex) {
      alert("Eroare: " + ex.message);
    }
    setSaving(false);
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 650 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: C.text, marginBottom: 16 }}>{orderType === "interventie" ? "Intervenție / Piese" : "Trimite la Service"}</h3>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4, display: "block" }}>Tip</label>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{ ...S.btn, flex: 1, background: orderType === "service" ? C.orange : "#f0f2f5", color: orderType === "service" ? "#fff" : C.text, border: "1px solid " + (orderType === "service" ? C.orange : C.border) }} onClick={() => setOrderType("service")}>🔧 Service</button>
            <button style={{ ...S.btn, flex: 1, background: orderType === "interventie" ? "#7c3aed" : "#f0f2f5", color: orderType === "interventie" ? "#fff" : C.text, border: "1px solid " + (orderType === "interventie" ? "#7c3aed" : C.border) }} onClick={() => setOrderType("interventie")}>🛞 Intervenție / Piese</button>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4, display: "block" }}>Mecanic {orderType === "service" ? "*" : "(opțional)"}</label>
          <select style={{ ...S.input, marginBottom: 0 }} value={mechanicId} onChange={e => setMechanicId(e.target.value)}>
            <option value="">— Selectează mecanic —</option>
            {mechanics.map(m => <option key={m.id} value={m.id}>{m.full_name || m.username}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4, display: "block" }}>Descriere (opțional)</label>
          <textarea style={{ ...S.input, height: 60, resize: "vertical", marginBottom: 0 }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Descriere generală..." />
        </div>

        {/* Priority */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4, display: "block" }}>Prioritate</label>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{ ...S.btn, flex: 1, background: priority === "normal" ? C.accent : "#f0f2f5", color: priority === "normal" ? "#fff" : C.text, border: "1px solid " + (priority === "normal" ? C.accent : C.border) }} onClick={() => setPriority("normal")}>Normal</button>
            <button style={{ ...S.btn, flex: 1, background: priority === "urgent" ? C.red : "#f0f2f5", color: priority === "urgent" ? "#fff" : C.text, border: "1px solid " + (priority === "urgent" ? C.red : C.border) }} onClick={() => setPriority("urgent")}>🚨 Urgent</button>
          </div>
        </div>

        {/* Differences with checkboxes */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
              Diferențe detectate ({(differences || []).length})
            </label>
            {(differences || []).length > 0 && (
              <div style={{ display: "flex", gap: 8 }}>
                <button style={{ fontSize: 11, color: C.accent, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }} onClick={selectAllDiffs}>Selectează tot</button>
                <button style={{ fontSize: 11, color: C.muted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }} onClick={deselectAllDiffs}>Deselectează</button>
              </div>
            )}
          </div>
          <div style={{ maxHeight: 220, overflow: "auto", background: C.card2, borderRadius: 8, padding: 8, border: `1px solid ${C.border}` }}>
            {(differences || []).map((d) => (
              <div key={d.id} style={{ marginBottom: 3 }}>
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 8px",
                  background: selectedDiffs[d.id] ? "#eff6ff" : "transparent", borderRadius: selectedDiffs[d.id] ? "6px 6px 0 0" : 6,
                  cursor: "pointer", border: `1px solid ${selectedDiffs[d.id] ? C.accent : "transparent"}`,
                  borderBottom: selectedDiffs[d.id] ? "none" : undefined,
                }} onClick={() => toggleDiff(d.id)}>
                  <div style={{
                    width: 20, height: 20, borderRadius: 4, border: `2px solid ${selectedDiffs[d.id] ? C.accent : C.border}`,
                    background: selectedDiffs[d.id] ? C.accent : "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0, marginTop: 1,
                  }}>
                    {selectedDiffs[d.id] ? "✓" : ""}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: C.text }}>
                      <span style={S.badge(d.severity === "critical" ? C.red : d.severity === "major" ? C.orange : C.green)}>{d.severity}</span>
                      {" "}<strong>{ZONES_LABELS[d.zone_id] || d.zone_id}</strong>: {d.description}
                    </div>
                  </div>
                </div>
                {selectedDiffs[d.id] && (
                  <div style={{ padding: "4px 8px 8px 36px", background: "#eff6ff", borderRadius: "0 0 6px 6px", border: `1px solid ${C.accent}`, borderTop: "none" }}>
                    <textarea style={{ ...S.input, fontSize: 11, padding: "4px 8px", height: 36, resize: "vertical", marginBottom: 0, background: "#fff" }}
                      value={diffInstructions[d.id] || ""} onChange={e => setDiffInstructions(prev => ({ ...prev, [d.id]: e.target.value }))}
                      onClick={e => e.stopPropagation()} placeholder="Instrucțiuni pentru mecanic... (ex: Coase prelata zona stânga, 30cm ruptură)" />
                  </div>
                )}
              </div>
            ))}
            {(!differences || differences.length === 0) && <div style={{ color: C.muted, fontSize: 12, padding: 4 }}>Nicio diferență — adaugă taskuri manual mai jos.</div>}
          </div>
          {selectedCount > 0 && <div style={{ fontSize: 12, color: C.accent, marginTop: 4, fontWeight: 600 }}>{selectedCount} diferențe selectate</div>}
        </div>

        {/* Extra manual tasks */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6, display: "block" }}>{(differences || []).length > 0 ? "Taskuri suplimentare" : "Taskuri de executat *"}</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input style={{ ...S.input, flex: 1, marginBottom: 0 }} value={newTaskText} onChange={e => setNewTaskText(e.target.value)}
              placeholder="Ex: Spălare, degresare..." onKeyDown={e => e.key === "Enter" && addExtraTask()} />
            <button style={{ ...S.btn, ...S.btnPrimary, padding: "8px 16px", whiteSpace: "nowrap" }} onClick={addExtraTask}>+ Adaugă</button>
          </div>
          {extraTasks.map((task, idx) => (
            <div key={idx} style={{ marginBottom: 3 }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
                background: "#f0fdf4", borderRadius: extraInstructions[idx] ? "6px 6px 0 0" : 6, border: `1px solid #bbf7d0`,
                borderBottom: extraInstructions[idx] ? "none" : undefined,
              }}>
                <div style={{ flex: 1, fontSize: 12, color: C.text }}>{task}</div>
                <button style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 16, padding: "0 4px" }} onClick={() => removeExtraTask(idx)}>×</button>
              </div>
              <div style={{ padding: "4px 10px 8px", background: "#f0fdf4", borderRadius: "0 0 6px 6px", border: "1px solid #bbf7d0", borderTop: "none" }}>
                <textarea style={{ ...S.input, fontSize: 11, padding: "4px 8px", height: 36, resize: "vertical", marginBottom: 0, background: "#fff" }}
                  value={extraInstructions[idx] || ""} onChange={e => setExtraInstructions(prev => ({ ...prev, [idx]: e.target.value }))}
                  placeholder="Instrucțiuni pentru mecanic... (opțional)" />
              </div>
            </div>
          ))}
        </div>

        {/* Parts section - only for interventie */}
        {orderType === "interventie" && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6, display: "block" }}>Piese / Costuri</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
              <input style={{ ...S.input, marginBottom: 0, fontSize: 12, padding: "6px 8px" }} placeholder="Denumire piesă *" value={newPart.name}
                onChange={e => setNewPart(p => ({ ...p, name: e.target.value }))} onKeyDown={e => e.key === "Enter" && addPart()} />
              <input style={{ ...S.input, marginBottom: 0, fontSize: 12, padding: "6px 8px" }} placeholder="Furnizor" value={newPart.supplier}
                onChange={e => setNewPart(p => ({ ...p, supplier: e.target.value }))} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 6, marginBottom: 8 }}>
              <input style={{ ...S.input, marginBottom: 0, fontSize: 12, padding: "6px 8px" }} type="number" min="1" placeholder="Cant." value={newPart.quantity}
                onChange={e => setNewPart(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} />
              <input style={{ ...S.input, marginBottom: 0, fontSize: 12, padding: "6px 8px" }} type="number" step="0.01" min="0" placeholder="Preț *" value={newPart.price}
                onChange={e => setNewPart(p => ({ ...p, price: e.target.value }))} onKeyDown={e => e.key === "Enter" && addPart()} />
              <input style={{ ...S.input, marginBottom: 0, fontSize: 12, padding: "6px 8px" }} type="date" value={newPart.date}
                onChange={e => setNewPart(p => ({ ...p, date: e.target.value }))} />
              <button style={{ ...S.btn, ...S.btnPrimary, padding: "6px 14px", fontSize: 12, whiteSpace: "nowrap" }} onClick={addPart} disabled={!newPart.name.trim() || !newPart.price}>+ Adaugă</button>
            </div>
            {parts.length > 0 && (
              <div style={{ border: "1px solid " + C.border, borderRadius: 8, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: C.card2 }}>
                      <th style={{ padding: "6px 8px", textAlign: "left", color: C.text2, fontWeight: 600, fontSize: 11 }}>Denumire</th>
                      <th style={{ padding: "6px 8px", textAlign: "left", color: C.text2, fontWeight: 600, fontSize: 11 }}>Furnizor</th>
                      <th style={{ padding: "6px 8px", textAlign: "right", color: C.text2, fontWeight: 600, fontSize: 11 }}>Cant.</th>
                      <th style={{ padding: "6px 8px", textAlign: "right", color: C.text2, fontWeight: 600, fontSize: 11 }}>Preț</th>
                      <th style={{ padding: "6px 8px", textAlign: "right", color: C.text2, fontWeight: 600, fontSize: 11 }}>Total</th>
                      <th style={{ padding: "6px 8px", textAlign: "left", color: C.text2, fontWeight: 600, fontSize: 11 }}>Data</th>
                      <th style={{ width: 30 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parts.map((p, i) => (
                      <tr key={i} style={{ borderTop: "1px solid " + C.border }}>
                        <td style={{ padding: "6px 8px", color: C.text, fontWeight: 500 }}>{p.name}</td>
                        <td style={{ padding: "6px 8px", color: C.text2 }}>{p.supplier || "—"}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", color: C.text }}>{p.quantity}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", color: C.text }}>{parseFloat(p.price).toFixed(2)} lei</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: C.accent }}>{((p.quantity || 1) * (p.price || 0)).toFixed(2)} lei</td>
                        <td style={{ padding: "6px 8px", color: C.text2, fontSize: 11 }}>{p.date || "—"}</td>
                        <td style={{ padding: "4px" }}><button style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 14 }} onClick={() => removePart(i)}>×</button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "2px solid " + C.border, background: C.card2 }}>
                      <td colSpan="4" style={{ padding: "6px 8px", fontWeight: 700, color: C.text, textAlign: "right" }}>TOTAL:</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: C.accent }}>{partsTotal.toFixed(2)} lei</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: C.muted, marginRight: "auto" }}>
            {selectedCount + extraTasks.length} taskuri{orderType === "interventie" && parts.length > 0 ? ` + ${parts.length} piese (${partsTotal.toFixed(2)} lei)` : ""}
          </span>
          <button style={{ ...S.btn, ...S.btnGhost }} onClick={onClose}>Anulează</button>
          <button style={{ ...S.btn, background: orderType === "interventie" ? "#7c3aed" : C.orange, color: C.white, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleCreate}>
            {saving ? "Se creează..." : orderType === "interventie" ? "Salvează Intervenția" : "Trimite la Service"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SETTINGS PAGE
   ═══════════════════════════════════════════════════════════ */
const EVENT_LABELS = {
  service_created: "Remorcă trimisă la service",
  service_completed: "Service finalizat",
  service_reminder: "Reminder service > 2 zile",
  pv_predare: "PV predare completat",
  pv_returnare: "PV returnare completat",
  trailer_added: "Remorcă adăugată",
  service_approved: "Service aprobat",
  trailer_active: "Remorcă activă",
  pv_inspectie: "PV Inspecție",
};
const EVENT_TYPES = Object.keys(EVENT_LABELS);
const ROLE_LABELS = { admin: "Admin", dispecer: "Dispecer", mecanic: "Mecanic", transportator: "Transportator" };
const ROLE_COLORS = { admin: "#dc2626", dispecer: "#2563eb", mecanic: "#16a34a", transportator: "#ea580c" };

function SettingsPage({ user }) {
  const [tab, setTab] = useState("notifications");
  const [notifs, setNotifs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newEventType, setNewEventType] = useState("service_created");
  const [showUserModal, setShowUserModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: "", password: "", full_name: "", role: "mecanic", email: "", phone: "", telegram_chat_id: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [fleetSettings, setFleetSettings] = useState([]);
  const [fleetDirty, setFleetDirty] = useState({});
  const [fleetSaving, setFleetSaving] = useState(false);
  const [tgStatus, setTgStatus] = useState(null);
  const [tgTesting, setTgTesting] = useState({});
  const [smsLog, setSmsLog] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogPage, setEmailLogPage] = useState(1);
  const [emailLogTotal, setEmailLogTotal] = useState(0);
  const [emailLogPages, setEmailLogPages] = useState(0);
  const [emailLogFilter, setEmailLogFilter] = useState({ event_type: "", status: "", to_email: "" });
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  const [smsTestPhone, setSmsTestPhone] = useState("");
  const [smsTesting, setSmsTesting] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const loadBackupStatus = useCallback(async () => {
    try {
      setBackupLoading(true);
      const data = await api.get("/api/settings/backup-status");
      if (data) setBackupStatus(data);
    } catch (ex) { console.error(ex); }
    finally { setBackupLoading(false); }
  }, []);

  const loadNotifs = useCallback(async () => {
    try {
      const data = await api.get("/api/settings/notifications");
      if (data) setNotifs(data);
    } catch (ex) { console.error(ex); }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await api.get("/api/auth/users");
      if (data) setUsers(data);
    } catch (ex) { console.error(ex); }
  }, []);

  const loadFleetSettings = useCallback(async () => {
    try {
      const data = await api.get("/api/settings/fleet");
      if (data) { setFleetSettings(data); setFleetDirty({}); }
    } catch (ex) { console.error(ex); }
  }, []);

  const loadSmsLog = useCallback(async () => {
    try {
      const data = await api.get("/api/settings/sms-log");
      if (data) setSmsLog(data);
    } catch (ex) { console.error(ex); }
  }, []);

  const loadEmailLog = useCallback(async (page = 1, filters = {}) => {
    setEmailLogLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.event_type) params.append("event_type", filters.event_type);
      if (filters.status) params.append("status", filters.status);
      if (filters.to_email) params.append("to_email", filters.to_email);
      const data = await api.get("/api/settings/email-log?" + params.toString());
      if (data) { setEmailLogs(data.logs || []); setEmailLogTotal(data.total || 0); setEmailLogPages(data.totalPages || 0); setEmailLogPage(data.page || 1); }
    } catch (ex) { console.error(ex); }
    setEmailLogLoading(false);
  }, []);

  const loadTgStatus = useCallback(async () => {
    try {
      const data = await api.get("/api/settings/telegram/status");
      if (data) setTgStatus(data);
    } catch (ex) { console.error(ex); }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadNotifs(), loadUsers(), loadFleetSettings(), loadTgStatus(), loadSmsLog(), loadEmailLog(), loadBackupStatus()]).then(() => setLoading(false));
  }, [loadNotifs, loadUsers, loadFleetSettings, loadTgStatus, loadSmsLog, loadEmailLog, loadBackupStatus]);

  const addNotif = async (data) => {
    if (data) {
      try {
        await api.post("/api/settings/notifications", data);
        loadNotifs();
      } catch (ex) { alert(ex.message || "Eroare"); }
      return;
    }
    if (!newEmail || !newEmail.includes("@")) return;
    try {
      await api.post("/api/settings/notifications", { event_type: newEventType, email: newEmail, email_enabled: true });
      setNewEmail("");
      loadNotifs();
    } catch (ex) { alert(ex.message || "Eroare"); }
  };

  const toggleSmsForNotif = async (id, currentVal) => {
    try {
      await api.put("/api/settings/notifications/" + id, { sms_enabled: !currentVal });
      loadNotifs();
    } catch (ex) { console.error(ex); }
  };

  const toggleEmailForNotif = async (id, currentVal) => {
    try {
      await api.put("/api/settings/notifications/" + id, { email_enabled: !currentVal });
      loadNotifs();
    } catch (ex) { console.error(ex); }
  };

  const sendTestSms = async () => {
    if (!smsTestPhone) return;
    setSmsTesting(true);
    try {
      await api.post("/api/settings/sms-test", { phone: smsTestPhone });
      alert("SMS test trimis cu succes!");
      loadSmsLog();
    } catch (ex) { alert(ex.message || "Eroare trimitere SMS test"); }
    setSmsTesting(false);
  };

  const toggleNotif = async (id, isActive) => {
    try {
      await api.put(`/api/settings/notifications/${id}`, { is_active: !isActive });
      loadNotifs();
    } catch (ex) { console.error(ex); }
  };

  const deleteNotif = async (id) => {
    if (!window.confirm("Ștergi această setare?")) return;
    try {
      await api.del(`/api/settings/notifications/${id}`);
      loadNotifs();
    } catch (ex) { console.error(ex); }
  };

  const openUserModal = (u) => {
    if (u) {
      setEditUser(u);
      setUserForm({ username: u.username, password: "", full_name: u.full_name || "", role: u.role, email: u.email || "", phone: u.phone || "", telegram_chat_id: u.telegram_chat_id || "" });
    } else {
      setEditUser(null);
      setUserForm({ username: "", password: "", full_name: "", role: "mecanic", email: "", phone: "", telegram_chat_id: "" });
    }
    setErr("");
    setShowUserModal(true);
  };

  const saveUser = async () => {
    if (!userForm.username || !userForm.full_name || !userForm.role) { setErr("Completează câmpurile obligatorii."); return; }
    if (!editUser && !userForm.password) { setErr("Parola e obligatorie pentru user nou."); return; }
    setSaving(true); setErr("");
    try {
      if (editUser) {
        const body = { ...userForm };
        if (!body.password) delete body.password;
        await api.put(`/api/auth/users/${editUser.id}`, body);
      } else {
        await api.post("/api/auth/register", userForm);
      }
      setShowUserModal(false);
      loadUsers();
    } catch (ex) { setErr(ex.message || "Eroare"); }
    setSaving(false);
  };

  const toggleUser = async (id) => {
    try {
      await api.put(`/api/auth/users/${id}/toggle`, {});
      loadUsers();
    } catch (ex) { console.error(ex); }
  };

  const saveFleetSettings = async () => {
    const changes = Object.entries(fleetDirty).map(([key, value]) => ({ key, value }));
    if (changes.length === 0) return;
    setFleetSaving(true);
    try {
      const data = await api.put("/api/settings/fleet", { settings: changes });
      if (data) { setFleetSettings(data); setFleetDirty({}); }
    } catch (ex) { alert(ex.message || "Eroare salvare"); }
    setFleetSaving(false);
  };

  const sendTgTest = async (userId) => {
    setTgTesting(p => ({ ...p, [userId]: true }));
    try {
      await api.post(`/api/settings/telegram/test/${userId}`, {});
      setTgTesting(p => ({ ...p, [userId]: "ok" }));
      setTimeout(() => setTgTesting(p => ({ ...p, [userId]: false })), 3000);
    } catch (ex) {
      alert(ex.message || "Eroare trimitere test");
      setTgTesting(p => ({ ...p, [userId]: false }));
    }
  };

  const tabs = [{ id: "notifications", label: "Notificări" }, { id: "sms", label: "SMS" }, { id: "users", label: "Utilizatori" }, { id: "telegram", label: "Telegram" }, { id: "fleet", label: "Parametri Flotă" }, { id: "emaillog", label: "Email Log" }, { id: "backup", label: "Backup" }];

  // Group notifications by event type
  const grouped = {};
  EVENT_TYPES.forEach(et => { grouped[et] = notifs.filter(n => n.event_type === et); });

  return (
    <div>
      <div className="lm-page-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text }}>Setări</h2>
      </div>

      <div className="lm-tabs" style={S.tabs}>
        {tabs.map(t => (
          <button key={t.id} style={{ ...S.tab, ...(tab === t.id ? S.tabActive : {}) }} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {loading && <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Se încarcă...</div>}

      {/* ═══ TAB: NOTIFICĂRI ═══ */}
      {!loading && tab === "notifications" && (
        <div>
          {/* Add new */}
          <div style={{ ...S.statCard, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 10 }}>Adaugă destinatar notificare</div>
            <div className="lm-filters" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <select style={{ ...S.input, width: "auto", marginBottom: 0, minWidth: 180 }}
                value={newEventType} onChange={e => setNewEventType(e.target.value)}>
                {EVENT_TYPES.map(et => <option key={et} value={et}>{EVENT_LABELS[et]}</option>)}
              </select>
              <input style={{ ...S.input, marginBottom: 0, flex: 1, minWidth: 200 }}
                type="email" placeholder="email@exemplu.com" value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addNotif()} />
              <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, padding: "10px 18px" }} onClick={() => addNotif()}>Adaugă</button>
            </div>
          </div>

          {/* Info about auto recipients */}
          <div style={{ ...S.statCard, padding: 14, marginBottom: 16, borderLeft: "4px solid " + C.accent, background: C.accent + "08" }}>
            <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.7 }}>
              <b>Destinatari automati:</b><br/>
              &bull; <b>Remorcă trimisă la service</b> — mecanicul asignat primește automat Email + SMS<br/>
              &bull; <b>Service finalizat</b> — dispecerul care a creat comanda primește automat Email + SMS<br/>
              &bull; <b>Reminder service</b> — mecanicul primește automat Email + SMS + Telegram<br/>
              &bull; <b>PV predare/returnare</b> — transportatorul primește automat emailul din PV
            </div>
          </div>

          {/* Grouped by event */}
          {EVENT_TYPES.map(et => (
            <div key={et} style={{ ...S.statCard, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 10 }}>{EVENT_LABELS[et]}</div>
              {grouped[et].length === 0 ? (
                <div style={{ fontSize: 13, color: C.text2, fontStyle: "italic" }}>Niciun destinatar suplimentar configurat</div>
              ) : (
                <div>
                  <div style={{ display: "flex", gap: 10, padding: "6px 0", borderBottom: "2px solid " + C.border, fontSize: 12, fontWeight: 700, color: C.text2 }}>
                    <div style={{ width: 40, textAlign: "center" }}>Activ</div>
                    <div style={{ flex: 1 }}>Email</div>
                    <div style={{ width: 50, textAlign: "center" }}>Email</div>
                    <div style={{ width: 50, textAlign: "center" }}>SMS</div>
                    <div style={{ width: 30 }}></div>
                  </div>
                  {grouped[et].map(n => (
                    <div key={n.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid " + C.border }}>
                      <div style={{ width: 40, textAlign: "center" }}>
                        <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: 2 }}
                          onClick={() => toggleNotif(n.id, n.is_active)}>
                          {n.is_active ? String.fromCodePoint(9989) : String.fromCodePoint(11036)}
                        </button>
                      </div>
                      <div style={{ flex: 1, fontSize: 14, color: n.is_active ? C.text : C.muted, textDecoration: n.is_active ? "none" : "line-through" }}>
                        {n.email || n.phone || "—"}
                      </div>
                      <div style={{ width: 50, textAlign: "center" }}>
                        <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 2, opacity: n.is_active ? 1 : 0.4 }}
                          onClick={() => toggleEmailForNotif(n.id, n.email_enabled)}
                          title={n.email_enabled ? "Email activ" : "Email inactiv"}>
                          {n.email_enabled && n.email ? String.fromCodePoint(9993) + String.fromCodePoint(65039) : "—"}
                        </button>
                      </div>
                      <div style={{ width: 50, textAlign: "center" }}>
                        <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 2, opacity: n.is_active ? 1 : 0.4 }}
                          onClick={() => toggleSmsForNotif(n.id, n.sms_enabled)}
                          title={n.sms_enabled ? "SMS activ" : "SMS inactiv"}>
                          {n.sms_enabled ? String.fromCodePoint(128241) : "—"}
                        </button>
                      </div>
                      <div style={{ width: 30 }}>
                        <button style={{ background: "none", border: "none", cursor: "pointer", color: C.red, fontSize: 16, padding: 2 }}
                          onClick={() => deleteNotif(n.id)}>x</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ═══ TAB: SMS ═══ */}
      {!loading && tab === "sms" && (() => {
        const smsGrouped = {};
        EVENT_TYPES.forEach(et => { smsGrouped[et] = notifs.filter(n => n.event_type === et && n.phone); });
        return (
        <div>
          {/* SMS Status + Test */}
          <div style={{ ...S.statCard, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: C.green, boxShadow: "0 0 8px " + C.green + "66" }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>SMS SendSMS.ro</div>
            </div>
            <div style={{ fontSize: 13, color: C.text2, marginBottom: 12 }}>
              Sender: <b>LaMaritec</b> &bull; Provider: SendSMS.ro
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input style={{ ...S.input, marginBottom: 0, maxWidth: 200 }}
                placeholder="07xx xxx xxx" value={smsTestPhone}
                onChange={e => setSmsTestPhone(e.target.value)} />
              <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, padding: "8px 16px" }}
                disabled={smsTesting || !smsTestPhone}
                onClick={sendTestSms}>
                {smsTesting ? "Se trimite..." : "Trimite SMS Test"}
              </button>
            </div>
          </div>

          {/* Destinatari automati info */}
          <div style={{ ...S.statCard, padding: 14, marginBottom: 16, borderLeft: "4px solid " + C.accent, background: C.accent + "08" }}>
            <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.7 }}>
              <b>Destinatari SMS automati:</b><br/>
              &bull; <b>Remorcă la service</b> — mecanicul asignat (din Users)<br/>
              &bull; <b>Service finalizat</b> — dispecerul care a creat comanda (din Users)<br/>
              &bull; <b>Service aprobat/respins</b> — mecanicul asignat (din Users)<br/>
              &bull; <b>Reminder service</b> — mecanicul asignat (din Users)<br/>
              Mai jos poți adăuga <b>numere suplimentare</b> per eveniment.
            </div>
          </div>

          {/* Add SMS recipient */}
          <div style={{ ...S.statCard, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 10 }}>Adaugă destinatar SMS suplimentar</div>
            <div className="lm-filters" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <select style={{ ...S.input, width: "auto", marginBottom: 0, minWidth: 180 }}
                value={newEventType} onChange={e => setNewEventType(e.target.value)}>
                {EVENT_TYPES.map(et => <option key={et} value={et}>{EVENT_LABELS[et]}</option>)}
              </select>
              <input style={{ ...S.input, marginBottom: 0, flex: 1, minWidth: 160 }}
                placeholder="+40 7xx xxx xxx" value={smsTestPhone}
                onChange={e => setSmsTestPhone(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && smsTestPhone) { addNotif({ event_type: newEventType, phone: smsTestPhone, sms_enabled: true, email_enabled: false }); setSmsTestPhone(""); }}} />
              <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, padding: "10px 18px" }}
                onClick={() => { if (smsTestPhone) { addNotif({ event_type: newEventType, phone: smsTestPhone, sms_enabled: true, email_enabled: false }); setSmsTestPhone(""); }}}>Adaugă</button>
            </div>
          </div>

          {/* Grouped by event */}
          {EVENT_TYPES.map(et => (
            <div key={et} style={{ ...S.statCard, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>{EVENT_LABELS[et]}</div>
              {smsGrouped[et].length === 0 ? (
                <div style={{ fontSize: 13, color: C.text2, fontStyle: "italic" }}>Niciun număr suplimentar configurat</div>
              ) : (
                smsGrouped[et].map(n => (
                  <div key={n.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid " + C.border }}>
                    <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: 4 }}
                      onClick={() => toggleNotif(n.id, n.is_active)}
                      title={n.is_active ? "Dezactivează" : "Activează"}>
                      {n.is_active ? String.fromCodePoint(9989) : String.fromCodePoint(11036)}
                    </button>
                    <span style={{ flex: 1, fontSize: 14, fontFamily: "monospace", color: n.is_active ? C.text : C.muted, textDecoration: n.is_active ? "none" : "line-through" }}>{n.phone}</span>
                    <button style={{ background: "none", border: "none", cursor: "pointer", color: C.red, fontSize: 16, padding: 4 }}
                      onClick={() => deleteNotif(n.id)} title="Șterge">x</button>
                  </div>
                ))
              )}
            </div>
          ))}

          {/* SMS Log */}
          <div style={{ ...S.statCard, padding: 16, marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Istoric SMS (ultimele 50)</div>
              <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "4px 12px", fontSize: 12 }}
                onClick={loadSmsLog}>Reincarcă</button>
            </div>
            {smsLog.length === 0 ? (
              <div style={{ fontSize: 13, color: C.text2, fontStyle: "italic" }}>Niciun SMS trimis.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ ...S.table, fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={S.th}>Data</th>
                      <th style={S.th}>Telefon</th>
                      <th style={S.th}>Mesaj</th>
                      <th style={S.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {smsLog.map(s => (
                      <tr key={s.id}>
                        <td style={{ ...S.td, whiteSpace: "nowrap", fontSize: 12 }}>{new Date(s.created_at).toLocaleString("ro-RO")}</td>
                        <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12 }}>{s.phone}</td>
                        <td style={{ ...S.td, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.message}</td>
                        <td style={S.td}>
                          <span style={S.badge(s.status === "sent" ? C.green : s.status === "disabled" ? C.orange : C.red)}>
                            {s.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* ═══ TAB: UTILIZATORI ═══ */}
      {!loading && tab === "users" && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary }} onClick={() => openUserModal(null)}>+ Adaugă utilizator</button>
          </div>

          <div className="lm-desktop-table" style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Username</th>
                  <th style={S.th}>Nume</th>
                  <th style={S.th}>Rol</th>
                  <th style={S.th}>Email</th>
                  <th style={S.th}>Telefon</th>
                  <th style={S.th}>Telegram</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ opacity: u.active === false ? 0.5 : 1 }}>
                    <td style={{ ...S.td, fontWeight: 600, borderRadius: "8px 0 0 8px" }}>{u.username}</td>
                    <td style={S.td}>{u.full_name || "—"}</td>
                    <td style={S.td}><span style={S.badge(ROLE_COLORS[u.role] || C.accent)}>{ROLE_LABELS[u.role] || u.role}</span></td>
                    <td style={S.td}>{u.email || "—"}</td>
                    <td style={S.td}>{u.phone || "—"}</td>
                    <td style={{ ...S.td, textAlign: "center" }}>
                      {u.telegram_chat_id ? (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                          <span style={{ color: C.green, fontWeight: 700, fontSize: 16 }}>✅</span>
                          <button className="lm-btn" style={{ ...S.btn, padding: "2px 8px", fontSize: 10, background: tgTesting[u.id] === "ok" ? C.green + "22" : C.accent + "15", color: tgTesting[u.id] === "ok" ? C.green : C.accent, border: `1px solid ${tgTesting[u.id] === "ok" ? C.green : C.accent}33` }}
                            disabled={tgTesting[u.id] === true} onClick={() => sendTgTest(u.id)}>
                            {tgTesting[u.id] === true ? "..." : tgTesting[u.id] === "ok" ? "Trimis!" : "Test"}
                          </button>
                        </div>
                      ) : <span style={{ color: C.red, fontSize: 16 }}>❌</span>}
                    </td>
                    <td style={S.td}><span style={S.badge(u.active !== false ? C.green : C.red)}>{u.active !== false ? "Activ" : "Inactiv"}</span></td>
                    <td style={{ ...S.td, borderRadius: "0 8px 8px 0" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "4px 10px", fontSize: 12 }} onClick={() => openUserModal(u)}>Editează</button>
                        <button className="lm-btn" style={{ ...S.btn, padding: "4px 10px", fontSize: 12, background: u.active !== false ? C.red + "22" : C.green + "22", color: u.active !== false ? C.red : C.green, border: `1px solid ${u.active !== false ? C.red : C.green}44` }}
                          onClick={() => toggleUser(u.id)}>{u.active !== false ? "Dezactivează" : "Activează"}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lm-mobile-cards" style={{ display: "none", flexDirection: "column", gap: 8 }}>
            {users.map(u => (
              <div key={u.id} style={{ ...S.statCard, padding: 14, opacity: u.active === false ? 0.5 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{u.full_name || u.username}</span>
                    <span style={{ ...S.badge(ROLE_COLORS[u.role] || C.accent), marginLeft: 8 }}>{ROLE_LABELS[u.role] || u.role}</span>
                  </div>
                  <span style={S.badge(u.active !== false ? C.green : C.red)}>{u.active !== false ? "Activ" : "Inactiv"}</span>
                </div>
                <div style={{ fontSize: 13, color: C.text2 }}>{u.email || "—"} · {u.phone || "—"} {u.telegram_chat_id ? " · 📱TG" : ""}</div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "6px 14px", fontSize: 13, flex: 1 }} onClick={() => openUserModal(u)}>Editează</button>
                  <button className="lm-btn" style={{ ...S.btn, padding: "6px 14px", fontSize: 13, background: u.active !== false ? C.red + "22" : C.green + "22", color: u.active !== false ? C.red : C.green }}
                    onClick={() => toggleUser(u.id)}>{u.active !== false ? "Dezactivează" : "Activează"}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ TAB: TELEGRAM ═══ */}
      {!loading && tab === "telegram" && (
        <div>
          {/* Status Bot */}
          <div style={{ ...S.statCard, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: tgStatus?.connected ? C.green : C.red, boxShadow: `0 0 8px ${tgStatus?.connected ? C.green : C.red}66` }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
                {tgStatus?.connected ? "Bot Conectat" : "Bot Deconectat"}
              </div>
            </div>
            {tgStatus?.connected && tgStatus.bot && (
              <div style={{ fontSize: 13, color: C.text2, marginBottom: 8 }}>
                Bot: <b>@{tgStatus.bot.username}</b> ({tgStatus.bot.first_name})
              </div>
            )}
            {!tgStatus?.connected && tgStatus?.error && (
              <div style={{ fontSize: 13, color: C.red, marginBottom: 8 }}>{tgStatus.error}</div>
            )}
            <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "6px 14px", fontSize: 12 }}
              onClick={loadTgStatus}>Verifică din nou</button>
          </div>

          {/* Link & instrucțiuni */}
          <div style={{ ...S.statCard, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 10 }}>Cum activezi notificările Telegram</div>
            <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.8 }}>
              <b>Trimite acest link colegilor:</b>
              <div style={{ margin: "8px 0", padding: "10px 14px", background: C.accent + "10", borderRadius: 8, border: `1px solid ${C.accent}30`, display: "flex", alignItems: "center", gap: 10 }}>
                <a href="https://t.me/lamaritec_bot" target="_blank" rel="noopener noreferrer"
                  style={{ color: C.accent, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>
                  https://t.me/lamaritec_bot
                </a>
                <button className="lm-btn" style={{ ...S.btn, padding: "4px 12px", fontSize: 11, background: C.accent + "15", color: C.accent, border: `1px solid ${C.accent}33` }}
                  onClick={() => { navigator.clipboard.writeText("https://t.me/lamaritec_bot"); }}>
                  Copiază
                </button>
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                  <span style={{ background: C.accent, color: "#fff", borderRadius: "50%", width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>1</span>
                  <span>Colegul deschide linkul de mai sus și apasă <b>Start</b> în Telegram.</span>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                  <span style={{ background: C.accent, color: "#fff", borderRadius: "50%", width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>2</span>
                  <span>Botul îi arată <b>Chat ID-ul</b> — un număr pe care ți-l trimite.</span>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ background: C.accent, color: "#fff", borderRadius: "50%", width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>3</span>
                  <span>Tu pui Chat ID-ul la userul respectiv din tab-ul <b>Utilizatori</b> (câmpul Telegram Chat ID).</span>
                </div>
              </div>
            </div>
          </div>

          {/* Users with Telegram status */}
          <div style={{ ...S.statCard, padding: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 12 }}>Stare Notificări Telegram per Utilizator</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ ...S.table, fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={S.th}>Utilizator</th>
                    <th style={S.th}>Rol</th>
                    <th style={S.th}>Chat ID</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th}>Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td style={{ ...S.td, fontWeight: 600 }}>{u.full_name || u.username}</td>
                      <td style={S.td}><span style={S.badge(ROLE_COLORS[u.role] || C.accent)}>{ROLE_LABELS[u.role] || u.role}</span></td>
                      <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12 }}>{u.telegram_chat_id || <span style={{ color: C.muted, fontStyle: "italic" }}>nesetat</span>}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        {u.telegram_chat_id ? <span style={{ color: C.green, fontSize: 16 }}>✅</span> : <span style={{ color: C.red, fontSize: 16 }}>❌</span>}
                      </td>
                      <td style={S.td}>
                        <div style={{ display: "flex", gap: 6 }}>
                          {u.telegram_chat_id && (
                            <button className="lm-btn" style={{ ...S.btn, padding: "4px 12px", fontSize: 12, background: tgTesting[u.id] === "ok" ? C.green : C.accent, color: "#fff" }}
                              disabled={tgTesting[u.id] === true}
                              onClick={() => sendTgTest(u.id)}>
                              {tgTesting[u.id] === true ? "Se trimite..." : tgTesting[u.id] === "ok" ? "Trimis!" : "Trimite Test"}
                            </button>
                          )}
                          <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "4px 12px", fontSize: 12 }}
                            onClick={() => openUserModal(u)}>
                            {u.telegram_chat_id ? "Editează" : "Adaugă Chat ID"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB: PARAMETRI FLOTĂ ═══ */}
      {!loading && tab === "fleet" && (() => {
        const groups = { tires: "Anvelope", brakes: "Frâne" };
        const groupedFleet = {};
        fleetSettings.forEach(s => {
          if (!groupedFleet[s.setting_group]) groupedFleet[s.setting_group] = [];
          groupedFleet[s.setting_group].push(s);
        });
        const hasDirty = Object.keys(fleetDirty).length > 0;
        return (
          <div>
            <div style={{ ...S.statCard, padding: 16, marginBottom: 16, borderLeft: `4px solid ${C.accent}` }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>Cum funcționează calculul de uzură</div>
              <div style={{ fontSize: 12, color: C.text2, lineHeight: 1.7 }}>
                La returnare, sistemul compară uzura reală (mm pierduți) cu uzura normală pentru km parcurși.<br />
                <span style={{ color: C.red }}>Excesivă</span> = a uzat mult mai mult decât normal &bull;
                <span style={{ color: C.green }}> Normală</span> = uzură conform așteptărilor &bull;
                <span style={{ color: C.accent }}> Mică</span> = a avut grijă de echipament
              </div>
            </div>
            {Object.entries(groupedFleet).map(([group, settings]) => (
              <div key={group} style={{ ...S.statCard, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.accent, marginBottom: 12 }}>{groups[group] || group}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
                  {settings.map(s => {
                    const val = fleetDirty.hasOwnProperty(s.setting_key) ? fleetDirty[s.setting_key] : s.setting_value;
                    const isDirty = fleetDirty.hasOwnProperty(s.setting_key);
                    return (
                      <div key={s.setting_key}>
                        <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>{s.setting_label}</label>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input style={{ ...S.input, marginBottom: 0, flex: 1, borderColor: isDirty ? C.orange : undefined }}
                            type="number" step="any" value={val}
                            onChange={e => setFleetDirty(d => ({ ...d, [s.setting_key]: e.target.value }))} />
                          {s.setting_unit && <span style={{ fontSize: 12, color: C.text2, minWidth: 24 }}>{s.setting_unit}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              {hasDirty && <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost }} onClick={() => setFleetDirty({})}>Anulează</button>}
              <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, opacity: !hasDirty || fleetSaving ? 0.5 : 1 }}
                disabled={!hasDirty || fleetSaving} onClick={saveFleetSettings}>
                {fleetSaving ? "Se salvează..." : "Salvează Parametrii"}
              </button>
            </div>
          </div>
        );
      })()}


      {tab === "emaillog" && (() => {
        const EVENT_TYPE_LABELS = { pv_predare: "PV Predare", pv_returnare: "PV Returnare", pv_inspectie: "PV Inspecție", service_created: "Service creat", service_completed: "Service finalizat", service_approved: "Service aprobat", service_rejected: "Service respins", service_reminder: "Reminder service", trailer_active: "Remorcă activă", trailer_added: "Remorcă adăugată", prepare_return: "Pregătire returnare", cancel_return: "Anulare returnare", cancel_handover: "Anulare predare", assign_client: "Alocare client" };
        return (
          <div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
              <select value={emailLogFilter.event_type} onChange={e => { const f = { ...emailLogFilter, event_type: e.target.value }; setEmailLogFilter(f); loadEmailLog(1, f); }}
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid " + C.border, fontSize: 13, background: C.card, color: C.text }}>
                <option value="">Toate tipurile</option>
                {Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select value={emailLogFilter.status} onChange={e => { const f = { ...emailLogFilter, status: e.target.value }; setEmailLogFilter(f); loadEmailLog(1, f); }}
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid " + C.border, fontSize: 13, background: C.card, color: C.text }}>
                <option value="">Toate statusurile</option>
                <option value="sent">Trimis</option>
                <option value="failed">Eșuat</option>
              </select>
              <input placeholder="Caută email..." value={emailLogFilter.to_email} onChange={e => setEmailLogFilter(f => ({ ...f, to_email: e.target.value }))}
                onKeyDown={e => { if (e.key === "Enter") loadEmailLog(1, emailLogFilter); }}
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid " + C.border, fontSize: 13, background: C.card, color: C.text, flex: 1, minWidth: 150 }} />
              <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, padding: "6px 16px" }} onClick={() => loadEmailLog(1, emailLogFilter)}>Filtrează</button>
            </div>
            <div style={{ fontSize: 13, color: C.text2, marginBottom: 8 }}>{emailLogTotal} email-uri în total</div>
            {emailLogLoading ? <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Se încarcă...</div> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: C.bg }}>
                      <th style={S.th}>Data/Ora</th>
                      <th style={S.th}>Către</th>
                      <th style={S.th}>Subiect</th>
                      <th style={S.th}>Tip</th>
                      <th style={S.th}>Status</th>
                      <th style={S.th}>Atașament</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLogs.map(log => (
                      <tr key={log.id} style={{ borderBottom: "1px solid " + C.border }}>
                        <td style={{ ...S.td, whiteSpace: "nowrap" }}>{new Date(log.created_at).toLocaleString("ro-RO")}</td>
                        <td style={{ ...S.td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{log.to_email}</td>
                        <td style={{ ...S.td, maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" }}>{log.subject}</td>
                        <td style={S.td}><span style={{ background: C.blue + "20", color: C.blue, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{EVENT_TYPE_LABELS[log.event_type] || log.event_type || "—"}</span></td>
                        <td style={S.td}><span style={{ background: log.status === "sent" ? C.green + "20" : C.red + "20", color: log.status === "sent" ? C.green : C.red, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{log.status === "sent" ? "Trimis" : "Eșuat"}</span>
                          {log.error_message && <div style={{ fontSize: 11, color: C.red, marginTop: 2 }}>{log.error_message}</div>}
                        </td>
                        <td style={S.td}>{log.has_attachment ? "📎" : "—"}</td>
                      </tr>
                    ))}
                    {emailLogs.length === 0 && <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: C.text2, padding: 30 }}>Niciun email înregistrat.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
            {emailLogPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16 }}>
                <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "4px 12px", opacity: emailLogPage <= 1 ? 0.4 : 1 }}
                  disabled={emailLogPage <= 1} onClick={() => loadEmailLog(emailLogPage - 1, emailLogFilter)}>← Înapoi</button>
                <span style={{ padding: "6px 12px", fontSize: 13, color: C.text2 }}>Pagina {emailLogPage} din {emailLogPages}</span>
                <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost, padding: "4px 12px", opacity: emailLogPage >= emailLogPages ? 0.4 : 1 }}
                  disabled={emailLogPage >= emailLogPages} onClick={() => loadEmailLog(emailLogPage + 1, emailLogFilter)}>Înainte →</button>
              </div>
            )}
          </div>
        );
      })()}


      {/* ═══ TAB: BACKUP ═══ */}
      {tab === "backup" && (() => {
        if (backupLoading) return <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>Se încarcă...</div>;
        if (!backupStatus || !backupStatus.configured) return (
          <div style={{ textAlign: "center", padding: 40, color: C.text2 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>💾</div>
            <div>Niciun backup configurat sau rulat încă.</div>
          </div>
        );
        const bs = backupStatus;
        const freshColor = bs.fresh ? "#16a34a" : "#dc2626";
        const freshLabel = bs.fresh ? "OK" : "Vechi (" + bs.age_hours + "h)";
        const cardStyle = { background: "#f8fafc", borderRadius: 10, padding: 20, border: "1px solid #e2e8f0" };
        return (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: 0 }}>Backup Status</h3>
              <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, padding: "6px 16px" }} onClick={loadBackupStatus}>Refresh</button>
            </div>
            <div className="lm-stat-cards" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
              <div style={cardStyle}>
                <div style={{ fontSize: 13, color: C.text2, marginBottom: 8 }}>💿 Baza de date</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{bs.db_size}</div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 4 }}>{new Date(bs.db_date).toLocaleString("ro-RO")}</div>
                <div style={{ marginTop: 8, display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600, color: "#fff", background: freshColor }}>{freshLabel}</div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 13, color: C.text2, marginBottom: 8 }}>📦 Full Backup</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{bs.full_size || bs.app_size}</div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 4 }}>{new Date(bs.full_date || bs.app_date).toLocaleString("ro-RO")}</div>
                <div style={{ marginTop: 8, display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600, color: "#fff", background: freshColor }}>{freshLabel}</div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 13, color: C.text2, marginBottom: 8 }}>☁️ Backblaze B2</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{bs.b2_uploaded ? "Sincronizat" : "Nesincronizat"}</div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 4 }}>{bs.b2_path}</div>
                <div style={{ marginTop: 8, display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600, color: "#fff", background: bs.b2_uploaded ? "#16a34a" : "#dc2626" }}>{bs.b2_uploaded ? "OK" : "FAIL"}</div>
              </div>
            </div>
            <div style={{ marginTop: 16, fontSize: 12, color: C.text2 }}>
              Fișiere: {bs.db_file} / {bs.full_file || bs.app_file} — Cron zilnic la 04:00
            </div>
          </div>
        );
      })()}
      {/* User modal */}
      {showUserModal && (
        <div style={S.overlay} onClick={() => setShowUserModal(false)}>
          <div className="lm-modal" style={{ ...S.modal, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ color: C.text, margin: 0 }}>{editUser ? "Editează Utilizator" : "Adaugă Utilizator"}</h3>
              <button style={{ ...S.btn, background: "transparent", color: C.text2, fontSize: 20 }} onClick={() => setShowUserModal(false)}>✕</button>
            </div>
            {err && <div style={{ color: C.red, marginBottom: 12, padding: "8px 12px", background: C.red + "15", borderRadius: 8, fontSize: 14 }}>{err}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Username *</label>
                <input style={S.input} value={userForm.username} onChange={e => setUserForm(f => ({ ...f, username: e.target.value }))} disabled={!!editUser} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>{editUser ? "Parolă nouă (opțional)" : "Parolă *"}</label>
                <input style={S.input} type="password" value={userForm.password} onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Nume complet *</label>
                <input style={S.input} value={userForm.full_name} onChange={e => setUserForm(f => ({ ...f, full_name: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Rol *</label>
                <select style={S.input} value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Email</label>
                <input style={S.input} type="email" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Telefon</label>
                <input style={S.input} value={userForm.phone} onChange={e => setUserForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12, gridColumn: "1 / -1" }}>
                <label style={{ fontSize: 12, color: C.text2, display: "block", marginBottom: 4 }}>Telegram Chat ID</label>
                <input style={S.input} placeholder="Ex: 8305388877 (trimite /start la bot)" value={userForm.telegram_chat_id}
                  onChange={e => setUserForm(f => ({ ...f, telegram_chat_id: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button className="lm-btn" style={{ ...S.btn, ...S.btnGhost }} onClick={() => setShowUserModal(false)}>Anulează</button>
              <button className="lm-btn" style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={saveUser}>
                {saving ? "..." : editUser ? "Salvează" : "Creează"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════════════════ */
export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lm_user")); } catch { return null; }
  });
  const [page, setPage] = useState("dashboard");
  const [trailers, setTrailers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editTrailer, setEditTrailer] = useState(null);
  const [detailKey, setDetailKey] = useState(0);
  const [pvWizard, setPvWizard] = useState(null); // { trailer, type }
  const [clients, setClients] = useState([]);
  const [showDifferences, setShowDifferences] = useState(null); // pvId for differences report
  const [showCreateService, setShowCreateService] = useState(null); // { trailerId, pvId, differences }
  // publishTrailer state removed — publish flow moved to CARGO
  const [serviceReviewCount, setServiceReviewCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tractors, setTractors] = useState([]);
  const [mechServiceOrderId, setMechServiceOrderId] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const token = localStorage.getItem("lm_token");
    if (token) setAuthCookie(token);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    const onPopState = () => {
      const h = window.location.hash;
      const m = h.match(/^#(trailer|tractor)\/(\d+)$/);
      if (m) { setSelectedId(Number(m[2])); setPage(m[1] === "tractor" ? "tractor_detail" : "detail"); }
      else { setPage("dashboard"); setSelectedId(null); }
    };
    window.addEventListener("popstate", onPopState);
    return () => { window.removeEventListener("resize", onResize); window.removeEventListener("popstate", onPopState); };
  }, []);

  const loadServiceReviewCount = useCallback(async () => {
    if (user && (user.role === "admin" || user.role === "dispecer")) {
      try {
        const data = await api.get("/api/service/pending-review/count");
        if (data) setServiceReviewCount(data.count || 0);
      } catch { /* ok */ }
    }
  }, [user]);

  const loadTrailers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/trailers");
      if (data) setTrailers(data);
    } catch (ex) {
      console.error("Load trailers:", ex);
    }
    setLoading(false);
  }, []);

  const loadTractors = useCallback(async () => {
    try {
      const data = await api.get("/api/trailers?vehicle_type=tractor");
      if (data) setTractors(data);
    } catch (ex) {
      console.error("Load tractors:", ex);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await api.get("/api/trailers/stats/dashboard");
      if (data) setStats(data);
    } catch (ex) {
      console.error("Load stats:", ex);
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const data = await api.get("/api/clients");
      if (data) setClients(data);
    } catch (ex) {
      console.error("Load clients:", ex);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadTrailers();
      loadTractors();
      loadStats();
      loadClients();
      loadServiceReviewCount();
      const h = window.location.hash;
      const m = h.match(/^#(trailer|tractor)\/(\d+)$/);
      if (m) { setSelectedId(Number(m[2])); setPage(m[1] === "tractor" ? "tractor_detail" : "detail"); }
    }
  }, [user, loadTrailers, loadStats, loadClients, loadServiceReviewCount]);

  const handleLogin = (userData) => {
    setUser(userData);
    if (userData.role === "mecanic") setPage("dashboard");
  };

  const handleLogout = () => {
    localStorage.removeItem("lm_token");
    localStorage.removeItem("lm_user");
    clearAuthCookie();
    setUser(null);
    setPage("dashboard");
  };

  const handleDelete = async (id) => {
    try {
      await api.del(`/api/trailers/${id}`);
      loadTrailers();
      loadStats();
    } catch (ex) {
      alert("Eroare: " + ex.message);
    }
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditTrailer(null);
    setDetailKey(k => k + 1);
    loadTrailers();
    loadStats();
  };

  const handleSelect = (t) => {
    setSelectedId(t.id);
    const isTr = t.vehicle_type === "tractor" || page === "tractors";
    setPage(isTr ? "tractor_detail" : "detail");
    window.history.pushState(null, "", "#" + (isTr ? "tractor" : "trailer") + "/" + t.id);
  };

  const handleStartPV = (trailer, type) => {
    setPvWizard({ trailer, type });
  };

  const handlePVSaved = (pvResult) => {
    setPvWizard(null);
    loadTrailers();
    loadStats();
    if (selectedId) {
      setPage("detail");
    }
    // If returnare with differences, show differences report
    if (pvResult && pvResult.differences_count > 0) {
      setShowDifferences(pvResult.id);
    }
  };

  // Not logged in
  if (!user) return <LoginPage onLogin={handleLogin} />;

  const navTo = (p) => { setPage(p); setSelectedId(null); setSidebarOpen(false); loadTrailers(); loadTractors(); loadStats(); if (window.location.hash) window.history.replaceState(null, "", window.location.pathname); };

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: "📊", show: true },
    { id: "trailers", label: "Remorci", icon: "🚛", show: user.role !== "mecanic", match: (p) => p === "trailers" || p === "detail" },
    { id: "tractors", label: "Capete tractor", icon: "🚙", show: user.role !== "mecanic", match: (p) => p === "tractors" || p === "tractor_detail" },
    { id: "service", label: "Service", icon: "🔧", show: user.role !== "mecanic", badge: serviceReviewCount },
    { id: "reports", label: "Rapoarte", icon: "📈", show: user.role === "admin" },
    { id: "clients", label: "Clienți", icon: "👥", show: user.role === "admin" },
    { id: "settings", label: "Setări", icon: "⚙️", show: user.role === "admin" },
  ];

  const sidebarContent = (
    <>
      {/* Logo */}
      <div style={S.sidebarLogo}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => { if (page === "dashboard") { window.location.reload(); } else { navTo("dashboard"); } }}>
          <span style={{ fontSize: 24 }}>🚛</span>
          <span style={{ fontFamily: "'Bira'", fontSize: 22, color: "#ffffff", letterSpacing: 1 }}>La Maritec</span>
        </div>
      </div>
      {/* User info */}
      <div style={S.sidebarUser}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{user.full_name || user.username}</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>{user.role}</div>
      </div>
      {/* Nav */}
      <div style={S.sidebarNav}>
        {navItems.filter(n => n.show).map(n => {
          const active = n.match ? n.match(page) : page === n.id;
          return (
            <button key={n.id} style={{ ...S.sidebarBtn, ...(active ? S.sidebarBtnActive : {}) }} onClick={() => navTo(n.id)}>
              <span style={{ fontSize: 18 }}>{n.icon}</span>
              <span>{n.label}</span>
              {n.badge > 0 && (
                <span style={{ marginLeft: "auto", background: C.red, color: "#fff", fontSize: 10, fontWeight: 700, width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{n.badge}</span>
              )}
            </button>
          );
        })}
      </div>
      {/* Footer */}
      <div style={S.sidebarFooter}>
        <button style={{ ...S.sidebarBtn, color: "rgba(255,255,255,0.5)" }} onClick={handleLogout}>
          <span style={{ fontSize: 18 }}>🚪</span>
          <span>Deconectare</span>
        </button>
      </div>
    </>
  );

  // Differences report overlay
  if (showDifferences) {
    return (
      <div style={{ ...S.app, flexDirection: "column" }}>
        <div style={{ ...S.header, flexShrink: 0, padding: "0 16px" }}>
          <div style={{ fontFamily: "'Bira'", fontSize: 20, color: "#fff", letterSpacing: 1 }}>La Maritec</div>
          <button style={{ ...S.btn, padding: "4px 12px", fontSize: 12, color: "#fff", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)" }} onClick={() => setShowDifferences(null)}>← Înapoi</button>
        </div>
        <div style={{ ...S.content, flex: 1, padding: "16px 12px" }}>
          <DifferencesReport pvId={showDifferences} user={user} onClose={() => setShowDifferences(null)}
            onSendToService={(data) => {
              setShowCreateService({
                trailerId: data.returnare?.trailer_id,
                pvId: showDifferences,
                differences: data.differences
              });
            }} />
        </div>
        {showCreateService && (
          <CreateServiceModal
            trailerId={showCreateService.trailerId}
            pvId={showCreateService.pvId}
            differences={showCreateService.differences}
            onClose={() => setShowCreateService(null)}
            onCreated={() => {
              setShowCreateService(null);
              setShowDifferences(null);
              loadTrailers();
              loadStats();
              loadServiceReviewCount();
              if (showCreateService.trailerId) {
                setSelectedId(showCreateService.trailerId);
                setPage("detail");
              }
            }}
          />
        )}
      </div>
    );
  }

  // PV Wizard overlay
  if (pvWizard) {
    return <PVWizard trailer={pvWizard.trailer} pvType={pvWizard.type} onClose={() => setPvWizard(null)} onSaved={handlePVSaved} clients={clients} userRole={user?.role} userName={user?.full_name || user?.username || ""} vehicleType={pvWizard.trailer?.vehicle_type || "trailer"} />;
  }

  return (
    <div style={S.app}>
      {/* Sidebar — desktop only */}
      {!isMobile && (
        <div style={S.sidebar}>
          {sidebarContent}
        </div>
      )}

      {/* Mobile header */}
      {isMobile && (
        <div style={{ ...S.mobileHeader, display: "flex" }}>
          <button style={{ background: "none", border: "none", color: "#fff", fontSize: 24, cursor: "pointer", padding: 8, minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <span style={{ fontFamily: "'Bira'", fontSize: 20, color: "#fff", letterSpacing: 1, cursor: "pointer" }} onClick={() => { setPage("dashboard"); setSelectedId(null); setSidebarOpen(false); setMechServiceOrderId(null); }}>La Maritec</span>
          <div style={{ width: 44 }} />
        </div>
      )}

      {/* Mobile sidebar overlay + sidebar */}
      {isMobile && (
        <>
          <div style={{ ...S.sidebarOverlay, opacity: sidebarOpen ? 1 : 0, pointerEvents: sidebarOpen ? "auto" : "none" }} onClick={() => setSidebarOpen(false)} />
          <div style={{ ...S.sidebar, zIndex: 250, width: 260, transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 0.3s ease" }}>
            {sidebarContent}
          </div>
        </>
      )}

      {/* Main content area */}
      <div style={{ flex: 1, marginLeft: isMobile ? 0 : 220, minHeight: "100vh", display: "flex", flexDirection: "column", paddingTop: isMobile ? 64 : 0 }}>
        <div className="lm-content" style={{ ...S.content, padding: isMobile ? "12px 10px" : "24px 32px" }}>
          {page === "dashboard" && mechServiceOrderId && (
            <ServiceDetail orderId={mechServiceOrderId} user={user} onBack={() => setMechServiceOrderId(null)} onSelectTrailer={(id) => { setSelectedId(id); setPage("detail"); }} />
          )}
          {page === "dashboard" && !mechServiceOrderId && (
            <Dashboard stats={stats} trailers={trailers} onSelect={handleSelect} serviceReviewCount={serviceReviewCount} user={user}
              onStartPV={handleStartPV}
              onNavToTrailers={(stare) => { setPage("trailers"); window.__lmFilterStare = stare || null; window.__lmFromDashboard = true; window.history.pushState({ page: "trailers", stare }, "", "#remorci"); }}
              onGoToService={(orderId) => { if (orderId) setMechServiceOrderId(orderId); else setPage("service"); }}
              onSendToService={async (trailerId, pvId) => {
                if (pvId) {
                  try {
                    const data = await api.get("/api/pvs/" + pvId + "/differences");
                    setShowCreateService({ trailerId, pvId, differences: data?.differences || [] });
                  } catch (ex) { alert("Eroare la încărcarea diferențelor: " + ex.message); }
                } else {
                  // Find latest returnare PV with differences for this trailer
                  try {
                    const t = await api.get("/api/trailers/" + trailerId);
                    const servicedPvIds = (t.service_orders || []).map(s => s.pv_returnare_id);
                    const diffPvs = (t.pvs || []).filter(p => p.type === "returnare" && p.differences_count > 0 && !servicedPvIds.includes(p.id));
                    if (diffPvs.length > 0) {
                      const latestPv = diffPvs[0];
                      const data = await api.get("/api/pvs/" + latestPv.id + "/differences");
                      setShowCreateService({ trailerId, pvId: latestPv.id, differences: data?.differences || [] });
                    } else {
                      setShowCreateService({ trailerId, pvId: null, differences: [] });
                    }
                  } catch (ex) {
                    setShowCreateService({ trailerId, pvId: null, differences: [] });
                  }
                }
              }}
            />
          )}

          {page === "trailers" && (
            <TrailerList trailers={trailers} onSelect={handleSelect} onAdd={() => { setEditTrailer(null); setShowForm(true); }} onDelete={handleDelete} loading={loading} userRole={user.role} onBack={() => setPage("dashboard")} onStartPV={handleStartPV} />
          )}

          {page === "tractors" && (
            <TrailerList trailers={tractors} onSelect={handleSelect} onAdd={() => { setEditTrailer(null); setShowForm(true); }} onDelete={handleDelete} loading={loading} userRole={user.role} onBack={() => setPage("dashboard")} onStartPV={handleStartPV} vehicleType="tractor" />
          )}

          {(page === "detail" || page === "tractor_detail") && selectedId && (
            <TrailerDetail key={detailKey}
              vehicleType={page === "tractor_detail" ? "tractor" : "trailer"}
              trailerId={selectedId}
              onBack={() => { setPage(page === "tractor_detail" ? "tractors" : "trailers"); setSelectedId(null); if (window.location.hash) window.history.replaceState(null, "", window.location.pathname); }}
              onEdit={(t) => { setEditTrailer(t); setShowForm(true); }}
              onStartPV={handleStartPV}
              onSendToService={async (trailerId, pvId) => {
                if (pvId) {
                  try {
                    const data = await api.get("/api/pvs/" + pvId + "/differences");
                    setShowCreateService({ trailerId, pvId, differences: data?.differences || [] });
                  } catch (ex) { alert("Eroare: " + ex.message); }
                } else {
                  // Find latest returnare PV with differences for this trailer
                  try {
                    const t = await api.get("/api/trailers/" + trailerId);
                    const servicedPvIds = (t.service_orders || []).map(s => s.pv_returnare_id);
                    const diffPvs = (t.pvs || []).filter(p => p.type === "returnare" && p.differences_count > 0 && !servicedPvIds.includes(p.id));
                    if (diffPvs.length > 0) {
                      const latestPv = diffPvs[0];
                      const data = await api.get("/api/pvs/" + latestPv.id + "/differences");
                      setShowCreateService({ trailerId, pvId: latestPv.id, differences: data?.differences || [] });
                    } else {
                      setShowCreateService({ trailerId, pvId: null, differences: [] });
                    }
                  } catch (ex) {
                    setShowCreateService({ trailerId, pvId: null, differences: [] });
                  }
                }
              }}
              onShowDifferences={(pvId) => setShowDifferences(pvId)}
              userRole={user.role}
            />
          )}

          {page === "service" && (
            <ServicePage user={user} onSelectTrailer={(id) => { setSelectedId(id); setPage("detail"); }} />
          )}

          {page === "reports" && (
            <ReportsPage onSelectTrailer={(id) => { setSelectedId(id); setPage("detail"); }} />
          )}

          {page === "clients" && (
            <ClientList onRefresh={loadClients} />
          )}

          {page === "settings" && (
            <SettingsPage user={user} />
          )}
        </div>
      </div>

      {/* Modal Form */}
      {showForm && (
        <TrailerForm trailer={editTrailer} onSave={() => { handleFormSave(); loadTractors(); }} onClose={() => { setShowForm(false); setEditTrailer(null); }} clients={clients} vehicleType={(page === "tractors" || page === "tractor_detail") ? "tractor" : (editTrailer?.vehicle_type || "trailer")} />
      )}

      {/* Create Service Order Modal */}
      {showCreateService && (
        <CreateServiceModal
          trailerId={showCreateService.trailerId}
          pvId={showCreateService.pvId}
          differences={showCreateService.differences}
          onClose={() => setShowCreateService(null)}
          onCreated={() => {
            const tid = showCreateService.trailerId;
            setShowCreateService(null);
            setShowDifferences(null);
            loadTrailers();
            loadStats();
            loadServiceReviewCount();
            if (tid) { setSelectedId(tid); setPage("detail"); }
          }}
        />
      )}
    </div>
  );
}
